
<template>
  <v-app>

    <!-- リセット中オーバーレイ -->
    <div v-if="isResetting" class="reset-overlay">
      <div class="reset-spinner"></div>
      <div class="reset-text">リセット中...</div>
    </div>

    <!-- 授業モード表示バナー（生徒側） -->
    <div v-if="lessonMode.active" class="lesson-mode-banner" :class="{ 'sync-locked': lessonMode.syncEnabled }">
      <v-icon size="small" class="mr-1">mdi-school</v-icon>
      <span>授業中: {{ lessonMode.className }}</span>
      <v-chip v-if="lessonMode.syncEnabled" size="x-small" color="error" variant="flat" class="ml-2">
        <v-icon start size="x-small">mdi-lock</v-icon>
        先生と同期中（操作不可）
      </v-chip>
      <v-chip v-else size="x-small" color="success" variant="flat" class="ml-2">
        <v-icon start size="x-small">mdi-lock-open</v-icon>
        自由操作
      </v-chip>
    </div>

    <Splash
        v-if="shouldShowSplash"
        ref="splash"
        logo-src="/oh3/ogp.png"
        :auto-hide="true"
        :map-ready="mapReady"
        :min-ms="800"
        :max-ms="3000"
        :fade-ms="800"
        @finish="onSplashFinish"
    />

    <!-- 利用規約同意ダイアログ（初回のみ表示） -->
    <TermsAgreementDialog @agreed="termsAgreed = true" />

    <WelcomeWizard />

    <WhatsNewDialog />

    <SimpleImportDialog
        v-model="showHandwriteDialog"
        :job-id="currentJobId"
        :start-x-y="startXY"
        @imported="onHandwriteImported"
    />

    <FloatingWindow
        windowId="kyuseki-import"
        title="求積表インポート"
        type="normal"
        :resizable="true"
        :default-top="50"
        :default-left="100"
        :default-width="isSmall500 ? 360 : 1200"
        :default-height="kyusekiDefaultHeight"
        :keepAspectRatio="false"
        :showMaxRestore="false"
        overflow="hidden"
        :is-modal="isSmall500"
        :use-fixed-position="true"
        @close="closeKyusekiWindow"
    >
      <KyusekiImportDialog
          v-if="showKyusekiDialog"
          :model-value="showKyusekiDialog"
          :job-id="currentJobId"
          :start-x-y="startXY"
          :initial-file="$store.state.kyusekiInitialFile"
          @update:modelValue="onKyusekiDialogVisibilityChange"
          @imported="onImported"
      />
    </FloatingWindow>

    <LoginManager></LoginManager>

    <VDialogCommon
        @watch-position="watchPosition"
    >
    </VDialogCommon>

    <FileDropFlowDialog v-if="mapReady" />
    <HomukyokuXmlImportDialog v-if="mapReady" />

    <RealEstateReportDialog v-if="mapReady" ref="realEstateReportDialog" />

    <!-- AI瞬間レポート（一時的にコメントアウト）
    <AiPointInfoDialog
      v-for="inst in $store.state.multiInstanceFWs['ai-point-info']"
      :key="inst.id"
      :instance-id="inst.id"
      :lng="inst.lng"
      :lat="inst.lat"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      :initial-tab="inst.tab"
      :from-share-feature="inst.fromShareFeature || false"
      :fw-visible="inst.visible !== false"
      @close="$store.commit('hideFWInstance', { type: 'ai-point-info', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'ai-point-info', id: inst.id })"
    />
    -->

    <!-- AI物件調査レポート（マルチインスタンス対応） -->
    <PropertyReportDialog
      v-for="inst in $store.state.multiInstanceFWs['property-report']"
      :key="inst.id"
      :instance-id="inst.id"
      :lng="inst.lng"
      :lat="inst.lat"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      :initial-tab="inst.tab"
      :from-share-feature="inst.fromShareFeature || false"
      :hide-numbering="inst.hideNumbering || false"
      :fw-visible="inst.visible !== false"
      @close="$store.commit('hideFWInstance', { type: 'property-report', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'property-report', id: inst.id })"
    />

    <!-- AI複数地点比較ランキング（シングルインスタンス） -->
    <MultiPointRankingDialog
      v-if="showMultiPointRanking"
      :initial-points="mprdInitialPoints"
      :initial-preset-key="mprdInitialPresetKey"
      :initial-weights="mprdInitialWeights"
      @close="showMultiPointRanking = false"
    />

    <!-- AI子育てレポート（マルチインスタンス対応） -->
    <ChildCareReportDialog
      v-for="inst in $store.state.multiInstanceFWs['ai-childcare-report']"
      :key="inst.id"
      :instance-id="inst.id"
      :lng="inst.lng"
      :lat="inst.lat"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      :initial-tab="inst.tab"
      :from-share-feature="inst.fromShareFeature || false"
      :fw-visible="inst.visible !== false"
      @close="$store.commit('hideFWInstance', { type: 'ai-childcare-report', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'ai-childcare-report', id: inst.id })"
    />

    <!-- AI人口プロファイル（マルチインスタンス対応） -->
    <SuperPopulationDialog
      v-for="inst in $store.state.multiInstanceFWs['super-population']"
      :key="inst.id"
      :instance-id="inst.id"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      :initial-lng="inst.lng"
      :initial-lat="inst.lat"
      :polygon-data="inst.polygonData"
      :analysis-mode="inst.analysisMode || 'point'"
      :from-share-feature="inst.fromShareFeature || false"
      :fw-visible="inst.visible !== false"
      :is-disaster-mode="inst.isDisasterMode || false"
      @close="$store.commit('hideFWInstance', { type: 'super-population', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'super-population', id: inst.id })"
    />

    <!-- 教員ダッシュボード -->
    <TeacherDashboard
      v-if="$store.state.showTeacherDashboard && $store.state.isTeacher"
      :uid="$store.state.userId"
      :teacher-info="$store.state.teacherInfo"
      @close="$store.commit('setShowTeacherDashboard', false)"
    />

    <!-- 組織管理画面 -->
    <OrganizationAdmin
      v-if="$store.state.showOrganizationAdmin && $store.state.isOrganizationOwner"
      :uid="$store.state.userId"
      :admin-info="$store.state.organizationInfo"
      @close="$store.commit('setShowOrganizationAdmin', false)"
    />

    <!-- AI防災リスク診断（マルチインスタンス対応） -->
    <AreaAnalysisDialog
      v-for="inst in $store.state.multiInstanceFWs['area-analysis']"
      :key="inst.id"
      :instance-id="inst.id"
      :polygon-data="inst.polygonData"
      :point-data="inst.pointData"
      :analysis-mode="inst.analysisMode"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      :fw-visible="inst.visible !== false"
      :open-simulation-dialog="inst.openSimulationDialog || false"
      @close="$store.commit('hideFWInstance', { type: 'area-analysis', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'area-analysis', id: inst.id })"
    />


    <!-- CSV結合分析（マルチインスタンス対応） -->
    <CsvJoinAnalysisDialog
      v-for="inst in $store.state.multiInstanceFWs['csv-join-analysis']"
      :key="inst.id"
      :instance-id="inst.id"
      :offset-index="inst.index"
      @close="$store.commit('removeFWInstance', { type: 'csv-join-analysis', id: inst.id })"
    />

    <!-- 土地法規等調査（マルチインスタンス対応） -->
    <FsInvestigationDialog
      v-for="inst in $store.state.multiInstanceFWs['fs-investigation']"
      :key="inst.id"
      :instance-id="inst.id"
      :lng="inst.lng"
      :lat="inst.lat"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      :polygon-data="inst.polygonData"
      :analysis-mode="inst.analysisMode || 'point'"
      @close="$store.commit('removeFWInstance', { type: 'fs-investigation', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'fs-investigation', id: inst.id })"
    />

    <!-- 簡易マイソク（マルチインスタンス対応） -->
    <MaisokuDialog
      v-for="inst in $store.state.multiInstanceFWs['maisoku']"
      :key="inst.id"
      :instance-id="inst.id"
      :lng="inst.lng"
      :lat="inst.lat"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      @close="$store.commit('removeFWInstance', { type: 'maisoku', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'maisoku', id: inst.id })"
    />

    <!-- 重説補助資料（マルチインスタンス対応） -->
    <JusetsuDraftDialog
      v-for="inst in $store.state.multiInstanceFWs['jusetsu-draft']"
      :key="inst.id"
      :instance-id="inst.id"
      :lng="inst.lng"
      :lat="inst.lat"
      :offset-index="inst.index"
      :type-index="inst.typeIndex"
      @close="$store.commit('removeFWInstance', { type: 'jusetsu-draft', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'jusetsu-draft', id: inst.id })"
    />

    <!-- 超串刺し抽出（ウィザード型） -->
    <FloatingWindow
      window-id="super-cross-section-window"
      title="超串刺し抽出"
      :default-width="420"
      :default-height="600"
      :default-top="65"
      :default-left="10"
      :resizable="!$store.state.isSmall500"
      :persist="!$store.state.isSmall500"
    >
      <SuperCrossSectionDialog />
    </FloatingWindow>

    <!-- 市区町村別人口ピラミッド（マルチインスタンス対応） -->
    <MunicipalityPyramidDialog
      v-for="inst in $store.state.multiInstanceFWs['municipality-pyramid']"
      :key="inst.id"
      :instance-id="inst.id"
      :offset-index="inst.index"
      :municipality-code="inst.municipalityCode"
      :municipality-name="inst.municipalityName"
      :pref-name="inst.prefName"
      :fw-visible="inst.visible !== false"
      @close="$store.commit('removeFWInstance', { type: 'municipality-pyramid', id: inst.id })"
      @destroy="$store.commit('removeFWInstance', { type: 'municipality-pyramid', id: inst.id })"
    />


    <!-- ダイアログコンテナ（v-dialog群をここに集約） -->
    <DialogContainer :vm="this" />
    <AiOsmSearchDialog v-if="mapReady" ref="aiOsmSearchDialog" />

    <TouhonOverlayDialog v-if="mapReady" ref="touhonOverlayDialog" />

    <TouhonDiffDialog v-if="mapReady" ref="touhonDiffDialog" />

    <MapCompareDialog v-if="mapReady" ref="mapCompareDialog" />

    <SpreadsheetSyncDialog v-if="mapReady" ref="spreadsheetSyncDialog" />

    <GroupManageDialog v-if="mapReady" ref="groupManageDialog" />

    <!-- ヘルプ -->
    <HelpDialog v-if="mapReady" :initial-topic="$store.state.helpInitialTopic" />

    <!-- AIヘルプチャット -->
    <AiHelpChat v-if="mapReady" />

    <!-- 標高図 -->
    <ElevationDialog v-if="mapReady" />

    <!-- 簡単スナップショットFAB -->
    <SnapshotFab v-if="mapReady" />

    <!-- ext-ダイアログ一括表示FAB（マップコンテナ内に移動） -->
    <!-- <ExtDialogFab /> -->

    <!-- AI機能 SpeedDial FAB（防災・人口・物件・子育てレポート） -->
    <AiMapChatFab v-if="mapReady" />

    <!-- OH3 エージェント専用 FAB -->
    <AiAgentFab v-if="mapReady" />

    <!-- AIマップクエリパネル（旧AI検索: Dialog-layer から開かれる） -->
    <AiMapQuery v-if="mapReady" />

    <!-- OH3 エージェント本体ダイアログ -->
    <AiAgentDialog v-if="mapReady" />

    <!-- マップAI: choropleth/contour/extrusion 用の地図上凡例 -->
    <ChoroplethLegend v-if="mapReady" />

    <!-- マップAI: D3 統計チャート FloatingWindow（複数インスタンス可） -->
    <ChartFloatingWindow
      v-for="inst in $store.state.multiInstanceFWs['chart-window']"
      :key="inst.id"
      :instance="inst"
    />

    <!-- 方位記号（北矢印） -->
    <NorthArrow />

    <VDialogConfirm
        v-model="s_showConfirm"
        v-bind="confirmProps"
        :message="confirmMessage"
        @ok="onConfirmOk"
        @cancel="onConfirmCancel"
        @middle="onConfirmMiddle"
    />

    <!-- =========================
    JOBピッカー
    ========================= -->
    <FloatingWindow
        windowId="job-picker"
        title="ジョブリスト"
        type="normal"
        :is-modal="isSmall500"
        :resizable="true"
        :default-top="10"
        :default-left="10"
        :default-width="400"
        :default-height="700"
        :keepAspectRatio="false"
        :showMaxRestore="false"
        @close="jobPickerOpen = false"
    >
      <JobPicker
          ref="jobPicker"
      >
      </JobPicker>
    </FloatingWindow>

    <!-- トラバース -->
    <FloatingWindow
        windowId = "traverse"
        title = "方位距離エディタ"
        type="normal"
        :resizable="false"
        :default-top = "10"
        :default-left = "10"
        :default-width = "530"
        :keepAspectRatio = "false"
        :showMaxRestore="false"
    >
      <Traverse>
      </Traverse>
    </FloatingWindow>


    <!-- ジオリファレンス -->
    <FloatingWindow
        windowId = "warp-wizard"
        title = "ジオリファレンス"
        type="normal"
        :resizable="true"
        :default-top = "10"
        :default-left = "10"
        :default-width = "400"
        :default-height = "430"
        :keepAspectRatio = "false"
        :showMaxRestore="false"
        @close="onWarpWindowClose"
    >
      <WarpWizard
          :mapName="mapName"
          :item="{ id:'warp-wizard', label:'Warp Wizard' }"
          v-model:gcpList="gcpList"
          :file="pendingFile"
          :stacked="true"
          @confirm="onWarpConfirm"
          @clear-map-markers="clearBlueDotsOnMap"
      />
    </FloatingWindow>

    <!-- ドロー地物編集ウィンドウ -->
    <DrawFloatingWindow v-if="mapReady" window-id="draw-floating-window" />

    <!-- 写真スケッチウィンドウ -->
    <PhotoSketchFloatingWindow v-if="mapReady" window-id="photo-sketch-fw" />

    <!-- OCR/メモ詳細ウィンドウ -->
    <TextDetailFloatingWindow v-if="mapReady" />

    <!-- 写真メモアップロード -->
    <PhotoMemoUpload v-if="mapReady" window-id="photo-memo-upload" />

    <!-- 求積プレビュー -->
    <FloatingWindow
        windowId="kyuseki-preview"
        title="地図・画像プレビュー"
        type="normal"
        :resizable="true"
        :default-top="10"
        :default-left="10"
        :default-width="400"
        :default-height="400"
        :keepAspectRatio="false"
        :showMaxRestore="false"
    >
      <KyusekiMapPreview />
    </FloatingWindow>

    <!-- 旧版地形図プレビュー（無限生成FW） -->
    <Mw5PreviewManager v-if="mapReady" />

    <!-- マピラリフィルター -->
    <FloatingWindow
        windowId = "mapillary-filter"
        title = "mapillaryフィルタ"
        type="normal"
        :default-top = "10"
        :default-left = "mlyDefaultLeft"
        :default-width = "isSmall500 ? 350 : 500"
        :default-height = "400"
        :keepAspectRatio = "false"
        :showMaxRestore="false"
        @close = "mapillaryClose"
    >
      <MapillaryFilter
          :observe-width="true"
          :show-close="true"
          :close-on-esc="true"
      />
    </FloatingWindow>

    <!-- 住所検索 -->
    <FloatingWindow
        windowId = "address-search"
        title = "住所検索"
        type="normal"
        :is-modal="isSmall500"
        :default-top = "10"
        :default-left = "350"
        :default-width = "380"
        :default-height = "400"
        :keepAspectRatio = "false"
        :showMaxRestore="false"
        :customIcons="[{ icon: 'mdi-delete-sweep', eventName: 'clear-all-markers', tooltip: '全マーカー削除' }]"
        @close = "addressSearchClose"
        @clear-all-markers="clearAllAddressMarkers"
    >
      <AddressSearch ref="addressSearch" />
    </FloatingWindow>

    <!-- 陰影設定 -->
    <HillshadeControl
        v-if="mapReady"
        ref="hs"
        :map01="map01"
        :map02="map02"
        :dem-tiles-url="demUrl"
    />
    <HillshadePanel
        v-if="mapReady"
        ref="hsPanel"
        :map="map01"
        :controller="hsRef"
    />

    <!-- マピラリビューワー -->
    <FloatingWindow
        windowId = "mapillary"
        :title-is-html="true"
        :title = "mapillaryTytle"
        type="normal"
        :default-top = "70"
        :default-left = "10"
        :default-width = "isSmall500 ? 200 : 400"
        :default-height = "300"
        :keepAspectRatio = "false"
        :showMaxRestore="true"
        @width-changed ="onWidthChangedForMapillary"
        @close = "mapillaryClose"
    >
      <div style="display:flex;flex-direction:column;height:100%;">
        <div class="mapillary-div" :style="{flex:'1 1 0',minHeight:0,width:'100%',background:'color-mix(in srgb, var(--main-color) 60%, black)',color:'white'}">
        </div>
        <div class="toolbar-row" style="flex:0 0 auto;">
          <v-btn v-track="'mapillary-filter'" @click="mapillaryFilterOpen">フィルタ</v-btn>
        </div>
      </div>
    </FloatingWindow>

    <!-- ペイントエディター -->
    <FloatingWindow
        windowId="painteditor"
        :title="`スタイル変更（id=${s_pmtiles0Id}-${s_pmtiles0Name}）`"
        type="normal"
        :default-width=400
        :default-height=600
        :keepAspectRatio="false"
    >
      <PaintEditor
          :id="Number(s_pmtiles0Id)"
          :initialPaint="paintSettings"
          @update:paint="onPaintUpdate"
      />
    </FloatingWindow>

    <!-- マイマップビューア (Google My Maps クローン) -->
    <FloatingWindow
        windowId="mymap-viewer"
        title="マイマップ"
        type="normal"
        :default-width="720"
        :default-height="560"
        :default-left="80"
        :default-top="100"
        :keepAspectRatio="false"
    >
      <MyMapViewer></MyMapViewer>
    </FloatingWindow>

    <!-- ズーム情報ウィンドウ -->
    <FloatingWindow
        windowId="zoom-info"
        title="画面中心の情報"
        type="normal"
        :default-top="120"
        :default-left="80"
        :default-width="450"
        :default-height="615"
        :keepAspectRatio="false"
        :showMaxRestore="false"
        :is-modal="isSmall500"
        @close="zoomInfoClose"
    >
      <ZoomInfoWindow />
    </FloatingWindow>

    <!-- 価格推移表FW（無限生成対応） -->
    <FloatingWindow
        v-for="pw in priceHistoryWindows"
        :key="pw.id"
        :windowId="'price-history-' + pw.id"
        :title="pw.type === 'chosa' ? '都道府県地価調査 推移' : '地価公示 推移'"
        type="normal"
        :default-top="pw.top"
        :default-left="pw.left"
        :default-width="400"
        :default-height="340"
        :keepAspectRatio="false"
        :showMaxRestore="false"
        :customIcons="[{ icon: 'mdi-download', tooltip: 'CSVダウンロード', handler: () => downloadPriceHistoryCsv(pw.id) }]"
        @close="closePriceHistoryWindow(pw.id)"
        @position-change="(pos) => onPriceHistoryPositionChange(pw.id, pos)"
    >
      <PriceHistoryChart
          :ref="'priceChart-' + pw.id"
          :lat="pw.lat"
          :lng="pw.lng"
          :type="pw.type"
          :location="pw.location"
          :lot-number="pw.lotNumber"
      />
    </FloatingWindow>

    <!-- タイムスライダー -->
    <TimeSlider
      map-name="map01"
      @close="onTimeSliderClose"
    />

    <!-- オフラインエリア管理 -->
    <OfflineAreaManager />

    <!-- 地番情報ウィンドウ -->
    <ChibanInfoWindow />
    <ChibanzuInfoWindow />

    <!-- 簡単SIMAウィンドウ -->
    <SimaQuickWindow />

    <!-- ストリートビューウィンドウ -->
    <StreetViewWindow />

    <!-- 到達圏ウィンドウ -->
    <IsochroneWindow />

    <!-- 道路到達圏ウィンドウ -->
    <RoadIsochroneWindow />

    <!-- 流域計算ウィンドウ -->
    <WatershedWindow />

    <!-- registry にある id を全部ホスト -->
    <message-dialog
        v-for="id in dialogIds"
        :key="id"
        :use-store="true"
        :store-module="'messageDialog'"
        :dialog-id="id"
    />

    <VDialogIframe v-if="mapReady"></VDialogIframe>


    <!-- ここからメイン  -->
    <v-main>

      <!-- 測位  -->
      <div v-if="isJobMenu"
           class="oh-left-bottom-tools mt-2"
           style="display:flex; flex-direction:column; bottom: 70px;">
        <!-- 1行目：モード切替 + 現在のジョブ -->
        <div class="mb-1 d-flex align-center" style="gap:8px;">
          <v-btn-toggle
              v-model="lineMode"
              mandatory
              density="compact"
              variant="elevated"
              color="primary"
              class="mr-2"
          >
            <v-btn v-track="'job-mode-point'" @click="setLineMode('point')" value="point" size="x-small">単点</v-btn>
            <v-btn v-track="'job-mode-chain'" @click="setLineMode('chain')" value="chain" size="x-small">結線</v-btn>
          </v-btn-toggle>

          <!--  <div class="text-subtitle-2">-->
          <!--    ジョブ：{{ currentJobName || '未選択' }}-->
          <!--  </div>-->

        </div>
        <!-- 2行目：ボタンを横並び -->
        <div class="d-flex align-center" style="gap:6px;">
        <!-- 業務を終了 -->
        <MiniTooltip text="JOBを終了" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'job-end'"
              icon
              size="small"
              @click="onJobEndClick(true)"
              aria-label="JOBを終了"
          >
            終了
          </v-btn>
        </MiniTooltip>

        <!-- Job Picker を開く -->
        <MiniTooltip text="JOBを選ぶ / 変更する" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'job-picker'"
              icon
              size="small"
              color="primary"
              @click="openJobPicker"
              aria-label="Job Picker を開く"
          >
            リスト
          </v-btn>
        </MiniTooltip>

        <!-- CSV -->
        <MiniTooltip text="CSVをダウンロード" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'job-csv-download'"
              icon
              size="small"
              :disabled="disabledForSokui"
              @click="downloadCsv2"
              aria-label="CSVをダウンロード"
          >
            csv
          </v-btn>
        </MiniTooltip>

        <!-- SIMA -->
        <MiniTooltip text="SIMをダウンロード" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'job-sima-download'"
              icon
              size="small"
              :disabled="disabledForSokui"
              @click="exportCsv2Sima"
              aria-label="SIMをダウンロード"
          >
            SIMA
          </v-btn>
        </MiniTooltip>

        <!-- 測位地点 新規追加 -->
        <MiniTooltip text="測位地点を新規追加" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'job-sokui'"
              icon
              size="small"
              :class="{ 'pulse-anim': !kansokuRunning && !torokuBusy }"
              :disabled="disabledForSokui"
              @click="startTorokuHere"
              aria-label="測位地点を新規追加"
          >
            測位
          </v-btn>
        </MiniTooltip>
        </div>
      </div>

      <div v-if="s_isKuiuchi" class="oh-left-bottom-tools">
        <!-- 終了 -->
        <MiniTooltip text="終了する" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'kuiuchi-end'"
              icon
              size="small"
              @click="toggleWatchPosition(null, true)"
          >
            終了
          </v-btn>
        </MiniTooltip>
      </div>

      <div v-if="isTracking" class="oh-left-bottom-tools" style="bottom: 90px;">
        <!-- 終了 -->
        <MiniTooltip text="終了する" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'tracking-end'"
              icon
              size="small"
              @click="toggleWatchPosition(null, true)"
          >
            終了
          </v-btn>
        </MiniTooltip>

        <!-- CSV ダウンロード -->
        <MiniTooltip text="CSVダウンロード" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'tracking-csv-download'"
              icon
              size="small"
              :disabled="(!logEnabled) && (!csvRows || csvRows.length <= 1)"
              @click="exportTrackCsv"
              aria-label="CSVダウンロード"
          >
            csv
          </v-btn>
        </MiniTooltip>

        <!-- SIMA ダウンロード -->
        <MiniTooltip text="SIMAダウンロード" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'tracking-sima-download'"
              icon
              size="small"
              :disabled="(!logEnabled) && (!csvRows || csvRows.length <= 1)"
              @click="exportTrackSima"
              aria-label="SIMAダウンロード"
          >
            sim
          </v-btn>
        </MiniTooltip>

        <!-- クリア -->
        <MiniTooltip text="ログをクリア" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'tracking-log-clear'"
              icon
              size="small"
              :disabled="(!logEnabled) && (!csvRows || csvRows.length <= 1)"
              @click="requestClearLog"
              aria-label="ログをクリア"
          >
            <v-icon size="18">mdi-trash-can-outline</v-icon>
          </v-btn>
        </MiniTooltip>

        <!-- 記録トグル：停止=赤Stop / 待機=録画 -->
        <MiniTooltip text="記録開始 / 記録停止" :offset-x="0" :offset-y="0">
          <v-btn
              v-track="'tracking-toggle-record'"
              :color="logEnabled ? 'red' : 'primary'"
              :class="logEnabled ? 'blink' : ''"
              icon
              size="small"
              @click="toggleTrackLog"
              :aria-label="logEnabled ? '記録停止' : '記録開始'"
          >
            rec
          </v-btn>
        </MiniTooltip>

      </div>
      <!-- AI画像鮮明化 -->
      <FloatingWindow
        windowId="ai-enhance-dialog"
        title="AI画像処理 (β)"
        type="normal"
        :resizable="false"
        :default-top="100"
        :default-left="100"
        :default-width="360"
        :default-height="'auto'"
      >
        <AiEnhanceDialog />
      </FloatingWindow>

      <!-- <v-btn @click="test">test</v-btn>-->

      <!-- 変形と移動が地物単位になったため、確定・元に戻すボタンは不要
      <div v-show="showDrawConfrim && s_drawButtonsVisible && !s_editEnabled" id="floating-buttons">
        <div class="draw-confirm-content">
          <v-icon
            class="drag-handle"
            size="small"
            color="grey"
            @mousedown.prevent="startFloatingButtonsDrag"
            @touchstart.prevent="startFloatingButtonsDrag"
          >mdi-drag</v-icon>
          <v-btn
            v-track="'draw-cancel'"
            size="small"
            color="error"
            variant="outlined"
            id="cancel-btn"
          >
            キャンセル
          </v-btn>
          <v-btn
            v-track="'draw-undo'"
            size="small"
            color="grey"
            variant="outlined"
            :disabled="isUndoBtnDisabled"
            id="undo-btn"
          >
            1点戻す
          </v-btn>
          <v-btn
            v-track="'draw-confirm'"
            size="small"
            color="primary"
            variant="flat"
            id="confirm-btn"
          >
            確定
          </v-btn>
        </div>
      </div>
      -->

      <!-- 編集モード用UI - 変形と移動が地物単位になったため不要
      <div v-show="s_editEnabled && s_drawButtonsVisible" id="edit-mode-bar">
        <div class="edit-mode-content">
          <v-icon size="small" color="primary">mdi-cursor-move</v-icon>
          <span class="edit-mode-text">編集モード</span>
          <v-btn
            v-track="'edit-mode-confirm'"
            size="small"
            color="primary"
            variant="flat"
            @click="onEditConfirm"
          >
            確定
          </v-btn>
          <v-btn
            v-track="'edit-mode-revert'"
            size="small"
            color="warning"
            variant="outlined"
            @click="onEditRevert"
          >
            元に戻す
          </v-btn>
        </div>
      </div>
      -->

      <!-- 複製モード用UI -->
      <div v-show="s_isDrawDuplicate && s_drawButtonsVisible" id="duplicate-mode-bar">
        <div class="duplicate-mode-content">
          <v-icon size="small" color="secondary">mdi-content-copy</v-icon>
          <span class="duplicate-mode-text">複製モード</span>
          <span class="duplicate-mode-hint">クリックで配置</span>
          <v-btn
            v-track="'duplicate-mode-exit'"
            size="small"
            color="error"
            variant="outlined"
            @click="exitDuplicateMode"
          >
            終了
          </v-btn>
        </div>
      </div>

      <DialogDrawConfig
          v-if="mapReady"
          :mapName="'map01'"
          @open-floating="openWindow"
      />
      <DialogDrawShare
          v-if="mapReady"
          :mapName="'map01'"
      />
      <GalleryWindow v-if="mapReady" />
      <DialogPopularSets v-if="mapReady" mapName="map01" />
      <DialogPopularSets v-if="mapReady && s_map2Flg" mapName="map02" />

      <!-- 投げ縄選択後のFloatingWindow -->
      <FloatingWindow
          windowId="lasso-control-window"
          title="投げ縄選択"
          :defaultWidth="340"
          :defaultHeight="320"
          :defaultTop="lassoFwTop"
          :defaultLeft="lassoFwLeft"
          :resizable="false"
          :persist="false"
          :preventAutoClose="true"
          @close="showLassoCloseConfirmDialog"
      >
        <template #default>
          <div class="lasso-control-content">
            <!-- 選択数表示 -->
            <div class="lasso-selected-info">
              <v-icon size="small" color="primary">mdi-checkbox-multiple-marked</v-icon>
              <span>{{ lassoSelectedCount }}個の地物を選択中</span>
            </div>

            <!-- インラインカラーセレクター -->
            <div class="lasso-stylish-color-section">
              <div class="lasso-stylish-color-grid">
                <div
                    v-for="color in lassoColorOptions"
                    :key="color.value"
                    class="lasso-stylish-color-item"
                    :class="{
                      'is-selected': lassoSelectedColor === color.value,
                      'is-transparent': color.value === 'rgba(0,0,0,0)'
                    }"
                    @click="applyLassoBulkColor(color.value)"
                >
                  <div
                      class="lasso-stylish-color-circle"
                      :style="{ backgroundColor: color.value === 'rgba(0,0,0,0)' ? 'transparent' : color.bg }"
                  >
                    <v-icon v-if="lassoSelectedColor === color.value" size="14" :color="getCheckColor(color.value)">mdi-check</v-icon>
                  </div>
                </div>
                <!-- カスタムカラーピッカー -->
                <label class="lasso-stylish-color-item lasso-stylish-picker">
                  <input
                      type="color"
                      v-model="lassoCustomColor"
                      @input="applyLassoCustomColor"
                      class="lasso-hidden-input"
                  />
                  <div class="lasso-stylish-color-circle lasso-stylish-picker-circle">
                    <v-icon size="14">mdi-eyedropper</v-icon>
                  </div>
                </label>
              </div>
              <!-- 透過度スライダー -->
              <div class="lasso-stylish-opacity-row">
                <v-icon size="16" class="lasso-opacity-icon">mdi-opacity</v-icon>
                <v-slider
                    v-model.number="lassoOpacity"
                    min="0"
                    max="100"
                    step="5"
                    hide-details
                    density="compact"
                    color="primary"
                    class="lasso-stylish-slider"
                    @update:model-value="applyLassoBulkColor(lassoSelectedColor)"
                ></v-slider>
                <span class="lasso-stylish-opacity-value">{{ lassoOpacity }}%</span>
              </div>
            </div>

            <!-- 削除・自分以外削除・戻す・確定ボタン -->
            <div class="lasso-action-buttons">
              <v-btn
                  v-track="'lasso-bulk-delete'"
                  color="error"
                  variant="tonal"
                  class="lasso-action-btn"
                  @click="confirmLassoBulkDelete"
                  prepend-icon="mdi-delete"
              >
                削除
              </v-btn>
              <v-btn
                  v-track="'lasso-delete-others'"
                  color="error"
                  variant="outlined"
                  class="lasso-action-btn lasso-delete-others-btn"
                  @click="confirmLassoDeleteOthers"
              >
                他を削除
              </v-btn>
              <v-btn
                  v-track="'lasso-restore'"
                  color="warning"
                  variant="tonal"
                  class="lasso-action-btn"
                  @click="restoreLassoChanges"
                  prepend-icon="mdi-undo"
              >
                戻す
              </v-btn>
              <v-btn
                  v-track="'lasso-confirm'"
                  color="success"
                  class="lasso-action-btn"
                  @click="exitLassoMode"
                  prepend-icon="mdi-check"
              >
                確定
              </v-btn>
            </div>
          </div>
        </template>
      </FloatingWindow>

      <!-- 空間クエリウィザード -->
      <FloatingWindow
          windowId="spatial-query-window"
          title="空間クエリウィザード"
          :defaultWidth="500"
          :defaultHeight="520"
          :defaultTop="100"
          :defaultLeft="100"
          :resizable="true"
          :persist="false"
          :is-modal="isSmall500"
          :bottom-sheet="true"
      >
        <template #default>
          <SpatialQueryWizard
            :polygon-data="spatialQueryPolygonData"
          />
        </template>
      </FloatingWindow>

      <!-- ドローヘルプ用FloatingWindow -->
      <FloatingWindow
          windowId="draw-help-window"
          title="ドローモード ショートカット"
          :defaultWidth="400"
          :defaultHeight="500"
          :defaultTop="65"
          :defaultLeft="10"
          :resizable="true"
      >
        <template #default>
          <div class="draw-help-content" style="padding: var(--oh3-spacing-4); overflow-y: auto; height: 100%;">
            <!-- キーボードショートカット -->
            <div class="help-section">
              <h3 style="margin-bottom: var(--oh3-spacing-3); font-size: var(--oh3-font-size-md); font-weight: bold; color: #1976d2;">
                <v-icon size="small" color="primary">mdi-keyboard</v-icon>
                キーボードショートカット
              </h3>
              <div class="shortcut-list">
                <div class="shortcut-item">
                  <kbd>Backspace</kbd> / <kbd>Delete</kbd>
                  <span>最後の頂点を削除</span>
                </div>
                <div class="shortcut-item">
                  <kbd>Ctrl</kbd> + <kbd>Z</kbd>
                  <span>元に戻す</span>
                </div>
                <div class="shortcut-item">
                  <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd>
                  <span>やり直す</span>
                </div>
                <div class="shortcut-item">
                  <kbd>T</kbd>
                  <span>テキスト描画</span>
                </div>
                <div class="shortcut-item">
                  <kbd>P</kbd>
                  <span>点・画像描画</span>
                </div>
                <div class="shortcut-item">
                  <kbd>L</kbd>
                  <span>線描画</span>
                </div>
                <div class="shortcut-item">
                  <kbd>O</kbd>
                  <span>多角形描画</span>
                </div>
                <div class="shortcut-item">
                  <kbd>C</kbd>
                  <span>円描画</span>
                </div>
                <div class="shortcut-item">
                  <kbd>F</kbd>
                  <span>フリーハンド描画</span>
                </div>
                <div class="shortcut-item">
                  <kbd>S</kbd>
                  <span>投げ縄選択</span>
                </div>
                <div class="shortcut-item">
                  <kbd>E</kbd>
                  <span>編集モード</span>
                </div>
                <div class="shortcut-item">
                  <kbd>ESC</kbd>
                  <span>キャンセル / 終了</span>
                </div>
                <div class="shortcut-item">
                  <kbd>Enter</kbd>
                  <span>線・多角形の確定</span>
                </div>
                <div class="shortcut-item">
                  <kbd>?</kbd>
                  <span>このヘルプを表示</span>
                </div>
              </div>
            </div>

            <!-- 今後追加される機能のプレースホルダー（非表示）
            <div class="help-section" style="margin-top: var(--oh3-spacing-6);">
              <h3 style="margin-bottom: var(--oh3-spacing-3); font-size: var(--oh3-font-size-md); font-weight: bold; color: #1976d2;">
                <v-icon size="small" color="primary">mdi-help-circle</v-icon>
                使い方ガイド
              </h3>
              <p style="font-size: var(--oh3-font-size-base); color: var(--oh3-color-text-secondary);">
                各描画モードの詳しい使い方、Tips、よくある質問などは今後追加予定です。
              </p>
            </div>
            -->
          </div>
        </template>
      </FloatingWindow>

      <!-- ドロー表示制御用FloatingWindow -->
      <DrawVisibilityWindow />

      <div
          ref="indicator"
          v-show="isDrawing && !s_isCursorOnPanel"
          class="draw-indicator"
      ><div id="draw-indicato-text" style="margin-top: var(--oh3-spacing-5);margin-left: 10px;"></div></div>

      <!-- 円半径ドラッグ時のツールチップ -->
      <div id="circle-radius-tooltip" class="circle-radius-tooltip"></div>

      <!-- リアルタイム面積・距離表示（描画中＋クリック時） -->
      <div
          v-show="showDrawInfo"
          class="draw-realtime-info bg-primary"
      >
        <span
            class="draw-info-copy-btn"
            role="button"
            aria-label="コピー"
            @click="copyDrawInfo"
        >
          <v-icon style="pointer-events: none;">mdi-content-copy</v-icon>
        </span>
        <span
            class="draw-info-close-x"
            role="button"
            aria-label="閉じる"
            @click="closeDrawInfo"
        >
          <v-icon style="pointer-events: none;">mdi-close</v-icon>
        </span>
        <div id="draw-realtime-info"></div>
      </div>

      <DrawDrawer
          v-if="showDrawDrawer"
          :value="showDrawDrawer"
          :feature="selectedPointFeature"
      />
      <DrawListDrawer
          v-if="showDrawListDrawer"
          :value="showDrawListDrawer"
          :feature="selectedPointFeature"
      />

      <AboutDrawer />

      <ChibanzuDrawer
          v-if="showChibanzuDrawer"
          :value="showChibanzuDrawer"
      />

      <RightDrawer />
      <RightDrawerTokizyo />

      <GyoseiHistoryDrawer />

      <PointInfoDrawer
          v-if="showPointInfoDrawer"
          :value="showPointInfoDrawer"
          :feature="selectedPointFeature"
          @input="setDrawerVisible"
          @save="savePointDescription"
      />

      <v-snackbar v-model="snackbar" :timeout="3000" color="primary">
        {{ snackbarText }}
      </v-snackbar>

      <v-snackbar content-class="snack-max"
                  v-model="loadingSnackbar"
                  :timeout="-1"
                  color="primary"
                  location="top"
                  class="my-snackbar"
      >
        <div v-stick-bottom.smooth="{ threshold: 40 }" class="snack-scroll" v-if="s_loading">処理中です。</div>
        <div v-stick-bottom.smooth="{ threshold: 40 }" class="snack-scroll" v-if="s_loading2"><span v-html="s_loadingMessage"></span></div>
        <div v-stick-bottom.smooth="{ threshold: 40 }" class="snack-scroll" v-if="s_loading3"><span v-html="s_loadingMessage3"></span></div>
      </v-snackbar>

      <v-snackbar v-model="s_snackbarForGroup" :timeout="3000" color="primary">
        {{ s_snackbarForGroupText }}
      </v-snackbar>

      <v-snackbar
          v-model="s_snackbar"
          :timeout="-1"
          color="primary"
          location="bottom"
      >
<div>
          <!-- SIMA buttons -->
          <template v-if="hasSimaLayers">
            <v-btn v-track="'sima-download'" color="white" class="tiny-btn" style="margin-bottom: 0px!important;" text @click="simaDl">
              {{ hasSimaLayers && hasDxfLayers ? 'SIMA DL' : 'SIMAダウンロード' }}
            </v-btn>
            <v-btn v-if="userId" v-track="'sima-part-download'" color="white" class="tiny-btn" style="margin-left: 10px;margin-bottom: 0px!important;" text @click="simaPartDl">
              {{ hasSimaLayers && hasDxfLayers ? 'SIMA部分DL' : '部分ダウンロード' }}
            </v-btn>
            <v-btn v-track="'sima-add-draw'" color="white" class="tiny-btn" style="margin-left: 10px;margin-bottom: 0px!important;" text @click="simaAddDraw">
              {{ hasSimaLayers && hasDxfLayers ? 'SIMAドローへ' : 'ドローへ' }}
            </v-btn>
            <v-btn v-if="!userId" v-track="'sima-delete'" style="margin-left: 10px;" color="pink" text @click="simaDelete">削除</v-btn>
          </template>
          <!-- DXF buttons -->
          <template v-if="hasDxfLayers">
            <v-btn v-track="'dxf-download'" color="grey-lighten-2" class="tiny-btn" :style="hasSimaLayers ? 'margin-left: 10px;margin-bottom: 0px!important;' : 'margin-bottom: 0px!important;'" text @click="dxfDl">
              {{ hasSimaLayers && hasDxfLayers ? 'DXF DL' : 'DXFダウンロード' }}
            </v-btn>
            <!-- DXFは線分で構成されるため部分ダウンロードは非対応 -->
            <!-- <v-btn v-if="userId" v-track="'dxf-part-download'" color="grey-lighten-2" class="tiny-btn" style="margin-left: 10px;margin-bottom: 0px!important;" text @click="dxfPartDl">
              {{ hasSimaLayers && hasDxfLayers ? 'DXF部分DL' : '部分ダウンロード' }}
            </v-btn> -->
            <v-btn v-if="!userId" v-track="'dxf-delete'" style="margin-left: 10px;" color="pink" text @click="dxfDelete">削除</v-btn>
          </template>
        </div>
        <template v-slot:actions>
          <v-btn v-track="'file-close'" color="white" text @click="fileClose">閉じる</v-btn>
        </template>
      </v-snackbar>

      <!-- 印刷モードのヘッダー（チップ群） -->
      <div v-if="s_isPrint" class="print-header-area">
        <v-chip-group v-model="s_printMap" mandatory class="print-chips-group">
          <v-chip @click="s_printMap01" value="map01" variant="flat" color="white">Map1</v-chip>
          <v-chip @click="s_printMap02" value="map02" variant="flat" color="white">Map2</v-chip>
          <v-chip @click="s_printMap03" value="map03" variant="flat" color="white">2画面</v-chip>
          <!-- 詳細/広域ドロップダウン -->
          <v-menu offset-y>
            <template v-slot:activator="{ props }">
              <v-chip v-bind="props" variant="flat" :color="printZoomOffset !== 0 ? 'light-blue-lighten-3' : 'white'">
                詳細/広域{{ printZoomOffset !== 0 ? ` (${printZoomOffset > 0 ? '+' : ''}${printZoomOffset})` : '' }}
                <v-icon end size="small">mdi-menu-down</v-icon>
              </v-chip>
            </template>
            <v-list density="compact">
              <v-list-item @click="setPrintZoomOffset(3)">
                <v-list-item-title>広域 +3（下を3段階ズームアウト）</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPrintZoomOffset(2)">
                <v-list-item-title>広域 +2（下を2段階ズームアウト）</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPrintZoomOffset(1)">
                <v-list-item-title>広域 +1（下を1段階ズームアウト）</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="setPrintZoomOffset(0)">
                <v-list-item-title>オフ（同じズーム）</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="setPrintZoomOffset(-1)">
                <v-list-item-title>詳細 -1（下を1段階ズームイン）</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPrintZoomOffset(-2)">
                <v-list-item-title>詳細 -2（下を2段階ズームイン）</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPrintZoomOffset(-3)">
                <v-list-item-title>詳細 -3（下を3段階ズームイン）</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-chip-group>
        <!-- 印刷モード時のレイヤー選択ボタン -->
        <div class="print-layer-btns">
          <v-btn size="x-small" variant="outlined" @click="printLayerBtn('map01')">
            <v-icon size="small" class="mr-1">mdi-layers</v-icon>Map1レイヤー
          </v-btn>
          <v-btn size="x-small" variant="outlined" @click="printLayerBtn('map02')">
            <v-icon size="small" class="mr-1">mdi-layers</v-icon>Map2レイヤー
          </v-btn>
        </div>
      </div>

      <!-- 有償化開始バナー（画面上部常設・map00の外） -->
      <div v-if="!s_isPrint" ref="paidBanner" class="paid-launch-banner" :class="['paid-launch-banner--' + (trialBannerStage || 'default')]">
        <!-- 体験プラン残量警告 -->
        <template v-if="trialBannerStage">
          <v-icon size="small" class="mr-1">mdi-alert</v-icon>
          <span class="trial-warn-text">
            体験プランの残り {{ trialPointBalance }}PT
            <template v-if="trialMonthlyUsage !== null && trialRecommendedPlan">
              <template v-if="!isSmall500">
                ／過去30日の使用 {{ trialMonthlyUsage }}PT → おすすめは
              </template>
              <template v-else>
                ／おすすめ
              </template>
              <strong>{{ trialRecommendedPlan.name }}</strong>
            </template>
          </span>
          <v-btn
            size="x-small"
            variant="flat"
            color="white"
            class="ml-2 paid-launch-banner-btn"
            @click="$store.state.openPlanTab = true"
          >
            <v-icon start size="small">mdi-star</v-icon>
            プラン
          </v-btn>
        </template>
        <!-- 通常（体験プラン以外 or 残量十分） -->
        <template v-else>
          <v-icon size="small" class="mr-1">mdi-information</v-icon>
          <span>2026年5月より有償プランの提供を開始します。<template v-if="!isSmall500">（地図閲覧は引き続き無償です。）</template></span>
          <v-btn
            size="x-small"
            variant="flat"
            color="white"
            class="ml-2 paid-launch-banner-btn"
            @click="$store.state.openPlanTab = true"
          >
            <v-icon start size="small">mdi-star</v-icon>
            プラン
          </v-btn>
          <a href="faq.html" target="_blank" rel="noopener noreferrer" class="banner-policy-link">FAQ</a>
          <a href="tokushoho.html" target="_blank" rel="noopener noreferrer" class="banner-policy-link">特定商取引法</a>
          <a href="privacy.html" target="_blank" rel="noopener noreferrer" class="banner-policy-link">プライバシーポリシー</a>
        </template>
      </div>

      <div id="map00" :class="{ 'print-mode': s_isPrint }" :style="!s_isPrint ? { height: 'calc(100dvh - ' + bannerHeight + 'px)' } : {}">
        <!-- ⭐️ここに置くと印刷プレビュー時もプレビュー内に収まる -->
        <FloatingWindow
            windowId="qrcode"
            type="simple"
            :default-width=200
            :default-height=200
            :keepAspectRatio="true"
            @width-changed="onWidthChanged"
        >
          <vue-qrcode :value="s_url" :options="{ width: qrCodeWidth }"></vue-qrcode>
        </FloatingWindow>

        <v-icon
            v-if="isUsingServerGeojson && !s_isPrint"
            color="info"
            size="32"
            class="pulse"
        >
          mdi-server-network
        </v-icon>

<!--        <v-btn @click="openPaintEditorWindow">test</v-btn>-->

<!--        <v-btn @click="test">test</v-btn>-->

        <div v-for="mapName in mapNames" :key="mapName" :id=mapName :style="[getMapStyle(mapName), (mapName === 'map01' || mapName === 'map02') && s_isPrint ? { border: '#000 1px solid' } : {}]" v-show="shouldShowMap(mapName)" @click="btnPosition"
             @dragover.prevent="is4ScreenMode && onMapDragEnter($event, mapName)"
             @dragleave="is4ScreenMode && onMapDragLeave($event)"
             @drop.prevent="is4ScreenMode && onMapDrop($event, mapName)"
        >
          <v-progress-linear  v-if="s_loading && !noProgress" style="z-index: 1" indeterminate color="blue"></v-progress-linear>
          <v-progress-linear  v-if="s_loading2 && !noProgress" style="z-index: 1" indeterminate color="blue"></v-progress-linear>
          <v-progress-linear  v-if="s_loading3 && !noProgress" style="z-index: 1" indeterminate color="primary"></v-progress-linear>
          <!-- 桜エフェクト（一時無効化。復活時はコメント解除） -->
          <!-- <SakuraEffect :active="$store.state.showSakuraEffect" /> -->

          <RightButtons
              v-if="mapName === 'map01'"
              :handlers="handlers"
              :is-map2-mode="s_map2Flg"
              @start-tour="startTour"
          ></RightButtons>

          <LayerCredits :map-name="mapName" />

          <MagnifyingGlass v-if="mapName === 'map01' && mapReady" />

          <!-- 強化版フリーハンドキャンバス -->
          <FreehandCanvas
              v-if="mapReady"
              :ref="'freehandCanvas_' + mapName"
              :map-name="mapName"
              @confirm="onFreehandConfirm"
              @close="onFreehandClose"
          />

          <!-- 投げ縄キャンバス -->
          <LassoCanvas
              v-if="mapName === 'map01' && mapReady"
              :map-name="mapName"
          />

          <!-- フリーメモ（全画面キャンバス） -->
          <FieldNoteMemo v-if="mapName === 'map01' && mapReady" />

          <div>
            <div id="pointer1" class="pointer" v-if="mapName === 'map01'"></div>
            <div id="pointer2" class="pointer" v-if="mapName === 'map02' && !swipeMode"></div>
            <div id="pointer3" class="pointer" v-if="mapName === 'map03' && is4ScreenMode"></div>
            <div id="pointer4" class="pointer" v-if="mapName === 'map04' && is4ScreenMode"></div>
          </div>

          <!-- 4画面モード用ドラッグハンドル -->
          <v-tooltip v-if="is4ScreenMode" location="right" text="ドラッグして他の地図上にドロップで位置入替" :disabled="!!draggingMapName">
            <template v-slot:activator="{ props }">
              <div
                v-bind="props"
                class="map-drag-handle"
                draggable="true"
                @dragstart="onMapDragStart($event, mapName)"
                @dragend="onMapDragEnd"
              >
                <v-icon size="16" color="white">mdi-drag</v-icon>
              </div>
            </template>
          </v-tooltip>

          <!-- 4画面モード用レイヤー名ラベル -->
          <div v-if="is4ScreenMode && !s_isPrint" class="map-layer-label">
            {{ getTopLayerLabel(mapName) }}
          </div>

          <!-- コンパス（map01のみ表示） -->
          <div :class="compassDivClass"
               v-if="mapName === 'map01' && (s_isPrint || isTracking || s_isKuiuchi)"
               @click="compassClick"
          >
            <!-- コンパス（OH3ミニマル） -->
            <div class="compass-wrap" title="Compass">
              <svg class="compass-icon" viewBox="0 0 100 100" width="60" height="60" aria-label="Compass">
                <defs>
                  <!-- ほのかな内側グロウ -->
                  <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="1.2" result="blur"/>
                    <feMerge>
                      <feMergeNode in="blur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                  <!-- 外周リングの微グラデ -->
                  <linearGradient id="ringGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="rgba(255,255,255,0.35)"/>
                    <stop offset="1" stop-color="rgba(255,255,255,0.08)"/>
                  </linearGradient>
                </defs>

                <!-- 背面 -->
                <g filter="url(#softGlow)">
                  <circle cx="50" cy="50" r="48" class="c-bg"/>
                  <!-- 外周リング（テーマ色×グラデ） -->
                  <circle cx="50" cy="50" r="46"
                          class="c-ring"
                          stroke="url(#ringGrad)"/>
                </g>

                <!-- 目盛り（30°刻み＋主要方位は太く） -->
                <g class="c-ticks" stroke-linecap="round">
                  <!-- 12本（0〜330°） -->
                  <g transform="translate(50,50)">
                    <!-- 主要4方向 -->
                    <g stroke-width="2.2">
                      <line y1="-40" y2="-46" transform="rotate(0)"/>
                      <line y1="-40" y2="-46" transform="rotate(90)"/>
                      <line y1="-40" y2="-46" transform="rotate(180)"/>
                      <line y1="-40" y2="-46" transform="rotate(270)"/>
                    </g>
                    <!-- 準目盛り -->
                    <g stroke-width="1.2" opacity="0.7">
                      <line y1="-42" y2="-46" transform="rotate(30)"/>
                      <line y1="-42" y2="-46" transform="rotate(60)"/>
                      <line y1="-42" y2="-46" transform="rotate(120)"/>
                      <line y1="-42" y2="-46" transform="rotate(150)"/>
                      <line y1="-42" y2="-46" transform="rotate(210)"/>
                      <line y1="-42" y2="-46" transform="rotate(240)"/>
                      <line y1="-42" y2="-46" transform="rotate(300)"/>
                      <line y1="-42" y2="-46" transform="rotate(330)"/>
                    </g>
                  </g>
                </g>

                <!-- 針（北=赤、南=薄グレー）。回転させたい場合はこのgに transform を当てる -->
                <g class="c-needle" transform="rotate(0 50 50)">
                  <!-- 北側 -->
                  <polygon class="c-needle-n"
                           points="50,10 57,50 50,44 43,50"/>
                  <!-- 南側 -->
                  <polygon class="c-needle-s"
                           points="50,90 43,50 50,56 57,50"/>
                  <!-- 中心キャップ -->
                  <circle cx="50" cy="50" r="4.2" class="c-cap"/>
                </g>

                <!-- 方位文字 -->
                <g class="c-labels" font-size="10" font-weight="600">
                  <text x="50" y="18" text-anchor="middle" class="c-N">N</text>
                  <text x="84" y="54" text-anchor="middle">E</text>
                  <text x="50" y="88" text-anchor="middle">S</text>
                  <text x="16" y="54" text-anchor="middle">W</text>
                </g>
              </svg>
            </div>
          </div>

          <div class="scale-ratio" v-if="s_isPrint">{{scaleText}}</div>

          <div ref="printTitle" :style="{fontSize: textPx + 'px', color: titleColor}" class="print-title">
            <MiniTooltip text="click me" :offset-x="0" :offset-y="4">
              <span
                  @click="drawConfig"
                  v-if="getMapTitle(mapName).trim().length > 0"
                  class="print-title-bg"
                  v-html="getMapTitle(mapName).replace(/\n/g, '<br>')"
              ></span>
              <span v-else class="dot" @click="drawConfig"></span>
            </MiniTooltip>
          </div>
          <!-- 印刷用日付 -->
          <div v-if="s_isPrint && printDate" :style="{fontSize: (textPx * 0.55) + 'px', color: titleColor, top: (5 + printTitleHeight) + 'px'}" class="print-date">
            <span class="print-date-bg">{{ formatPrintDate(printDate) }}</span>
          </div>
          <div v-if="isIframe" id="iframe-div">
              <MiniTooltip text="全画面表示" :offset-x="0" :offset-y="2">
                <v-btn v-track="'fullscreen-iframe'" :size="isSmall ? 'small' : 'default'" icon @click="fullscreenForIframe" v-if="mapName === 'map01'"><v-icon>mdi-fullscreen</v-icon></v-btn>
              </MiniTooltip>
          </div>
          <div v-if="!s_isPrint && showCrosshair" class="center-target" :class="{ 'center-target-adjusted': isReportDialogOpenOnSmallScreen || isElevationDialogOpenOnSmallScreen, 'center-target-adjusted-50': isOfflineDialogOpenOnSmallScreen || isRightDrawerOpenOnSmallScreen, 'center-target-bottom-sheet': bottomSheetActive, 'center-target-drawer-right': isRightDrawerOpenOnLargeScreen, 'center-target-mlit-drawer': isMlitDrawerOpenOnLargeScreen, 'center-target-mlit-drawer-sm': isMlitDrawerOpenOnSmallScreen }"></div>

          <!-- 新規登録促進ダイアログ（利用規約同意後に表示） -->
          <MessageDialog
              v-if="!authUser && authStateReady && termsAgreed && mapName === 'map01' && !isIframe && !$store.state.skipTerms && $store.state.urlParamsReady"
              v-model="showSignUpPromoDialog"
              :max-width="600"
              :show-close-icon="true"
              :show-title-divider="false"
              :show-actions-divider="false"
              :card-props="{ class: 'signup-promo-dialog' }"
              dont-show-key="oh3.hideSignUpPromo"
              @close="showSignUpPromoDialog = false"
          >
            <template v-slot:title>
              <div class="d-flex align-center">
                <v-icon color="primary" size="32" class="mr-2">mdi-rocket-launch</v-icon>
                <span class="text-h6 font-weight-bold">もっと便利に！登録ユーザーの特典</span>
              </div>
            </template>

            <div class="signup-promo-content">
              <div class="promo-hero">
                <v-icon size="80" color="primary" class="mb-4">mdi-account-star</v-icon>
                <h2 class="text-h6 font-weight-bold mb-2">アカウント登録で広がる可能性</h2>
                <p class="text-body-2 text-medium-emphasis mb-4">メールアドレスだけで今すぐ始められます</p>
              </div>

              <v-card class="features-card mb-4" variant="tonal" color="primary">
                <v-card-text>
                  <div class="feature-item">
                    <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
                    <div>
                      <div class="font-weight-bold">SIMA・DXF・測量データの永久保存</div>
                      <div class="text-caption">マイルームに無制限で保存・いつでも呼び出し</div>
                    </div>
                  </div>

                  <v-divider class="my-3" />

                  <div class="feature-item">
                    <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
                    <div>
                      <div class="font-weight-bold">GPS写真の地図配置 <v-chip size="x-small" color="error" class="ml-1">NEW!</v-chip></div>
                      <div class="text-caption">位置情報つきの写真を自動で地図上に配置</div>
                    </div>
                  </div>

                  <v-divider class="my-3" />

                  <div class="feature-item">
                    <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
                    <div>
                      <div class="font-weight-bold">描いた図形・測量データの保存</div>
                      <div class="text-caption">作業途中でも安心。次回すぐに再開可能</div>
                    </div>
                  </div>

                  <v-divider class="my-3" />

                  <div class="feature-item">
                    <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
                    <div>
                      <div class="font-weight-bold">複数デバイスで同じ環境</div>
                      <div class="text-caption">現場のスマホ→事務所のPCでシームレス連携</div>
                    </div>
                  </div>

                  <!-- PCのみ表示 -->
                  <template v-if="!isMobileDevice">
                    <v-divider class="my-3" />

                    <div class="feature-item">
                      <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
                      <div>
                        <div class="font-weight-bold">高度なデータ変換機能</div>
                        <div class="text-caption">Sima、GeoJSON、GeoTIFF、KML/KMZ、GPX、PDF等々</div>
                      </div>
                    </div>
                  </template>
                </v-card-text>
              </v-card>

              <div class="text-center mb-3">
                <v-btn
                    color="primary"
                    size="large"
                    variant="elevated"
                    @click="startSignUp"
                    class="px-8 signup-cta-btn"
                >
                  <v-icon left class="mr-2">mdi-account-plus</v-icon>
                  今すぐ無料登録
                </v-btn>
              </div>

              <!-- 使い方を見るボタン（一時的に非表示）
              <div v-if="!isSmall500" class="text-center mb-3">
                <v-btn
                    color="success"
                    size="large"
                    variant="outlined"
                    @click="startTour"
                    class="px-8"
                >
                  <v-icon left class="mr-2">mdi-school</v-icon>
                  使い方を見る
                </v-btn>
              </div>
              -->

              <div class="text-center">
                <v-btn
                    variant="text"
                    size="small"
                    @click="startLogin"
                >
                  すでにアカウントをお持ちの方はこちら
                </v-btn>
              </div>
            </div>

            <template v-slot:actions>
              <!-- 次回から表示しないは非ログインユーザーへ毎回表示するためコメントアウト
              <v-checkbox
                  v-model="dontShowSignUpPromo"
                  class="ma-0 pa-0"
                  color="primary"
                  hide-details
                  density="compact"
                  label="次回から表示しない"
              />
              -->
              <v-spacer />
              <v-btn
                  variant="text"
                  @click="closeSignUpPromo"
              >
                閉じる
              </v-btn>
            </template>
          </MessageDialog>

          <!--左上部メニュー-->
          <div v-if="!isIframe" id="left-top-div" :style="{ pointerEvents: $store.state.isFreehandCanvasActive ? 'none' : 'auto', opacity: $store.state.isFreehandCanvasActive ? 0.3 : 1 }">
            <span v-if="!s_isPrint">
              <MiniTooltip text="リセット" :offset-x="4" :offset-y="-2">
                <v-btn v-track="'main-reset'" :size="isSmall ? 'small' : 'default'" icon @click="reset" v-if="mapName === 'map01'" :disabled="$store.state.isNetworkOffline"><v-icon>mdi-refresh</v-icon></v-btn>
              </MiniTooltip>
              <MiniTooltip text="基本設定" :offset-x="4" :offset-y="-2">
                <v-btn v-track="'main-menu'" :size="isSmall ? 'small' : 'default'" icon style="margin-left:8px;" @click="btnClickMenu(mapName)" v-if="mapName === 'map01'"><v-icon>mdi-menu</v-icon></v-btn>
              </MiniTooltip>
              <!-- 非ログイン時 -->
              <MiniTooltip text="ログイン" :offset-x="4" :offset-y="-2">
                <v-btn v-track="'login'" :size="isSmall ? 'small' : 'default'" icon style="margin-left:8px;" @click="openLoginDialog" v-if="mapName === 'map01' && !authUser"><v-icon>mdi-login</v-icon></v-btn>
              </MiniTooltip>
              <MiniTooltip text="新規登録" :offset-x="4" :offset-y="-2">
                <v-btn v-track="'signup'" :size="isSmall ? 'small' : 'default'" icon style="margin-left:8px;" @click="openSignUpDialog" v-if="mapName === 'map01' && !authUser" color="primary"><v-icon>mdi-account-plus</v-icon></v-btn>
              </MiniTooltip>
              <!-- ログイン済み時 -->
              <MiniTooltip text="アカウント" :offset-x="4" :offset-y="-2">
                <v-btn v-track="'account'" :size="isSmall ? 'small' : 'default'" icon style="margin-left:8px;" @click="s_dialogForLogin = true" v-if="mapName === 'map01' && authUser"><v-icon>mdi-account-circle</v-icon></v-btn>
              </MiniTooltip>
              <v-menu v-if="mapName === 'map01'" :close-on-content-click="true">
                <template v-slot:activator="{ props }">
                  <MiniTooltip text="複数画面表示" :offset-x="4" :offset-y="-2">
                    <v-btn v-track="'split-screen'" :size="isSmall ? 'small' : 'default'" icon style="margin-left:8px;" v-bind="props" :disabled="swipeMode"><v-icon>mdi-monitor-multiple</v-icon></v-btn>
                  </MiniTooltip>
                </template>
                <v-list density="compact" class="draw-tool-menu">
                  <v-list-item v-track="'screen-mode-1'" @click="setScreenMode(1)" :active="!s_map2Flg && !is4ScreenMode">
                    <template v-slot:prepend>
                      <v-icon>mdi-monitor</v-icon>
                    </template>
                    <v-list-item-title>1画面表示</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-track="'screen-mode-2'" @click="setScreenMode(2)" :active="s_map2Flg && !is4ScreenMode">
                    <template v-slot:prepend>
                      <v-icon>mdi-monitor-multiple</v-icon>
                    </template>
                    <v-list-item-title>2画面表示</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-track="'screen-mode-4'" @click="setScreenMode(4)" :active="is4ScreenMode" :disabled="isSmall500">
                    <template v-slot:prepend>
                      <v-icon>mdi-view-grid</v-icon>
                    </template>
                    <v-list-item-title>4画面表示</v-list-item-title>
                    <template v-slot:append v-if="isSmall500">
                      <span style="font-size: var(--oh3-font-size-xs); color: var(--oh3-color-text-tertiary);">PC専用</span>
                    </template>
                  </v-list-item>
                  <!-- 4画面プリセット（プリセット内容確定後に有効化）
                  <v-divider v-if="!isSmall500" class="my-1"></v-divider>
                  <v-list-subheader v-if="!isSmall500" class="text-caption">4画面プリセット</v-list-subheader>
                  <v-list-item v-if="!isSmall500" v-track="'preset-timeline'" @click="apply4ScreenPreset('timeline')" density="compact">
                    <template v-slot:prepend>
                      <v-icon size="small">mdi-timeline-clock</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">時系列比較</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!isSmall500" v-track="'preset-history'" @click="apply4ScreenPreset('history')" density="compact">
                    <template v-slot:prepend>
                      <v-icon size="small">mdi-map-legend</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">歴史探索</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!isSmall500" v-track="'preset-realestate'" @click="apply4ScreenPreset('realestate')" density="compact">
                    <template v-slot:prepend>
                      <v-icon size="small">mdi-home-city</v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">不動産調査</v-list-item-title>
                  </v-list-item>
                  -->
                </v-list>
              </v-menu>
                <MiniTooltip text="マイルーム" :offset-x="4" :offset-y="-2">
              <v-btn v-track="'myroom'" :size="isSmall ? 'small' : 'default'" v-if="mapName === 'map01'" icon style="margin-left:8px;" @click="onMyroomClick(mapName)"><v-icon>mdi-home</v-icon></v-btn>
              </MiniTooltip>
              <!-- <MiniTooltip text="グループ機能">-->
              <!--   <v-btn :size="isSmall ? 'small' : 'default'" v-if="userId && mapName === 'map01'" icon style="margin-left:8px;" @click="s_dialogForGroup = !s_dialogForGroup"><v-icon v-if="userId">mdi-account-supervisor</v-icon></v-btn>-->
              <!--  </MiniTooltip>-->
              <v-menu offset-y :close-on-content-click="true" v-if="userId && mapName === 'map01'">
                <template v-slot:activator="{ props }">
                  <MiniTooltip text="測位" :offset-x="4" :offset-y="-2">
                    <v-btn
                        v-bind="props"
                        :size="isSmall ? 'small' : 'default'"
                        icon
                        style="margin-left:8px;"
                    >
                      <img :src="rtkPng" alt="RTK-GNSS座標取得" class="btn-img" />
                    </v-btn>
                  </MiniTooltip>
                </template>
                <v-list density="compact" class="draw-tool-menu">
                  <v-list-item v-track="'positioning'" @click="openJobPickerWithToken">
                    <template v-slot:prepend>
                      <v-icon>mdi-crosshairs-gps</v-icon>
                    </template>
                    <v-list-item-title>測位</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-track="'stake-placing'" @click="toggleWatchPosition('k'); onJobEndClick(true)">
                    <template v-slot:prepend>
                      <v-icon>mdi-map-marker-plus</v-icon>
                    </template>
                    <v-list-item-title>杭打</v-list-item-title>
                  </v-list-item>
                  <!-- シンプルOCR - 求積表機能に統合のためコメントアウト
                  <v-list-item v-track="'ocr-handwrite'" @click="openHandwriteDialog">
                    <template v-slot:prepend>
                      <v-icon>mdi-text-recognition</v-icon>
                    </template>
                    <v-list-item-title>シンプルOCR</v-list-item-title>
                  </v-list-item>
                  -->
                  <v-list-item v-track="'kyuseki-table'" @click="openKyusekiDialogWithToken">
                    <template v-slot:prepend>
                      <v-icon>mdi-table</v-icon>
                    </template>
                    <v-list-item-title>求積表</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- map01のレイヤーボタン（左上） -->
              <MiniTooltip v-if="mapName === 'map01'" text="レイヤー選択" :offset-x="4" :offset-y="-2">
                <v-btn id="layer-selection-btn" v-track="'layer-selection'" :size="isSmall ? 'small' : 'default'" icon style="margin-left:8px;" @click="btnClickLayer(mapName)"><v-icon>mdi-layers</v-icon></v-btn>
              </MiniTooltip>
            </span>
          </div>



          <!-- map02用レイヤーボタン（常に右上に配置、印刷モード時は非表示） -->
          <div v-if="mapName === 'map02' && !s_isPrint" class="map02-layer-btn">
            <MiniTooltip text="Map2レイヤー選択" :offset-x="-4" :offset-y="-2">
              <v-btn v-track="'layer-selection-map02'" :size="isSmall ? 'small' : 'default'" icon color="primary" @click="btnClickLayer('map02')"><v-icon>mdi-layers</v-icon></v-btn>
            </MiniTooltip>
          </div>

          <!-- map03用レイヤーボタン（4画面モード時、印刷モード時は非表示） -->
          <div v-if="mapName === 'map03' && is4ScreenMode && !s_isPrint" class="map02-layer-btn">
            <MiniTooltip text="Map3レイヤー選択" :offset-x="-4" :offset-y="-2">
              <v-btn v-track="'layer-selection-map03'" :size="isSmall ? 'small' : 'default'" icon color="primary" @click="btnClickLayer('map03')"><v-icon>mdi-layers</v-icon></v-btn>
            </MiniTooltip>
          </div>

          <!-- map04用レイヤーボタン（4画面モード時、印刷モード時は非表示） -->
          <div v-if="mapName === 'map04' && is4ScreenMode && !s_isPrint" class="map02-layer-btn">
            <MiniTooltip text="Map4レイヤー選択" :offset-x="-4" :offset-y="-2">
              <v-btn v-track="'layer-selection-map04'" :size="isSmall ? 'small' : 'default'" icon color="primary" @click="btnClickLayer('map04')"><v-icon>mdi-layers</v-icon></v-btn>
            </MiniTooltip>
          </div>

          <div class="terrain-btn-wrap" v-show="mapName === 'map01' || (mapName === 'map02' && !s_isPrint) || mapName === 'map03' || mapName === 'map04'">
            <div
                :id="'terrain-btn-div-' + mapName"
                class="terrain-btn-div"
                v-show="!s_isPrint"
            >
              <div class="terrain-btn-container">
                <v-icon class="terrain-btn-close" @pointerdown="terrainBtnClos">mdi-close</v-icon>

                <!-- 3Dメニュー（右上） -->
                <div class="terrain-3d-menu-wrapper">
                  <span class="terrain-btn-3d-label" @click="terrain3dMenu1s[mapName] = !terrain3dMenu1s[mapName]">3D</span>
                  <div v-show="terrain3dMenu1s[mapName]" class="terrain-3d-dropdown" @pointerdown.stop @touchstart.stop @mousedown.stop>
                    <v-icon class="terrain-menu-close" size="18" @click="terrain3dMenu1s[mapName] = false">mdi-close</v-icon>
                    <div class="terrain-3d-item" :class="{ active: is3dOff }" @click="handle3dOff(mapName)">
                      <v-icon :color="is3dOff ? 'primary' : ''">mdi-cube-off-outline</v-icon>
                      <span>3Dオフ</span>
                    </div>
                    <div class="terrain-3d-item" :class="{ active: is3dNoTerrain }" @click="handle3dNoTerrain(mapName)">
                      <v-icon :color="is3dNoTerrain ? 'primary' : ''">mdi-rotate-3d</v-icon>
                      <span>3D表示（地形なし）</span>
                    </div>
                    <div class="terrain-3d-item" :class="{ active: is3dWithTerrain }" @click="handle3dWithTerrain(mapName)">
                      <v-icon :color="is3dWithTerrain ? 'primary' : ''">mdi-image-filter-hdr</v-icon>
                      <span>3D表示（地形あり）</span>
                    </div>
                    <div class="terrain-3d-item" @click="openDiorama3D(mapName)">
                      <v-icon>mdi-cube-scan</v-icon>
                      <span>3Dジオラマ</span>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="terrain-slider-container">
                      <div class="terrain-slider-label">標高強調: <strong>{{ s_terrainLevel }}倍</strong></div>
                      <v-slider
                        v-model="s_terrainLevel"
                        :min="1"
                        :max="10"
                        :step="0.1"
                        hide-details
                        color="primary"
                        density="compact"
                        @update:modelValue="terrainLevelInput"
                      ></v-slider>
                    </div>
                  </div>
                </div>

                <v-btn v-track="'terrain-pitch-up'" type="button" class="terrain-btn-up terrain-btn"
                       @pointerdown="pressPitchUp(mapName)" @pointerup="releaseOrientationButtons">
                  <v-icon>mdi-arrow-up</v-icon>
                </v-btn>

                <v-btn v-track="'terrain-pitch-down'" type="button" class="terrain-btn-down terrain-btn"
                       @pointerdown="pressPitchDown(mapName)" @pointerup="releaseOrientationButtons">
                  <v-icon>mdi-arrow-down</v-icon>
                </v-btn>

                <v-btn v-track="'terrain-rotate-left'" type="button" class="terrain-btn-left terrain-btn"
                       @pointerdown="pressRotateRight(mapName)" @pointerup="releaseOrientationButtons">
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>

                <v-btn v-track="'terrain-rotate-right'" type="button" class="terrain-btn-right terrain-btn"
                       @pointerdown="pressRotateLeft(mapName)" @pointerup="releaseOrientationButtons">
                  <v-icon>mdi-arrow-right</v-icon>
                </v-btn>

                <v-btn v-track="'terrain-reset'" icon type="button" class="terrain-btn-center terrain-btn"
                       @pointerdown="terrainReset(mapName)">
                  <v-icon>mdi-undo</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-show="!s_isPrint && mapName === 'map01'" class="terrain-btn-expand-div">
              <v-btn
                v-track="'terrain-expand'"
                icon
                type="button"
                class="floating-action-btn"
                @click="terrain3dMenu2 = !terrain3dMenu2"
              >
                <v-icon>mdi-rotate-3d</v-icon>
              </v-btn>
              <div v-show="terrain3dMenu2" class="terrain-3d-dropdown terrain-3d-dropdown-fab" @pointerdown.stop @touchstart.stop @mousedown.stop>
                <v-icon class="terrain-menu-close" size="18" @click="terrain3dMenu2 = false">mdi-close</v-icon>
                <div class="terrain-3d-item" :class="{ active: is3dOff }" @click="handle3dOffFab()">
                  <v-icon :color="is3dOff ? 'primary' : ''">mdi-cube-off-outline</v-icon>
                  <span>3Dオフ</span>
                </div>
                <div class="terrain-3d-item" :class="{ active: is3dNoTerrain }" @click="handle3dNoTerrainFab()">
                  <v-icon :color="is3dNoTerrain ? 'primary' : ''">mdi-rotate-3d</v-icon>
                  <span>3D表示（地形なし）</span>
                </div>
                <div class="terrain-3d-item" :class="{ active: is3dWithTerrain }" @click="handle3dWithTerrainFab()">
                  <v-icon :color="is3dWithTerrain ? 'primary' : ''">mdi-image-filter-hdr</v-icon>
                  <span>3D表示（地形あり）</span>
                </div>
                <div class="terrain-3d-item" @click="openDiorama3D('map01')">
                  <v-icon>mdi-cube-scan</v-icon>
                  <span>3Dジオラマ</span>
                </div>
                <v-divider class="my-2"></v-divider>
                <div class="terrain-slider-container">
                  <div class="terrain-slider-label">標高強調: <strong>{{ s_terrainLevel }}倍</strong></div>
                  <v-slider
                    v-model="s_terrainLevel"
                    :min="1"
                    :max="10"
                    :step="0.1"
                    hide-details
                    color="primary"
                    density="compact"
                    @update:modelValue="terrainLevelInput"
                  ></v-slider>
                </div>
              </div>
            </div>

            <div class="zoom-div" :class="{ 'zoom-div-passthrough': isSmall500 }" v-if="!s_isPrint && mapName === 'map01'">
              {{ distance }}<br>
              <div v-html="geoQuality"></div>
              zoom={{ zoom.toFixed(2) }} {{ elevation }}<br>
              {{ s_address }}<span v-if="s_oazaYomi" class="oaza-yomi">（{{ s_oazaYomi }}）</span><br>
              {{ s_zahyokei }}
              {{ s_jdpCoordinates }}
            </div>
            <!-- ストリートビューペグマンボタン -->
            <v-btn
              v-if="!s_isPrint && mapName === 'map01' && !isJobMenu && !s_isKuiuchi"
              v-track="'pegman-button'"
              class="pegman-btn"
              :class="{ 'pegman-btn--active': $store.state.streetViewFlg }"
              icon
              size="small"
              variant="flat"
              @click.stop="togglePegman"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 40" class="pegman-icon">
                <circle cx="16" cy="7" r="5" fill="#FBBC04"/>
                <path d="M10 14 C10 12, 22 12, 22 14 L22 26 C22 27, 21 28, 20 28 L12 28 C11 28, 10 27, 10 26 Z" fill="#FBBC04"/>
                <path d="M10 15 L5 22 L7 23 L11 17" fill="#FBBC04" stroke="#E8A800" stroke-width="0.5"/>
                <path d="M22 15 L27 22 L25 23 L21 17" fill="#FBBC04" stroke="#E8A800" stroke-width="0.5"/>
                <path d="M12 28 L11 36 L14 36 L14 28" fill="#FBBC04" stroke="#E8A800" stroke-width="0.5"/>
                <path d="M18 28 L18 36 L21 36 L20 28" fill="#FBBC04" stroke="#E8A800" stroke-width="0.5"/>
                <circle cx="14" cy="6.5" r="1" fill="#333"/>
                <circle cx="18" cy="6.5" r="1" fill="#333"/>
              </svg>
            </v-btn>
            <!-- 画面中心情報ボタン（常時クリック可能） -->
            <v-btn
              v-if="!s_isPrint && mapName === 'map01' && !isJobMenu && !s_isKuiuchi"
              v-track="'zoom-info-button'"
              class="zoom-info-btn"
              icon
              size="small"
              color="primary"
              variant="flat"
              @click.stop="openZoomInfo"
            >
              <v-icon size="small">mdi-information-outline</v-icon>
            </v-btn>

            <!-- ext-ダイアログ一括表示FAB -->
            <ExtDialogFab v-if="mapName === 'map01'" />
          </div>

          <DialogMenu v-if="mapName === 'map01'" :mapName=mapName />
          <DialogMyroom v-if="mapName === 'map01'" :mapName=mapName />
          <!-- map02のDialogLayerはスワイプモード時にclipPathの影響を受けるため、map01のみここに配置 -->
          <DialogLayer v-if="mapName === 'map01' || !swipeMode" :mapName=mapName />
          <PopularLayers v-if="mapName === 'map01'" :mapName=mapName />
          <!-- map01のDialogExtはコンテナ内に配置、map02-04はCSS filterの影響を避けるため外に配置 -->
          <DialogExt v-if="mapName === 'map01'" :mapName=mapName />
          <!-- RRIM直接マウント（ext経由を廃止）複数同時対応 -->
          <ExtMpiRrim v-for="rrimItem in $store.state.rrimItems[mapName]" :key="rrimItem.id" :item="rrimItem" :mapName="mapName" style="display:none" />
          <dialog2 :mapName=mapName />
          <DialogShare v-if="mapName === 'map01'" :mapName=mapName />

        </div>

        <!-- スワイプ比較スライダー（小画面では非表示） -->
        <div v-if="swipeMode && !isSmall500" class="swipe-slider-container">
          <div
            class="swipe-handle"
            :style="{ left: swipePosition + '%' }"
            @mousedown.prevent.stop="startSwipeDrag"
            @touchstart.prevent.stop="startSwipeDrag"
          >
            <div class="swipe-handle-knob">
              <v-icon size="20" color="white">mdi-swap-horizontal</v-icon>
            </div>
          </div>
          <v-btn
            class="swipe-close-btn"
            icon
            size="small"
            color="error"
            @click="exitSwipeAndSplit"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- スワイプモード時のmap02用DialogLayer（clipPathの外に配置） -->
        <DialogLayer v-if="swipeMode" mapName="map02" />

        <!-- map02-04用DialogExt（CSS filterによるposition:fixedの問題を避けるためコンテナ外に配置） -->
        <DialogExt v-if="s_map2Flg || is4ScreenMode" mapName="map02" />
        <DialogExt v-if="is4ScreenMode" mapName="map03" />
        <DialogExt v-if="is4ScreenMode" mapName="map04" />
        <!-- RRIM直接マウント（map02用） -->
        <ExtMpiRrim v-for="rrimItem in $store.state.rrimItems['map02']" :key="rrimItem.id" :item="rrimItem" mapName="map02" style="display:none" />

        <!-- マップ連動/個別モード切り替えトグル（2画面・4画面時のみ、スワイプ・印刷時は非表示） -->
        <div
          v-if="(s_map2Flg || is4ScreenMode) && !swipeMode && !s_isPrint"
          class="map-sync-toggle-container"
        >
          <v-tooltip :text="mapSyncMode ? '連動モード：全マップが同期移動' : '個別モード：各マップ独立移動'" location="top">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :color="mapSyncMode ? 'primary' : 'grey'"
                size="x-small"
                icon
                class="map-sync-toggle-btn"
                @click="toggleMapSyncMode"
              >
                <v-icon size="18">{{ mapSyncMode ? 'mdi-link' : 'mdi-link-off' }}</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <!-- 求積表インポートダイアログが開いている時の地図操作ブロック用オーバーレイ -->
      <div
        v-if="$store.state.isKyusekiDialogOpen"
        class="map-overlay-blocker"
      ></div>

    </v-main>

    <!-- グローバルメッセージダイアログ（alert()の代わり） -->
    <message-dialog
      :use-store="true"
      store-module="messageDialog"
      dialog-id="global"
      :show-close-icon="true"
    />

    <!-- オンボーディング: 使用目的選択 -->
    <OnboardingPurposeSelector
      :visible="showPurposeSelector"
      @select="handlePurposeSelect"
      @skip="handleOnboardingSkip"
    />

    <!-- オンボーディングツールチップ -->
    <OnboardingTooltip
      v-if="currentOnboardingStep"
      :visible="showOnboardingTooltip"
      :target-selector="currentOnboardingStep.targetSelector"
      :message="currentOnboardingStep.message"
      :step="onboardingStepIndex + 1"
      :total-steps="onboardingTotalSteps"
      :show-skip="true"
      :show-next="hasMoreSteps"
      :position="currentOnboardingStep.position"
      @next="handleOnboardingNext"
      @skip="handleOnboardingSkip"
      @complete="handleOnboardingComplete"
    />

    <!-- OH3 使い方ツアー -->
    <OnboardingTooltip
      v-if="showUsageTour && currentTourSteps[usageTourStep]"
      :visible="showUsageTour"
      :target-selector="currentTourSteps[usageTourStep].targetSelector"
      :message="currentTourSteps[usageTourStep].message"
      :step="usageTourStep + 1"
      :total-steps="currentTourSteps.length"
      :show-skip="true"
      :show-next="usageTourStep < currentTourSteps.length - 1"
      :position="currentTourSteps[usageTourStep].position || 'auto'"
      :show-overlay="currentTourSteps[usageTourStep].showOverlay !== false"
      @next="handleUsageTourNext"
      @skip="handleUsageTourSkip"
      @complete="handleUsageTourComplete"
    />

    <!-- フッターリンク（小画面・印刷モードでは非表示、スプラッシュ後にフェードイン） -->
    <transition name="disclaimer-fade">
      <div v-if="showDisclaimer && !isSmall500 && !s_isPrint" class="disclaimer-footer">
        <!-- <span @click="showTermsDialog" style="cursor: pointer;">本サービスは登記・測量・境界確定等の法的判断を代替するものではありません。利用はすべて自己責任で行ってください。</span> -->
        <span class="footer-links">
          <a href="tokushoho.html" target="_blank" rel="noopener noreferrer">特定商取引法</a>
          <a href="privacy.html" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
        </span>
      </div>
    </transition>

    <!-- グローバル画像プレビュー（popup.js等から呼び出し可能） -->
    <MediaPreviewDialog
      v-model="globalMediaPreviewOpen"
      :src="globalMediaPreviewSrc"
      :kind="globalMediaPreviewKind"
      :filename="globalMediaPreviewFilename"
    />

    <!-- 3Dジオラマ -->
    <Diorama3D />
    <!-- 3D断面図 -->
    <CrossSection3D />

    <!-- 3D復元時のオーバーレイ（通常マップを隠す） -->
    <Transition name="fade-3d-overlay">
      <div v-if="$store.state.restoring3DOverlay" class="restoring-3d-overlay">
        <div class="restoring-3d-content">
          <v-progress-circular indeterminate size="48" color="white" />
          <div class="restoring-3d-text">3Dジオラマ復元中...</div>
        </div>
      </div>
    </Transition>

    <!-- ログイン要求ダイアログ（非ログインユーザーに有償機能の利用をブロック） -->
    <v-dialog
      :model-value="!!$store.state.tokenLoginRequired"
      @update:model-value="$store.state.tokenLoginRequired = false"
      max-width="440"
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center pt-5 px-6" style="gap: var(--oh3-spacing-sm)">
          <v-icon color="primary" size="28">mdi-lock-outline</v-icon>
          <span style="font-size: var(--oh3-font-size-lg)">ログインが必要です</span>
        </v-card-title>
        <v-card-text class="px-6 pb-2">
          <div style="font-size: var(--oh3-font-size-md); line-height: 1.7">
            この機能をご利用いただくには、<br>アカウント登録（無料）が必要です。
          </div>
          <v-divider class="my-3" />
          <div class="text-medium-emphasis" style="font-size: var(--oh3-font-size-sm); line-height: 1.6">
            <div class="d-flex align-center mb-1" style="gap: 6px">
              <v-icon size="16" color="success">mdi-check-circle</v-icon>
              体験プランで１００ポイントが付与されます
            </div>
            <div class="d-flex align-center mb-1" style="gap: 6px">
              <v-icon size="16" color="success">mdi-check-circle</v-icon>
              AI分析・データ出力など全機能が利用可能
            </div>
            <div class="d-flex align-center" style="gap: 6px">
              <v-icon size="16" color="success">mdi-check-circle</v-icon>
              各種データをクラウド保存
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 pt-2">
          <v-btn
            variant="text"
            @click="$store.state.tokenLoginRequired = false"
          >
            閉じる
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            variant="outlined"
            @click="$store.state.tokenLoginRequired = false; openLoginDialog()"
          >
            ログイン
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="$store.state.tokenLoginRequired = false; openSignUpDialog()"
          >
            無料で新規登録
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ポイント不足ダイアログ（consumeToken()が自動セット → 全画面で表示可能） -->
    <v-dialog
      :model-value="!!$store.state.tokenLimitExceeded"
      @update:model-value="$store.state.tokenLimitExceeded = null"
      max-width="420"
      persistent
    >
      <v-card v-if="$store.state.tokenLimitExceeded" rounded="lg">
        <v-card-title class="d-flex align-center" style="gap: var(--oh3-spacing-sm)">
          <v-icon color="warning" size="28">mdi-alert-circle</v-icon>
          ポイントが不足しています
        </v-card-title>
        <v-card-text>
          <div class="mb-3" style="font-size: var(--oh3-font-size-md)">
            ポイント残高が不足しているため、この機能を利用できませんでした。
          </div>
          <v-table density="compact" class="mb-3">
            <tbody>
              <tr>
                <td class="text-medium-emphasis">必要ポイント</td>
                <td class="text-right font-weight-bold">{{ $store.state.tokenLimitExceeded.tokenCost }} pt</td>
              </tr>
              <tr>
                <td class="text-medium-emphasis">残高</td>
                <td class="text-right font-weight-bold">{{ $store.state.tokenLimitExceeded.pointBalance ?? 0 }} pt</td>
              </tr>
            </tbody>
          </v-table>
          <div class="text-medium-emphasis" style="font-size: var(--oh3-font-size-sm)">
            プランをアップグレードするとポイントが追加されます。
            メニュー → 利用 タブからプランを確認できます。
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            @click="$store.state.tokenLimitExceeded = null"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-app>
</template>

<script>
import { imageCssToNatural, lngLatToMerc, fitSimilarity2PExactDown, fitAffineRobust,
  composeAffine, applyAffine, worldFileFromAffine, fetchElevation,
  epsgFromZahyokei, pickNearestVertex, xyFromProps, xyFromLngLat, xmlToGeojson,
  history,
} from '@/js/utils/app'
import { showSuccess, showInfo, showWarning, showLoginPrompt } from '@/js/utils/messageHelper'
import { getMdiIconPath, findStampById, preloadMdiIcons } from '@/js/stamps'
import { escapeHtml } from '@/js/utils/sanitize'

// Vue Core
import { nextTick, defineAsyncComponent } from 'vue'
import { mapState, mapMutations, mapActions, mapGetters} from 'vuex'
import { storeComputed, storeComputedReadonly } from '@/js/storeComputed'
import { onCreated } from '@/js/createdHook'
import { onBeforeUnmount } from '@/js/beforeUnmountHook'
import { onMounted } from '@/js/mountedHook'
import { getBasicTourSteps } from '@/js/tourSteps'
import {
  openAreaAnalysisModeDialog as _mdOpenAreaAnalysisModeDialog,
  cancelAreaAnalysisModeDialog as _mdCancelAreaAnalysisModeDialog,
  openDisasterSimulationDialog as _mdOpenDisasterSimulationDialog,
  openElevationProfileModeDialog as _mdOpenElevationProfileModeDialog,
  selectElevationProfileMode as _mdSelectElevationProfileMode,
  confirmFreehandDraw as _mdConfirmFreehandDraw,
  openPopulationProfileModeDialog as _mdOpenPopulationProfileModeDialog,
  cancelPopulationProfileModeDialog as _mdCancelPopulationProfileModeDialog,
  selectPopulationProfileMode as _mdSelectPopulationProfileMode,
  startPopulationProfileFreehandFlow as _mdStartPopulationProfileFreehandFlow,
  startPopulationProfileCircleFlow as _mdStartPopulationProfileCircleFlow,
  getMapSpeedOverrides as _mdGetMapSpeedOverrides,
  applyMapSpeedProfile as _mdApplyMapSpeedProfile,
} from '@/js/modeDialogMethods'
import { useGpsPhotoImport } from '@/composables/useGpsPhotoImport.js'

// MapLibre CSS（JSはinitApp.ts/mapSetup.tsが個別import）
import 'maplibre-gl/dist/maplibre-gl.css'

// External Libraries (alphabetical)
// 注: turf, html2canvas, proj4, shp, DxfParser, Encoding, gpx, kml, axios, debounce は
// 各ファイルが個別にimportしているため、ここでの同期importを廃止（起動高速化）

// Firebase & Auth
import firebase, { db } from '@/firebase'
import { user } from "@/authState"

// Store
import store from '@/store'

// Components - Dialogs (初期表示に必要なもの)
import DialogLayer from '@/components/Dialog-layer'
import DialogMenu from '@/components/Dialog-menu'
import MessageDialog from '@/components/Message-Dialog'

// Components - Dialogs (遅延読み込み)
const Dialog2 = defineAsyncComponent(() => import('@/components/Dialog2'))
const DialogDrawConfig = defineAsyncComponent(() => import("@/components/Dialog-draw-config"))
const DialogDrawShare = defineAsyncComponent(() => import("@/components/Dialog-draw-share"))
const GalleryWindow = defineAsyncComponent(() => import("@/components/GalleryWindow"))
const DialogPopularSets = defineAsyncComponent(() => import("@/components/Dialog-popular-sets"))
const DialogExt = defineAsyncComponent(() => import('@/components/Dialog-ext'))
const ExtMpiRrim = defineAsyncComponent(() => import('@/components/ext-mpi-rrim'))
const PopularLayers = defineAsyncComponent(() => import('@/components/PopularLayers.vue'))
const DialogMyroom = defineAsyncComponent(() => import('@/components/Dialog-myroom'))
const DialogShare = defineAsyncComponent(() => import("@/components/Dialog-share"))
const WelcomeWizard = defineAsyncComponent(() => import('@/components/WelcomeWizard'))
const OnboardingTooltip = defineAsyncComponent(() => import('@/components/OnboardingTooltip.vue'))
const OnboardingPurposeSelector = defineAsyncComponent(() => import('@/components/OnboardingPurposeSelector.vue'))
const Diorama3D = defineAsyncComponent(() => import('@/components/Diorama3D.vue'))
const CrossSection3D = defineAsyncComponent(() => import('@/components/CrossSection3D.vue'))
const SakuraEffect = defineAsyncComponent(() => import('@/components/SakuraEffect.vue'))
import { getStep, getTotalSteps, hasNextStep } from '@/utils/onboardingSteps'

// Components - V-Dialog (基本ダイアログは同期、その他遅延)
import VDialogCommon from "@/components/V-dialog/V-dialog-common"
import VDialogConfirm from "@/components/V-dialog/V-dialog-confirm"
const VDialogIframe = defineAsyncComponent(() => import("@/components/V-dialog/V-dialog-Iframe"))
const MediaPreviewDialog = defineAsyncComponent(() => import("@/components/MediaPreviewDialog.vue"))

// Components - Drawers (遅延読み込み — ドロワーは開くまで不要)
const AboutDrawer = defineAsyncComponent(() => import('@/components/drawer/AboutDrawer.vue'))
const ChibanzuDrawer = defineAsyncComponent(() => import('@/components/chibanzuDrawer.vue'))
const DrawDrawer = defineAsyncComponent(() => import('@/components/drawer/DrawDrawer.vue'))
const DrawListDrawer = defineAsyncComponent(() => import('@/components/drawer/DrawLisiDrawer'))
const PointInfoDrawer = defineAsyncComponent(() => import('@/components/PointInfoDrawer.vue'))
const RightDrawer = defineAsyncComponent(() => import('@/components/rightDrawer.vue'))
const RightDrawerTokizyo = defineAsyncComponent(() => import('@/components/RightDrawerTokizyo.vue'))
const GyoseiHistoryDrawer = defineAsyncComponent(() => import('@/components/GyoseiHistoryDrawer.vue'))

// Components - Floating Windows (FWシステム本体は同期、個別ウィンドウは遅延)
import FloatingWindow from '@/components/floatingwindow/FloatingWindow'
import AddressSearch from '@/components/AddressSearch.vue'
const DrawFloatingWindow = defineAsyncComponent(() => import('@/components/DrawFloatingWindow.vue'))
const PhotoSketchFloatingWindow = defineAsyncComponent(() => import('@/components/PhotoSketchFloatingWindow.vue'))
const TextDetailFloatingWindow = defineAsyncComponent(() => import('@/components/TextDetailFloatingWindow.vue'))
const DrawVisibilityWindow = defineAsyncComponent(() => import('@/components/DrawVisibilityWindow.vue'))
const SpatialQueryWizard = defineAsyncComponent(() => import('@/components/SpatialQueryWizard.vue'))
const FreehandCanvas = defineAsyncComponent(() => import('@/components/FreehandCanvas.vue'))
const LassoCanvas = defineAsyncComponent(() => import('@/components/LassoCanvas.vue'))
const FieldNoteMemo = defineAsyncComponent(() => import('@/components/FieldNoteMemo.vue'))
const JobPicker = defineAsyncComponent(() => import("@/components/floatingwindow/JobPicker"))
const MapillaryFilter = defineAsyncComponent(() => import('@/components/floatingwindow/MapillaryFilter.vue'))
const KyusekiMapPreview = defineAsyncComponent(() => import('@/components/floatingwindow/KyusekiMapPreview.vue'))
const Mw5PreviewManager = defineAsyncComponent(() => import('@/components/floatingwindow/Mw5PreviewManager.vue'))
const HillshadeControl = defineAsyncComponent(() => import("@/components/HillshadeControl"))
const HillshadePanel = defineAsyncComponent(() => import('@/components/HillshadePanel'))
const PaintEditor = defineAsyncComponent(() => import('@/components/floatingwindow/PaintEditor'))
const MyMapViewer = defineAsyncComponent(() => import('@/components/floatingwindow/MyMapViewer'))
const AiEnhanceDialog = defineAsyncComponent(() => import('@/components/AiEnhanceDialog'))
const Traverse = defineAsyncComponent(() => import("@/components/floatingwindow/Traverse"))
const ZoomInfoWindow = defineAsyncComponent(() => import('@/components/floatingwindow/ZoomInfoWindow.vue'))
const TimeSlider = defineAsyncComponent(() => import('@/components/floatingwindow/TimeSlider.vue'))
const OfflineAreaManager = defineAsyncComponent(() => import('@/components/floatingwindow/OfflineAreaManager.vue'))
const ChibanInfoWindow = defineAsyncComponent(() => import('@/components/floatingwindow/ChibanInfoWindow.vue'))
const ChibanzuInfoWindow = defineAsyncComponent(() => import('@/components/floatingwindow/ChibanzuInfoWindow.vue'))
const SimaQuickWindow = defineAsyncComponent(() => import('@/components/floatingwindow/SimaQuickWindow.vue'))
const StreetViewWindow = defineAsyncComponent(() => import('@/components/floatingwindow/StreetViewWindow.vue'))
const IsochroneWindow = defineAsyncComponent(() => import('@/components/floatingwindow/IsochroneWindow.vue'))
const RoadIsochroneWindow = defineAsyncComponent(() => import('@/components/floatingwindow/RoadIsochroneWindow.vue'))
const WatershedWindow = defineAsyncComponent(() => import('@/components/floatingwindow/WatershedWindow.vue'))
const PriceHistoryChart = defineAsyncComponent(() => import('@/components/floatingwindow/PriceHistoryChart.vue'))

// Components - Other (初期表示に必要)
import RightButtons from "@/components/RightButtons";
import LayerCredits from "@/components/LayerCredits";
import LoginManager from "@/components/LoginManager"
import MiniTooltip from '@/components/MiniTooltip'
import Splash from '@/components/Splash'
import DialogContainer from '@/components/DialogContainer.vue'
// Components - Other (遅延読み込み)
const MagnifyingGlass = defineAsyncComponent(() => import("@/components/MagnifyingGlass.vue"))
const KyusekiImportDialog = defineAsyncComponent(() => import("@/components/KyusekiImportDialog.vue"))
const SimpleImportDialog = defineAsyncComponent(() => import("@/components/SimpleImportDialog.vue"))
const TermsAgreementDialog = defineAsyncComponent(() => import('@/components/TermsAgreementDialog.vue'))
const WhatsNewDialog = defineAsyncComponent(() => import('@/components/WhatsNewDialog.vue'))
const WarpWizard = defineAsyncComponent(() => import('@/components/WarpWizard.vue'))
const FileDropFlowDialog = defineAsyncComponent(() => import('@/components/FileDropFlowDialog.vue'))
const HomukyokuXmlImportDialog = defineAsyncComponent(() => import('@/components/HomukyokuXmlImportDialog.vue'))
const RealEstateReportDialog = defineAsyncComponent(() => import('@/components/RealEstateReportDialog.vue'))
import {
  checkTeacherStatus as _checkTeacherStatus,
  checkOverseas as _checkOverseas,
  startLessonListener as _startLessonListener,
  startTeacherLessonSync as _startTeacherLessonSync,
  startTeacherMapSync as _startTeacherMapSync,
  stopTeacherMapSync as _stopTeacherMapSync,
  disableStudentMapInteractions as _disableStudentMapInteractions,
  enableStudentMapInteractions as _enableStudentMapInteractions,
  isSameUrl as _isSameUrl,
  applyLessonViewState as _applyLessonViewState,
  applyLessonFullState as _applyLessonFullState,
  applyDrawToMap as _applyDrawToMap,
  setDrawLayersOpacity as _setDrawLayersOpacity,
  checkSyncTimeout as _checkSyncTimeout,
  restoreUserSettings as _restoreUserSettings,
  setupAuthStateListener,
} from '@/js/authSetup'
import {
  handlePrint as _handlePrint,
  getScreenConfiguration as _getScreenConfiguration,
  adjustPositionForScreenChange as _adjustPositionForScreenChange,
  updateScaleDisplay as _updateScaleDisplay,
  initScaleDraggable as _initScaleDraggable,
  setup4ScreenSync as _setup4ScreenSync,
  setScreenMode as _setScreenMode,
  apply4ScreenPreset as _apply4ScreenPreset,
  toggle4ScreenMode as _toggle4ScreenMode,
  setupPointerSync as _setupPointerSync,
  toggleSwipeMode as _toggleSwipeMode,
  updatePrintTitleHeight as _updatePrintTitleHeight,
} from '@/js/screenLayout'
import {
  startWatchPosition as _startWatchPosition,
  stopWatchPosition as _stopWatchPosition,
  saveGeoMetrics as _saveGeoMetrics,
  getGeoQualityLabel as _getGeoQualityLabel,
  drawGpsLine as _drawGpsLine,
  attachGpsLineClick as _attachGpsLineClick,
  detachGpsLineClick as _detachGpsLineClick,
  clearGpsLine as _clearGpsLine,
  watchPositionHandler as _watchPositionHandler,
  toggleWatchPosition as _toggleWatchPosition,
  requestClearLog as _requestClearLog,
  doClearLog as _doClearLog,
  toggleTrackLog as _toggleTrackLog,
  startTrackLog as _startTrackLog,
  stopTrackLog as _stopTrackLog,
  clearTrackLog as _clearTrackLog,
  maybeLogPoint as _maybeLogPoint,
  exportTrackCsv as _exportTrackCsv,
  exportTrackSima as _exportTrackSima,
  $_jstLocal,
  $_jstStamp,
  $_toShiftJisBytes,
  $_downloadBlob,
  $_downloadText,
  updateLocationAndCoordinates as _updateLocationAndCoordinates,
  goToCurrentLocation as _goToCurrentLocation,
  createCurrentLocationPopupContent as _createCurrentLocationPopupContent,
  setupCurrentLocationPopupButtons as _setupCurrentLocationPopupButtons,
  copyCurrentLocationInfo as _copyCurrentLocationInfo,
} from '@/js/gpsTracking'
import {
  startMapSync as _startMapSync,
  stopMapSync as _stopMapSync,
  toggleMapSyncMode as _toggleMapSyncMode,
  syncAllMapsToMap01 as _syncAllMapsToMap01,
  saveAllMapStates as _saveAllMapStates,
  enableSwipePan as _enableSwipePan,
  disableSwipePan as _disableSwipePan,
  enableSwipeNudge as _enableSwipeNudge,
  disableSwipeNudge as _disableSwipeNudge,
  enableSwipeManualPan as _enableSwipeManualPan,
  disableSwipeManualPan as _disableSwipeManualPan,
  forceEnableMapInteractions as _forceEnableMapInteractions,
  setMapInteractions as _setMapInteractions,
  enableSwipeFollow as _enableSwipeFollow,
  disableSwipeFollow as _disableSwipeFollow,
  addOsmToMap02 as _addOsmToMap02,
} from '@/js/mapSync'
import {
  handleAiShareUrl as _handleAiShareUrl,
  updateAiShareUrl as _updateAiShareUrl,
  showShareFeaturesOnMap as _showShareFeaturesOnMap,
  handleShareFeatureClick as _handleShareFeatureClick,
  resolveShareFeatureAction as _resolveShareFeatureAction,
  fitToShareFeatures as _fitToShareFeatures,
  addShareFeature as _addShareFeature,
  removeShareFeature as _removeShareFeature,
} from '@/js/shareFeatures'
import {
  drawToolSet as _drawToolSet,
  drawListOpen as _drawListOpen,
  drawOpen as _drawOpen,
  drawGeojsonAddPicture as _drawGeojsonAddPicture,
  uploadDraw as _uploadDraw,
  uploadDrawGeojson as _uploadDrawGeojson,
  endDraw as _endDraw,
  toggleLDraw as _toggleLDraw,
  drawFix as _drawFix,
  toggleDrawFixMethod as _toggleDrawFixMethod,
  simaAddDraw as _simaAddDraw,
  drawToggle as _drawToggle,
  drawConfig as _drawConfig,
  drawShare as _drawShare,
} from '@/js/drawingMethods'
import {
  highlightToukiPolygon as _highlightToukiPolygon,
  clearToukiHighlight as _clearToukiHighlight,
  handleToukiBatchExtract as _handleToukiBatchExtract,
  handleToukiAll as _handleToukiAll,
} from '@/js/toukiProcessing'
import {
  migrateLocalStorage as _migrateLocalStorage,
  handleFlowSelected as _handleFlowSelected,
  handleToukiJump as _handleToukiJump,
} from '@/js/fileImport'
import {
  updatePermalink as _updatePermalink,
  restoreGyoseiHistoryFromUrl as _restoreGyoseiHistoryFromUrl,
  restoreFromParamString as _restoreFromParamString,
  restoreUserSettings as _restoreUserSettingsPermalink,
  restoreSwipeState as _restoreSwipeState,
  cancelShortUrlTimer as _cancelShortUrlTimer,
  scheduleShortUrl as _scheduleShortUrl,
  createShortUrl as _createShortUrl,
  parseUrlParams as _parseUrlParams,
} from '@/js/permalinkState'
import {
  onKeydown as _onKeydown,
} from '@/js/keyboardShortcuts'
import {
  cachesCrear as _cachesCrear,
  downloadTiles as _downloadTiles,
  bboxPolygon as _bboxPolygon,
} from '@/js/offlineCache'
import {
  startLassoBulkMove as _startLassoBulkMove,
  getCheckColor as _getCheckColor,
  applyLassoBulkColor as _applyLassoBulkColor,
  applyLassoCustomColor as _applyLassoCustomColor,
  confirmLassoBulkDelete as _confirmLassoBulkDelete,
  executeLassoBulkDelete as _executeLassoBulkDelete,
  confirmLassoDeleteOthers as _confirmLassoDeleteOthers,
  executeLassoDeleteOthers as _executeLassoDeleteOthers,
  colorNameToRgba as _colorNameToRgba,
} from '@/js/lassoOperations'
import {
  uploadChibanzu as _uploadChibanzu,
  uploadMyroomKml as _uploadMyroomKml,
  uploadDrawKml as _uploadDrawKml,
  uploadDrawCsv as _uploadDrawCsv,
  uploadMyLayer as _uploadMyLayer,
} from '@/js/uploadHandlers'
import {
  startTiling as _startTiling,
  openTileUploadDialog as _openTileUploadDialog,
  updateMapMarkers as _updateMapMarkers,
  onMapClick as _onMapClick,
  test0 as _test0,
  executeDuplicate as _executeDuplicate,
  calculateCentroid as _calculateCentroid,
  createStampMarkerForDuplicate as _createStampMarkerForDuplicate,
  createElevationFromFeature as _createElevationFromFeature,
  elevationCreate as _elevationCreate,
  pointCsvCreate as _pointCsvCreate,
  pointSimaCreate as _pointSimaCreate,
} from '@/js/tileAndDuplicate'
import {
  dialogForDlOpen as _dialogForDlOpen,
  dialogForSaveDXFOpen as _dialogForSaveDXFOpen,
  saveGpx as _saveGpx,
  saveSima as _saveSima,
  saveGeojson as _saveGeojson,
  saveKml as _saveKml,
  saveDxf as _saveDxf,
  execVisibleDxfExport as _execVisibleDxfExport,
  saveHistory as _saveHistory,
  undo as _undo,
  redo as _redo,
  finishLine as _finishLine,
} from '@/js/drawSaveHistory'
import {
  pdfDl as _pdfDl,
  executePdfDl as _executePdfDl,
  openPdfPrintOptionsDialog as _openPdfPrintOptionsDialog,
  confirmPdfDl as _confirmPdfDl,
  pngDl0 as _pngDl0,
  print as _print,
  s_printMap01 as _s_printMap01,
  s_printMap02 as _s_printMap02,
  s_printMap03 as _s_printMap03,
  startPrintSync as _startPrintSync,
  stopPrintSync as _stopPrintSync,
  applyPrintMap03Layout as _applyPrintMap03Layout,
  setPrintZoomOffset as _setPrintZoomOffset,
} from '@/js/printExport'
import {
  checkAndStartOnboarding as _checkAndStartOnboarding,
  resetOnboarding as _resetOnboarding,
  reset as _reset,
  handlePurposeSelect as _handlePurposeSelect,
  handleOnboardingNext as _handleOnboardingNext,
  handleOnboardingSkip as _handleOnboardingSkip,
  handleOnboardingComplete as _handleOnboardingComplete,
} from '@/js/resetAndOnboarding'
import {
  persistAndExitEditMode as _persistAndExitEditMode,
  startAnglePlus as _startAnglePlus,
  stopAnglePlus as _stopAnglePlus,
  startAngleMinus as _startAngleMinus,
  stopAngleMinus as _stopAngleMinus,
  onRotateInput as _onRotateInput,
  resetLassoTransform as _resetLassoTransform,
  closeLassoControl as _closeLassoControl,
  exitLassoMode as _exitLassoMode,
  showLassoCloseConfirmDialog as _showLassoCloseConfirmDialog,
  confirmLassoClose as _confirmLassoClose,
  cancelLassoMode as _cancelLassoMode,
  restoreLassoChanges as _restoreLassoChanges,
} from '@/js/lassoTransformControl'
import {
  enableAreaAnalysisEvacuationLayerOnce as _enableAreaAnalysisEvacuationLayerOnce,
  addSelectedLayerById as _addSelectedLayerById,
  removeSelectedLayerById as _removeSelectedLayerById,
  findLayerById as _findLayerById,
  selectAreaAnalysisMode as _selectAreaAnalysisMode,
  checkAreaLimit as _checkAreaLimit,
  startAreaAnalysisFreehandFlow as _startAreaAnalysisFreehandFlow,
  startAreaAnalysisCircleFlow as _startAreaAnalysisCircleFlow,
  startAreaAnalysisPolygonFlow as _startAreaAnalysisPolygonFlow,
} from '@/js/areaAnalysisFlows'
import {
  closeDrawInfo as _closeDrawInfo,
  autoOpenDrawModeIfNeeded as _autoOpenDrawModeIfNeeded,
  applyDrawLayerVisibility as _applyDrawLayerVisibility,
  copyDrawInfo as _copyDrawInfo,
  extractDrawInfoText as _extractDrawInfoText,
  copyWithExecCommand as _copyWithExecCommand,
} from '@/js/drawInfoDisplay'
import {
  savePicture as _savePicture,
  deletePicture as _deletePicture,
  onFileSelected as _onFileSelected,
} from '@/js/mediaUploadHandlers'
import {
  _applyAffine as __applyAffine,
  generateWorldFile as _generateWorldFile,
  onWarpConfirm as _onWarpConfirm,
  _validCorners as __validCorners,
  _worldFileFromAffine as __worldFileFromAffine,
  onWarpWindowClose as _onWarpWindowClose,
  clearBlueDotsOnMap as _clearBlueDotsOnMap,
} from '@/js/georeferencing'
import {
  targets as _targets,
  hillshadeStop as _hillshadeStop,
  pressRotateRight as _pressRotateRight,
  pressRotateLeft as _pressRotateLeft,
  pressPitchUp as _pressPitchUp,
  pressPitchDown as _pressPitchDown,
  releaseOrientationButtons as _releaseOrientationButtons,
  terrainReset as _terrainReset,
} from '@/js/mapOrientationControl'
import {
  startFloatingButtonsDrag as _startFloatingButtonsDrag,
  onFloatingButtonsDrag as _onFloatingButtonsDrag,
  endFloatingButtonsDrag as _endFloatingButtonsDrag,
  resetFloatingButtonsPos as _resetFloatingButtonsPos,
} from '@/js/floatingButtonsControl'
import {
  openDialog as _openDialog,
  captureAndPreview as _captureAndPreview,
} from '@/js/screenCapture'
import {
  maybeFocusCenterIncludePoint as _maybeFocusCenterIncludePoint,
  focusCenterIncludePoint as _focusCenterIncludePoint,
  installTouchSuppressor as _installTouchSuppressor,
  openKyusekiDialog as _mfOpenKyusekiDialog,
  onKyusekiDialogVisibilityChange as _mfOnKyusekiDialogVisibilityChange,
  closeKyusekiWindow as _mfCloseKyusekiWindow,
  openHandwriteDialog as _mfOpenHandwriteDialog,
  onImported as _mfOnImported,
  onHandwriteImported as _mfOnHandwriteImported,
} from '@/js/mapFocus'
import {
  toggleFreehandCanvas as _fcToggleFreehandCanvas,
  onFreehandConfirm as _fcOnFreehandConfirm,
  onFreehandClose as _fcOnFreehandClose,
  restoreStampsFromGeojson as _fcRestoreStampsFromGeojson,
  closeFreehandCanvas as _fcCloseFreehandCanvas,
} from '@/js/freehandCanvasMethods'
import {
  startScaleDown as _dmStartScaleDown,
  stopScaleDown as _dmStopScaleDown,
  onScaleInput as _dmOnScaleInput,
  onEditConfirm as _dmOnEditConfirm,
  onEditRevert as _dmOnEditRevert,
  exitDuplicateMode as _dmExitDuplicateMode,
  revertEditChanges as _dmRevertEditChanges,
  deleteAllforDraw as _dmDeleteAllforDraw,
  directionChange as _dmDirectionChange,
  toggleEditEnabled as _dmToggleEditEnabled,
  toggleDrawLasso as _dmToggleDrawLasso,
  toggleLDrawFree as _dmToggleLDrawFree,
  toggleLDrawPolygon as _dmToggleLDrawPolygon,
  toggleLDrawLine as _dmToggleLDrawLine,
  toggleDrawCircle as _dmToggleDrawCircle,
  toggleDrawPoint as _dmToggleDrawPoint,
  toggleDrawText as _dmToggleDrawText,
} from '@/js/drawModeToggles'
import {
  btnPosition as _tcBtnPosition,
  terrainBtnClos as _tcTerrainBtnClos,
  terrain3dOff as _tcTerrain3dOff,
  terrainBtnExpand as _tcTerrainBtnExpand,
  terrainBtnExpandWithTerrain as _tcTerrainBtnExpandWithTerrain,
  terrainLevelInput as _tcTerrainLevelInput,
  handle3dOff as _tcHandle3dOff,
  handle3dNoTerrain as _tcHandle3dNoTerrain,
  handle3dWithTerrain as _tcHandle3dWithTerrain,
  handle3dOffFab as _tcHandle3dOffFab,
  handle3dNoTerrainFab as _tcHandle3dNoTerrainFab,
  handle3dWithTerrainFab as _tcHandle3dWithTerrainFab,
  openDiorama3D as _tcOpenDiorama3D,
  onMyroomClick as _tcOnMyroomClick,
  btnClickMyroom as _tcBtnClickMyroom,
} from '@/js/terrainControls'
import {
  save as _foSave,
  close as _foClose,
  setDrawerVisible as _foSetDrawerVisible,
  setRightVisible as _foSetRightVisible,
  savePointDescription as _foSavePointDescription,
  openFeatureDrawer as _foOpenFeatureDrawer,
  handleDescriptionSave as _foHandleDescriptionSave,
  simaClose as _foSimaClose,
  myRoom as _foMyRoom,
  updateSnackbar as _foUpdateSnackbar,
  onResize as _foOnResize,
  simaDelete as _foSimaDelete,
  fileClose as _foFileClose,
  dxfDl as _foDxfDl,
  dxfPartDl as _foDxfPartDl,
  dxfDelete as _foDxfDelete,
  pngDownloadFn as _foPngDownloadFn,
  dxfLoad as _foDxfLoad,
  shpLoad as _foShpLoad,
  imagePngLoad as _foImagePngLoad,
  pdfLoad as _foPdfLoad,
  pngLoad0 as _foPngLoad0,
  jpgLoad0 as _foJpgLoad0,
  geoTiffLoad0 as _foGeoTiffLoad0,
  geoTifLoad1file as _foGeoTifLoad1file,
  simaOpacityInput as _foSimaOpacityInput,
  loadSima as _foLoadSima,
  simaPartDl as _foSimaPartDl,
  simaDl as _foSimaDl,
} from '@/js/fileOperations'
import {
  btnClickMenu as _msBtnClickMenu,
  btnClickLayer as _msBtnClickLayer,
  btnClickSplit as _msBtnClickSplit,
  shouldShowMap as _msShouldShowMap,
  onMapDragStart as _msOnMapDragStart,
  onMapDragEnter as _msOnMapDragEnter,
  onMapDragLeave as _msOnMapDragLeave,
  onMapDrop as _msOnMapDrop,
  onMapDragEnd as _msOnMapDragEnd,
  swapMapPositions as _msSwapMapPositions,
  initializeMap03And04 as _msInitializeMap03And04,
  ensureMap03And04Layers as _msEnsureMap03And04Layers,
  applyExistingLayersToMap as _msApplyExistingLayersToMap,
  ensureMapLayers as _msEnsureMapLayers,
  setupNative4ScreenSync as _msSetupNative4ScreenSync,
  setup4ScreenPointerSync as _msSetup4ScreenPointerSync,
  ensureMap02Layers as _msEnsureMap02Layers,
  syncMap02ToMap01 as _msSyncMap02ToMap01,
  getTopLayerLabel as _msGetTopLayerLabel,
  getMapStyle as _msGetMapStyle,
} from '@/js/mapScreenManagement'
import {
  help as _mmHelp,
  share as _mmShare,
  zoomIn as _mmZoomIn,
  zoomOut as _mmZoomOut,
  downloadCsv2 as _mmDownloadCsv2,
  exportCsv2Sima as _mmExportCsv2Sima,
  setLineMode as _mmSetLineMode,
  openJobPicker as _mmOpenJobPicker,
  zahyoGet as _mmZahyoGet,
  startTorokuHere as _mmStartTorokuHere,
  onJobEndClick as _mmOnJobEndClick,
  onAiEnhance as _mmOnAiEnhance,
  exitSwipeAndSplit as _mmExitSwipeAndSplit,
  startSwipeDrag as _mmStartSwipeDrag,
} from '@/js/miscMethods'
import {
  startPopulationProfilePolygonFlow as _ifStartPopulationProfilePolygonFlow,
  openFsInvestigationModeDialog as _ifOpenFsInvestigationModeDialog,
  cancelFsInvestigationModeDialog as _ifCancelFsInvestigationModeDialog,
  selectFsInvestigationMode as _ifSelectFsInvestigationMode,
  startFsInvestigationFreehandFlow as _ifStartFsInvestigationFreehandFlow,
  startFsInvestigationCircleFlow as _ifStartFsInvestigationCircleFlow,
  startFsInvestigationPolygonFlow as _ifStartFsInvestigationPolygonFlow,
  openSuperCrossSectionWizard as _ifOpenSuperCrossSectionWizard,
  openSpatialQueryModeDialog as _ifOpenSpatialQueryModeDialog,
  beginAreaAnalysisPointSelection as _ifBeginAreaAnalysisPointSelection,
  openAreaAnalysisPoint as _ifOpenAreaAnalysisPoint,
} from '@/js/investigationFlows'
import {
  showTermsDialog as _udShowTermsDialog,
  getMapTitle as _udGetMapTitle,
  formatPrintDate as _udFormatPrintDate,
  getSourceData as _udGetSourceData,
  openLoginDialog as _udOpenLoginDialog,
  openSignUpDialog as _udOpenSignUpDialog,
  openHillshadeDialog as _udOpenHillshadeDialog,
  closeSignUpPromo as _udCloseSignUpPromo,
  startSignUp as _udStartSignUp,
  startLogin as _udStartLogin,
  startTour as _udStartTour,
  handleUsageTourNext as _udHandleUsageTourNext,
  handleUsageTourSkip as _udHandleUsageTourSkip,
  handleUsageTourComplete as _udHandleUsageTourComplete,
  onConfirmOk as _udOnConfirmOk,
  onConfirmCancel as _udOnConfirmCancel,
  onConfirmMiddle as _udOnConfirmMiddle,
  fullscreenForIframe as _udFullscreenForIframe,
  waitForMap as _udWaitForMap,
} from '@/js/uiDialogsAndTour'
import {
  onPaintUpdate as _umOnPaintUpdate,
  cancelPointNameEdit as _umCancelPointNameEdit,
  updatePointNameOnServer as _umUpdatePointNameOnServer,
  _stopIntervalAndFinalize as _um_stopIntervalAndFinalize,
  _tickKansoku as _um_tickKansoku,
  compassClick as _umCompassClick,
  mapillaryFilterOpen as _umMapillaryFilterOpen,
  openZoomInfo as _umOpenZoomInfo,
  mapillaryClose as _umMapillaryClose,
  addressSearchClose as _umAddressSearchClose,
  clearAllAddressMarkers as _umClearAllAddressMarkers,
  zoomInfoClose as _umZoomInfoClose,
  openPriceHistoryWindow as _umOpenPriceHistoryWindow,
  closePriceHistoryWindow as _umClosePriceHistoryWindow,
  onPriceHistoryPositionChange as _umOnPriceHistoryPositionChange,
  downloadPriceHistoryCsv as _umDownloadPriceHistoryCsv,
  onTimeSliderClose as _umOnTimeSliderClose,
  openTimeSliderIfKonjyakuExists as _umOpenTimeSliderIfKonjyakuExists,
  popupDialogClose as _umPopupDialogClose,
  isDisabledChange as _umIsDisabledChange,
  triggerCamera as _umTriggerCamera,
  onCameraPicked as _umOnCameraPicked,
  test as _umTest,
  openPaintEditorWindow as _umOpenPaintEditorWindow,
  onWidthChangedForMapillary as _umOnWidthChangedForMapillary,
  onWidthChanged as _umOnWidthChanged,
  openWindow as _umOpenWindow,
  setMaxZIndex as _umSetMaxZIndex,
  startScaleUp as _umStartScaleUp,
  stopScaleUp as _umStopScaleUp,
} from '@/js/utilityMethods'
import { initApp } from '@/js/initApp'
// Components - FABs (初期表示に必要 — 地図上に常時表示)
import SnapshotFab from '@/components/SnapshotFab.vue'
import ExtDialogFab from '@/components/ExtDialogFab.vue'
import AiMapChatFab from '@/components/AiMapChatFab.vue'
import AiAgentFab from '@/components/AiAgentFab.vue'
import NorthArrow from '@/components/NorthArrow.vue'
// Components - Heavy dialogs (遅延読み込み — 開くまで不要)
// import AiPointInfoDialog from '@/components/AiPointInfoDialog.vue' // 一時的にコメントアウト
const PropertyReportDialog = defineAsyncComponent(() => import('@/components/PropertyReportDialog.vue'))
const MultiPointRankingDialog = defineAsyncComponent(() => import('@/components/MultiPointRankingDialog.vue'))
const ChildCareReportDialog = defineAsyncComponent(() => import('@/components/ChildCareReportDialog.vue'))
const SuperPopulationDialog = defineAsyncComponent(() => import('@/components/SuperPopulationDialog.vue'))
const TeacherDashboard = defineAsyncComponent(() => import('@/components/TeacherDashboard.vue'))
const OrganizationAdmin = defineAsyncComponent(() => import('@/components/OrganizationAdmin.vue'))
const AreaAnalysisDialog = defineAsyncComponent(() => import('@/components/AreaAnalysisDialog.vue'))
const CsvJoinAnalysisDialog = defineAsyncComponent(() => import('@/components/CsvJoinAnalysisDialog.vue'))
const FsInvestigationDialog = defineAsyncComponent(() => import('@/components/FsInvestigationDialog.vue'))
const MaisokuDialog = defineAsyncComponent(() => import('@/components/MaisokuDialog.vue'))
const JusetsuDraftDialog = defineAsyncComponent(() => import('@/components/JusetsuDraftDialog.vue'))
const SuperCrossSectionDialog = defineAsyncComponent(() => import('@/components/SuperCrossSectionDialog.vue'))
const MunicipalityPyramidDialog = defineAsyncComponent(() => import('@/components/MunicipalityPyramidDialog.vue'))
const AiOsmSearchDialog = defineAsyncComponent(() => import('@/components/AiOsmSearchDialog.vue'))
const PhotoMemoUpload = defineAsyncComponent(() => import('@/components/PhotoMemoUpload.vue'))
const TouhonOverlayDialog = defineAsyncComponent(() => import('@/components/TouhonOverlayDialog.vue'))
const TouhonDiffDialog = defineAsyncComponent(() => import('@/components/TouhonDiffDialog.vue'))
const MapCompareDialog = defineAsyncComponent(() => import('@/components/MapCompareDialog.vue'))
const SpreadsheetSyncDialog = defineAsyncComponent(() => import('@/components/SpreadsheetSyncDialog.vue'))
const GroupManageDialog = defineAsyncComponent(() => import('@/components/GroupManageDialog.vue'))
const HelpDialog = defineAsyncComponent(() => import('@/components/help/HelpDialog.vue'))
const AiHelpChat = defineAsyncComponent(() => import('@/components/help/AiHelpChat.vue'))
const ElevationDialog = defineAsyncComponent(() => import('@/components/ElevationDialog.vue'))
const AiMapQuery = defineAsyncComponent(() => import('@/components/AiMapQuery.vue'))
const AiAgentDialog = defineAsyncComponent(() => import('@/components/AiAgentDialog.vue'))
const ChoroplethLegend = defineAsyncComponent(() => import('@/components/ChoroplethLegend.vue'))
const ChartFloatingWindow = defineAsyncComponent(() => import('@/components/ChartFloatingWindow.vue'))

// JS Modules - Main
import * as Layers from '@/js/layers'
import {
  clickCircleSource,
  extLayer,
  extSource,
  osmBrightLayers,
  osmBrightSources,
  vertexSource,
  zenkokuChibanzuAddLayer
} from "@/js/layers"
import muni from '@/js/muni'
import {closeAllPopups, mouseMoveForPopup, popup} from "@/js/popup"
import { clearSelectedFeature, clearFeatureInfo, highlightSelectedFeature, showFeatureInfo, openDrawFloatingWindow, setTextClickHandled, getNextDefaultLabel, hideTransformBox, showTransformBox, refreshTransformBox } from "@/js/draw"
import { getUsageTracker } from '@/js/utils/usageTracker'
import { consumeToken, getMonthlyUsage, getTokenBalance } from '@/js/tokenService'
import { canUseSimulation } from '@/js/allowedUsers'
import pyramid, {
  createSquarePolygonAtCenter,
  deleteAll,
  generateSegmentLabelGeoJSON,
  generateStartEndPointsFromGeoJSON,
  geojsonCreate,
  geojsonUpdate,
  getAllVertexPoints,
  lavelUpdate,
  setAllMidpoints,
} from '@/js/pyramid'

// JS Modules - Download (large import)
import {
  addDraw,
  addressFromMapCenter,
  addSvgAsImage,
  animateRelocate,
  bakeRotationToBlob,
  capture,
  changePrintMap03,
  compressImageToUnder10MB,
  convertFromEPSG4326,
  csvGenerateForUserPng,
  ddSimaUpload,
  dedupeCoords,
  delay0,
  detectLatLonColumns,
  downloadGeoJSONAsCSV,
  downloadSimaText,
  downloadTextFile,
  DXFDownload,
  dxfToGeoJSON,
  enableDragHandles,
  extractFirstFeaturePropertiesAndCheckCRS,
  extractSimaById,
  featureCollectionAdd,
  featuresDelete,
  fitClickCircleAll,
  fncPngDl,
  fncPdfDl,
  fncPdfDlTiled,
  geocode,
  geojsonAddLayer,
  geojsonDownload,
  geoJSONToSIMA,
  geoTiffLoad,
  geoTiffLoad2,
  getBBoxFromPolygon,
  getCRS,
  getNextZIndex,
  getNowFileNameTimestamp,
  gpxDownload,
  handleFileUpload,
  highlightSpecificFeatures,
  highlightSpecificFeatures2025,
  highlightSpecificFeaturesCity,
  setupHighlightLayer,
  updateHighlightLayer,
  clearHighlightLayer,
  hitExcludedLayers,
  installDrawerStabilizer,
  installSafePicking,
  isImageFile,
  isPdfFile,
  isVideoFile,
  japanCoord,
  jpgLoad,
  kmlDownload,
  kmlLoadForUser,
  kmzLoadForUser,
  LngLatToAddress,
  mapillaryCreate,
  mapillaryFilterRiset,
  mapillaryViewer,
  mapillaryWindowOpenDebounced,
  markaersRemove,
  markerAddAndRemove,
  parseCSV,
  pmtilesGenerate,
  pmtilesGenerateForUser2,
  pngDownload,
  pngLoad,
  printDirectionChange,
  removeThumbnailMarkerByKey,
  restorePhotoMemoPathArrows,
  sanitizeLongText,
  scaleAndRotateLassoSelected,
  setFllter360,
  setVertex,
  simaLoadForUser,
  dxfLoadForUser,
  splitLineStringIntoPoints,
  startPolling,
  saveDrowFeatures,
  pushMyDrawUndoAction,
  stopDrawerAnimations,
  tileGenerateForUser,
  tileGenerateForUserPdf,
  transformGeoJSONToEPSG4326,
  updateDragHandles,
  updateSingleMarker,
  userKmzSet,
  userKmlSet,
  userSimaSet,
  userDxfSet,
  vertexAndMidpoint,
  zahyokei,
  zipDownloadSimaText,
  zipDownloadDxfText,
} from '@/js/downLoad'

// JS Modules - Draw
import drawMethods, {
  drawCancel,
  drawConfirm,
  drawRedo,
  drawUndo,
  pushSnapshotDebounced,
  seedInitialSnapshot,
  removeLastVertex,
  toggleDrawMode,
  toggleDrawFix,
  toggleEditEnabled,
  toggleDrawLasso,
  toggleDrawFree,
  toggleDrawPolygon,
  toggleDrawLine,
  toggleDrawCircle,
  toggleDrawPoint,
  toggleDrawText,
} from "@/js/draw"

// JS Modules - Utils
import attachMapRightClickMenu, {
  buildBookmarksMenu,
  buildGoogleMapsSearchUrl,
  buildJosmRemoteUrlByCenter,
  buildOsmEditorUrl,
  buildRapidMapillaryUrl,
  buildRapidUrl,
  buildRoutingMenuORS,
  buildShortLinksMenu,
  buildStreetViewUrl,
  buildSVUrlSimple,
  exportVisibleDXF_fromMap01,
  exportVisibleGeoJSON_fromMap01,
  openOrCopyOverpass,
  openTopRightWindowFlushHalfWidthFullHeight,
  pointFeature,
  pushFeatureToGeoJsonSource,
  removePointUnderCursor,
  setSvPin,
} from "@/js/utils/context-menu"
import { tuneMapForIOS } from '@/js/utils/ios-map-tuning'
import { tuneMapInteractionSpeed } from '@/js/utils/map-speed-tuning'
import { startMemoryMonitor } from '@/js/utils/memory-monitor'
import {refreshRadiusHighlight} from "@/js/utils/radius-highlight"
import {buildTri50Submenu, snapIfNeeded} from '@/js/utils/triangle50'
import { attachViewOrientationPair } from '@/js/utils/view-orientation-tracker'
import { startHoldRotate, startHoldPitch } from '@/js/utils/view-orientation-anim'
import { registerBlendModeProtocol } from '@/js/blendModeProtocol'
import { setupTouchScrollHandlers, setupMapProtocols, createMapInstances } from '@/js/mapSetup'
import { startAreaAnalysisFreehand } from '@/js/areaAnalyzer'

// Geoid
import { ensureGeoid } from '@/geoid'

// Assets
import rtkPngUrl from '@/assets/icons/oh-rtk.png'

/* ======== GCPワープ改善ユーティリティ ======== */



export default {
  name: 'App',
  components: {
    Splash,
    TermsAgreementDialog,
    WhatsNewDialog,
    DialogLayer,
    PopularLayers,
    DialogMenu,
    DialogMyroom,
    DialogExt,
    ExtMpiRrim,
    Dialog2,
    DialogShare,
    DialogDrawConfig,
    DialogDrawShare,
    GalleryWindow,
    DialogPopularSets,
    DrawDrawer,
    DrawListDrawer,
    AboutDrawer,
    PointInfoDrawer,
    RightDrawer,
    RightDrawerTokizyo,
    GyoseiHistoryDrawer,
    ChibanzuDrawer,
    MiniTooltip,
    FloatingWindow,
    DrawFloatingWindow,
    PhotoSketchFloatingWindow,
    TextDetailFloatingWindow,
    DrawVisibilityWindow,
    SpatialQueryWizard,
    FreehandCanvas,
    LassoCanvas,
    FieldNoteMemo,
    PaintEditor,
    MyMapViewer,
    AiEnhanceDialog,
    VDialogIframe,
    MessageDialog,
    WelcomeWizard,
    OnboardingTooltip,
    OnboardingPurposeSelector,
    MapillaryFilter,
    AddressSearch,
    KyusekiMapPreview,
    Mw5PreviewManager,
    HillshadeControl,
    HillshadePanel,
    VDialogConfirm,
    WarpWizard,
    Traverse,
    JobPicker,
    VDialogCommon,
    LoginManager,
    KyusekiImportDialog,
    SimpleImportDialog,
    RightButtons,
    LayerCredits,
    MagnifyingGlass,
    ZoomInfoWindow,
    TimeSlider,
    OfflineAreaManager,
    ChibanInfoWindow,
    ChibanzuInfoWindow,
    SimaQuickWindow,
    StreetViewWindow,
    IsochroneWindow,
    RoadIsochroneWindow,
    WatershedWindow,
    PriceHistoryChart,
    FileDropFlowDialog,
    HomukyokuXmlImportDialog,
    RealEstateReportDialog,
    DialogContainer,
    // AiPointInfoDialog, // 一時的にコメントアウト
    PropertyReportDialog,
    MultiPointRankingDialog,
    ChildCareReportDialog,
    SuperPopulationDialog,
    TeacherDashboard,
    OrganizationAdmin,
    AreaAnalysisDialog,
    CsvJoinAnalysisDialog,
    FsInvestigationDialog,
    MaisokuDialog,
    JusetsuDraftDialog,
    SuperCrossSectionDialog,
    MunicipalityPyramidDialog,
    AiOsmSearchDialog,
    PhotoMemoUpload,
    TouhonOverlayDialog,
    TouhonDiffDialog,
    MapCompareDialog,
    SpreadsheetSyncDialog,
    GroupManageDialog,
    HelpDialog,
    AiHelpChat,
    ElevationDialog,
    SnapshotFab,
    ExtDialogFab,
    AiMapChatFab,
    AiAgentFab,
    AiMapQuery,
    AiAgentDialog,
    ChoroplethLegend,
    ChartFloatingWindow,
    NorthArrow,
    MediaPreviewDialog,
    Diorama3D,
    CrossSection3D,
    // SakuraEffect, // 桜エフェクト一時無効化
  },
  setup() {
    const { handleGpsPhotosImport } = useGpsPhotoImport()
    return {
      handleGpsPhotosImport
    }
  },
  data: () => ({
    terrain3dMenu1s: { map01: false, map02: false, map03: false, map04: false }, // 3Dメニュー1の開閉状態（mapName毎）
    terrain3dMenu2: false, // 3Dメニュー2の開閉状態（map01のみ）
    showDisclaimer: false, // 免責表示フラグ（スプラッシュ後にtrue）
    isResetting: false, // リセット処理中フラグ
    studentInteractionsDisabled: false, // 授業同期中の生徒操作無効化フラグ
    usageTracker: null, // Usage tracking
    showElevationProfileModeDialog: false,
    showFreehandInfoDialog: false, // フリーハンド断面図不可の案内ダイアログ
    pendingFreehandSource: null, // フリーハンド開始元 ('modeDialog' | 'rightButton')
    showAreaAnalysisModeDialog: false,
    showPopulationProfileModeDialog: false,
    showFsInvestigationModeDialog: false, // 土地法規等調査モード選択
    showMultiPointRanking: false, // AI複数地点比較ランキング
    mprdInitialPoints: null,      // URL共有からの復元用: [{lng, lat}, ...]
    mprdInitialPresetKey: null,   // URL共有からの復元用: 'balanced' | 'family' | ...
    mprdInitialWeights: null,     // URL共有からの復元用: [access, convenience, education, safety, assetValue, future]
    showFsAreaTooLargeDialog: false, // 土地法規等調査面積超過ダイアログ
    fsAreaTooLargeValue: '', // 土地法規等調査超過面積（ha）
    areaAnalysisPointSelectActive: false,
    pendingOpenSimulationDialog: false, // ポイント選択後にシミュレーションダイアログを開く
    // 共有地物アクションダイアログ（3択）
    showShareFeatureActionDialog: false,
    shareFeatureActionProps: null, // { feature, aiType, typeName, isPolygon }
    shareFeatureActionResolve: null,
    // 面積超過エラーダイアログ
    showAreaTooLargeDialog: false,
    areaTooLargeValue: '',
    // 空間クエリ用ポリゴンデータ
    spatialQueryPolygonData: null,
    osmVectorInitialized: false, // OSMベクター初期化フラグ
    rtkPng: '', // RTKボタン用アイコン
    swipeBlockedNotified: false, // 小画面スワイプ不可通知
    // 陰影関連
    hsRef: null,
    demUrl: '',
    // オンボーディング関連
    showPurposeSelector: false, // オンボーディング無効化
    showOnboardingTooltip: false,
    onboardingPurpose: null, // 'survey', 'general', 'old-map'
    onboardingStepIndex: 0,
    wizardOpen: false,
    showSignUpPromoDialog: true, // 新規登録促進ダイアログの表示状態
    dontShowSignUpPromo: false, // 次回から表示しないチェックボックス
    authStateReady: false, // Firebase認証状態の確認が完了したか
    termsAgreed: false, // 利用規約同意フラグ
    // 使い方ツアー（OnboardingTooltip使用）
    showUsageTour: false, // ツアー表示フラグ
    usageTourStep: 0, // 現在のステップ
    currentTourType: 'basic', // 現在のツアータイプ
    opacityAnimationTimer: null, // 透過度アニメーションのタイマーID
    layerSwapTimer: null, // レイヤー順入れ替えアニメーションのタイマーID
    // usageTourSteps は computed に移動（アクションでthisを使うため）
    affineM: null,   // ★ 追加（リアクティブ）
    printTitleHeight: 8, // 印刷タイトルの高さ（日付の位置調整用）
    printTileEnabledLocal: false,
    printTileSizeLocal: 'a4',
    printTileSizeItems: [
      { value: 'a4', label: 'A4' },
      { value: 'a3', label: 'A3' },
    ],
    isRightDiv: true,
    segments: 100,
    mainGeojson: { type: 'FeatureCollection', features: [] },
    history: [],
    redoStack: [],
    snackbar: false,
    snackbarText: '',
    tempFreehandCoords: [],
    tempPolygonCoords: [],
    tempLineCoords: [],
    tempLineCoordsGuide: [],
    isDrawingLine: false,
    isDrawingFree: false,
    isDrawing: false,
    isCursorOnPanel: false,
    panelHoverCount: 0, // ←複数パネル対策
    // floating-buttons ドラッグ用
    floatingButtonsDragging: false,
    floatingButtonsDragOffset: { x: 0, y: 0 },
    floatingButtonsUserPos: null, // ユーザーが手動で移動した位置
    clickedFeatureInfo: null, // クリックされたポリゴン/ラインの情報
    showXDialog: false,
    imgUrl: null,
    mapDivId: "map01", // ← MapLibreのDIVのID
    tweetText: "入力可能になりました。画像が表示されない場合もう一回やりなおしてください。\n\n#openhinata3 #OH3",
    attributionControl: null,
    printDialog: false,
    originalStyle: null,
    file: null,
    mapillaryWidth: '0px',
    mapillarHeight: '0px',
    bannerHeight: 30, // 有償化バナーの高さ（px）、mounted後に実測値で上書き
    trialMonthlyUsage: null, // 体験プラン用: 過去30日間の消費PT（nullは未取得）
    mapNames: ['map01','map02','map03','map04'],
    lastScaleMapLayout: null, // 画面表示モード変更時のスケール位置リセット用
    mapSpeedTuners: { map01: null, map02: null, map03: null, map04: null },
    iosTuningCleanup: null,        // iOS向けチューニングのクリーンアップ関数
    memoryMonitorCleanup: null,    // メモリ監視のクリーンアップ関数
    memoryCleanupInterval: null,   // メモリクリーンアップ用インターバル
    // 4画面モード ドラッグ&ドロップ用
    draggingMapName: null,         // ドラッグ中のマップ名
    dragOverMapName: null,         // ドラッグオーバー中のマップ名
    // map01とmap02の同期用
    mapSyncEnabled: false,
    mapSyncMaster: 'map01',        // どちらのマップを主にするか（スワイプ時に設定）
    printZoomOffset: 0,            // 詳細/俯瞰モード: map02のズームオフセット（+1, +2, +3, -1, -2, -3）
    mapSyncHandlers: {
      map01ToMap02: null,
      map02ToMap01: null,
      map01PointerDown: null,
      map01TouchStart: null,
      map02PointerDown: null,
      map02TouchStart: null,
      rafId: null,
    },
    swipeFollowHandlers: {
      move: null,
      rafId: null,
    },
    // 価格推移表FW（無限生成対応）
    priceHistoryWindows: [],
    priceHistoryDefaultPos: { top: 10, left: 65 },
    priceHistoryOffset: 30, // ずらし幅
    swipeManualPanHandlers: null,
    swipeNudgeHandlers: null,      // スワイプ時の再描画促進用ハンドラ
    swipePanHandlers: null,        // （未使用）スワイプ専用パン用ハンドラ
    swipePanActive: false,
    swipeNudgeScheduled: false,
    mapSize: {
      map01: {top: 0, left: 0, width: '100%', height: '100%'},
      map02: {top: 0, right: 0, width: '50%', height: '100%'},
      map03: {top: '50%', left: 0, width: '50%', height: '50%'},
      map04: {top: '50%', right: 0, width: '50%', height: '50%'},
    },
    // スワイプ比較モード
    swipeMode: false,
    swipePosition: 50, // パーセント（0-100）
    permalink: '',
    param:'',
    dbparams:'',
    mapName: '',
    pitch:{map01:0,map02:0},
    bearing:0,
    zoom:0,
    dialog: false,
    dialogForSima: false,
    ddSimaText: '',
    items: [
      'WGS84',
      '公共座標1系', '公共座標2系', '公共座標3系',
      '公共座標4系', '公共座標5系', '公共座標6系',
      '公共座標7系', '公共座標8系', '公共座標9系',
      '公共座標10系', '公共座標11系', '公共座標12系',
      '公共座標13系', '公共座標14系', '公共座標15系',
      '公共座標16系', '公共座標17系', '公共座標18系',
      '公共座標19系',
      '日本測地系1系', '日本測地系2系', '日本測地系3系',
      '日本測地系4系', '日本測地系5系', '日本測地系6系',
      '日本測地系7系', '日本測地系8系', '日本測地系9系',
      '日本測地系10系', '日本測地系11系', '日本測地系12系',
      '日本測地系13系', '日本測地系14系', '日本測地系15系',
      '日本測地系16系', '日本測地系17系', '日本測地系18系',
      '日本測地系19系'
    ],
    dialogForDxfApp: false,
    windowWidth: window.innerWidth,
    // transparent: 1,
    transparentType: [
      { label: '透過する。（処理遅い）', value: '1' },
      { label: '透過なし。（処理早い）', value: '0' }
    ],
    thumbnailType: {borderRadius:'10px',containerSize:100},
    thumbnailTypes: [
      { label: '四角大', value: {borderRadius:'10px',containerSize:100} },
      { label: '四角中', value: {borderRadius:'10px',containerSize:50} },
      { label: '四角小', value: {borderRadius:'10px',containerSize:30} },
      { label: '丸大', value: {borderRadius:'50%',containerSize:100} },
      { label: '丸中', value: {borderRadius:'50%',containerSize:50} },
      { label: '丸小', value: {borderRadius:'50%',containerSize:30} },
      { label: '無し', value: {borderRadius:'0%',containerSize:1} },
    ],
    containerColor: 'white',
    containerColors: [
      { label: '白', value: 'white' },
      { label: '黒', value: 'black' },
      { label: '赤', value: 'red' },
      { label: '青', value: 'blue' },
      { label: '黄', value: 'yellow' },
      { label: '緑', value: 'green' },
      { label: '橙', value: 'orange' },
      { label: '紫', value: 'violet' },
      { label: 'ピンク', value: 'pink' },

    ],
    resolutions: [13,14,15,16,17,18,19,20,21,22,23,24],
    dialogForGeotiffApp1file: false,
    dialogForPdfApp: false,
    dialogForPdfPrintOptions: false,
    dialogForImagePng: false,
    shpPropaties: [],
    shpGeojson: [],
    loadingSnackbar: false,
    publicItems: [{public:-1,label:'オープンデータ（マップ上は緑色、詳細は表示）'},{public:0,label:'完全非公開（マップ上は透明、詳細は非表示）'},{public:3,label:'非公開（マップ上は灰色、詳細は非表示）'},{public:1,label:'公開（マップ上は青色、詳細は表示）'}],
    kaiji2s: [{kaiji2:1,label:'自分で自治体へ確認する'},{kaiji2:2,label:'運営者（復代理人含む）に一任する'},{kaiji2:3,label:'開示を希望しない'}],
    isPublic: false,
    drawControl: null,
    showDrawUI: false,
    tDraw: false,
    drawInstance: false,
    geojon: null,
    intervalId: null,
    preUrl: '',
    historyCount: 0,
    drawerVisible: false,
    selectedFeature: null,
    showLayerDialog: false,
    dialogForSaveDXF2: false,
    // 右クリックメニュー DXF出力ダイアログ用
    dialogForVisibleDXF: false,
    visibleDxfCadType: '2d',
    visibleDxfCadTypeItems: [
      { text: '2D CAD用（jww/HO-CAD）', value: '2d' },
      { text: '測量・3D CAD用（福井/アイサン/AutoCAD）', value: 'survey' }
    ],
    // -----------------------------------------
    showFloatingImage: false,
    gcpList: [],  // ← GCP座標リスト
    mapCoordMarkers: [], // 地図上に置いたマーカー一覧
    hoveredRow: null,
    primaryColor: '#1976d2',
    showWarpCanvas: false,
    imageLoaded: false,
    showOriginal: true,
    showTileDialog: false,
    originalEnable: null,
    scaleText: '',
    isRightDiv2: false,  // 印刷モードの状態（false: 通常モード, true: 印刷モード）
    dialogForDl: false,
    dialogForChibanzyOrDraw: false,
    angleValue:0,
    plusInterval: null,
    minusInterval: null,
    plusStep: 1,
    minusStep: 1,
    // 拡大／縮小操作用ステップやインターバルID
    scaleUpStep: 1,
    scaleDownStep: -1,
    scaleUpInterval: null,
    scaleDownInterval: null,
    scaleValue: 100,
    lassoSelectedColor: 'yellow',
    lassoOpacity: 50,
    lassoCustomColor: '#ffff00',
    showLassoCloseConfirm: false,
    showLassoAdvanced: false,
    dialogForDrawCsv: false,
    // 登記簿PDF一括処理ダイアログ
    toukiBatchDialog: false,
    toukiBatchProgress: { current: 0, total: 0, fileName: '' },
    csvColumns: [],
    csvAddressColumn: '',
    csvLabelColumn: '',
    csvColorColumn: '',
    csvLongTextColumn: '',
    csvPictureUrlColumn: '',
    csvRecords: [],
    // CSV取り込みモード: 'geocode' | 'wgs84' | 'jprcs'
    csvImportMode: 'geocode',
    csvLonColumn: '',
    csvLatColumn: '',
    csvXColumn: '',
    csvYColumn: '',
    csvJprcsZone: 9, // 公共座標系の系番号 (1-19)
    geojsonForDraw: null,
    dialogForDraw: false,
    dialogForDrawGeojson: false,
    geojsonProperties: [],
    geojsonLabelProperty: '',
    geojsonPointColor: 'red',
    geojsonData: null,
    colorItems: [
      { label: '赤', value: 'red' },
      { label: '黒', value: 'black' },
      { label: '青', value: 'blue' },
      { label: '緑', value: 'green' },
      { label: 'オレンジ', value: 'orange' },
      { label: 'ホットピンク', value: 'hotpink' },
      { label: '透明', value: 'rgba(0,0,0,0)' }
    ],
    csvLonLat: null,
    tileUrl: '',
    tileCount: 0,
    totalTileCount: 0,
    minZoom: 1,
    maxZoom: 17,
    tileTemplates: [
      'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png',
      // 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'
    ],
    pmtilesTemplates: [
        'https://kenzkenz.net/pmtiles/2025final3.pmtiles'
    ],
    windowlTitle: '',
    qrCodeWidth: 200,
    paintSettings: {},
    dialogForLayerName: false,
    isDisabled: false,
    isDisabled2: true,
    isNewPicture: false,
    imgRotation: 0,
    dialogForPictureZindex: 0,
    zIndex: 0,
    confirmBtnColor: 'info',
    drawTool: null,
    isUndoBtnDisabled: false,
    cancelBtnMiniTooltip: "描きかけ中の全ポイントを削除",
    undoBtnMiniTooltip: "直前のポイントを削除",
    map01Tracker: null,
    map02Tracker: null,
    stopSpin: null,
    stopPitch: null,

    // 状態
    watchId: null,
    geoPollTimer: null,
    isTracking: false,
    // isKuiuchi: false,
    geoLastTs: 0,

    // 表示・品質関連
    prevQuality: null,
    geoAccAvgAlpha: 0.3, // EWMA 平滑化係数（0<alpha<=1）

    // UI 設定
    isHeadingUp: false,

    // 既存UI依存（存在すれば使う）
    currentMarker: null,
    centerMarker: null,
    compass: null,

    rtkWindowMs: 5000,   // 無視する時間窓（ms）
    lastRtkAt: 0,      // 直近でRTK級を測位したタイムスタンプ(ms)

    logEnabled: false,          // ロギングON/OFF
    csvRows: null,              // 2次元配列（ヘッダー含む）
    lastLogAt: 0,               // 直近記録時刻
    lastLogXY: null,            // 直近記録XY {x, y}（X=北, Y=東）
    minLogIntervalMs: 1000,     // 時間間引き（ms）
    minLogDistanceM: 0.3,       // 距離間引き（m）

    confirmClearLog: false,

    isJobMenu: false,

    lineMode: false,

    currentHillshadeMaps: { map01: true, map02: true },

    showKyusekiDialog: false,
    showHandwriteDialog: false,
    // OH3側のジョブIDなど、親から渡したいものがあればここで管理
    currentJobId: null,
    // 方位＋距離から座標展開する場合の起点。XY読み取りなら null でもOK
    startXY: { x: 0, y: 0 },

  }),
  computed: {
    ...mapState([
      'isSplash',
      'mapReady',
      'pendingFile',
      'disabledForSokui',
      'isContextMenu',
      'showCrosshair',
      'isTerrainEnabled',
      'userId',
      'myNickname',
      'isKuiuchi',
      'confirmMessage',
      'confirmProps',
      'showConfirm',
      'isFromIframe',
      'isIframe',
      'showDrawConfrim',
      'mapillaryTytle',
      'mapillaryType',
      'noProgress',
      'map01',
      'map02',
      'is360Pic',
      'popupDialog',
      'drawFeatureId',
      'isIphone',
      'isSmall500',
      'bottomSheetActive',
      'isDraw',
      'showDrawDrawer',
      'showDrawListDrawer',
      'showPointInfoDrawer',
      'showRightDrawer',
      'selectedPointFeature',
      'showChibanzuDrawer',
      'clickCircleGeojsonText',
      'printTitleText',
      'printDate',
      'textPx',
      'titleColor',
      'titleDirection',
      'drawGeojsonId',
      'isUsingServerGeojson',
      'drawFeature',
      'geo',
      'elevation',
      'distance',
      'is4ScreenMode',
      'fourScreenPositions',
      'mapSyncMode',
      'mapIndividualStates',
      'shareFeatures',
      'shareFeaturesLayerAdded',
      'lessonMode'
    ]),
    ...mapGetters([
      'canUndo',
      'canRedo',
      'undoCount',
      'redoCount'
    ]),
    daysUntilRestriction() {
      const deadline = new Date('2026-04-01T00:00:00+09:00')
      const now = new Date()
      const diff = deadline - now
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
    },
    // --- s_* store computed (自動生成) ---
    ...storeComputed([
      'printMap', 'isKuiuchi', 'showConfirm',
      'mapillaryEndDate', 'mapillaryStartDate', 'mapillaryUserName',
      'is360Pic', 'dialogForVersion',
      'selectedFile', 'previewUrl', 'pictureUrl',
      'drawGeojsonId', 'dialogForPicture', 'pmtilesMaximum',
      'propnames', 'pmtiles0Name', 'pmtiles0Id',
      'dialogForOffline2', 'dialogForOffline', 'isPrint', 'isLassoSelected',
      'popupDialog', 'popupHtml', 'pointSima',
      'updatePermalinkFire', 'gazoName', 'isDraw', 'editEnabled',
      'isDrawLasso', 'isDrawFix', 'isDrawFree',
      'isDrawPolygon', 'isDrawLine', 'isDrawCircle', 'isDrawPoint', 'isDrawText',
      'drawButtonsVisible',
      'selectedPublic', 'selectedKaiji2', 'geojsonFile',
      'chibanzuPrefCode', 'chibanzuCityCode', 'chibanzuPropaties', 'chibanzuGeojson',
      'showChibanzuDialog', 'mapillaryDialog',
      'address', 'pmtilesName', 'pmtilesPropertieName', 'shpPropertieName',
      'isTransparent', 'loadingMessage', 'isSmartPhone', 'isWindow',
      'loading3', 'loading2', 'loading',
      'drawGeojsonText', 'map2Flg', 'dialogForLink', 'dialogForImage',
      'simaOpacity', 'dialogForGroup', 'dialogForLogin',
      'dialogForPngApp', 'dialogForPng2App', 'dialogForJpgApp',
      'dialogForGeotiffApp', 'dialogForGeotiff2App', 'dialogForSimaApp',
      'ocrAccuracy', 'zahyokei', 'snackbar', 'snackbarForGroup', 'snackbarForGroupText',
      'terrainLevel', 'selectedLayers',
    ]),
    ...storeComputedReadonly([
      'url', 'finishLineFire', 'saveHistoryFire', 'isDrawDuplicate',
      'currentGroupId', 'isAndroid', 'simaFire', 'isCursorOnPanel',
    ]),
    // permalink更新トリガー統合用（5つのwatcherを1つにまとめる）
    // いずれかが変化するとwatchが発火してupdatePermalink()が呼ばれる
    permalinkTriggerKey() {
      return [
        this.s_updatePermalinkFire,
        this.$store.state.isCrossSection3D,
        this.$store.state.isDiorama3D,
        this.$store.state.elevationGeojson,
        this.s_isWindow,
      ]
    },
    // 認証状態（authStateからインポートしたuserをリアクティブに取得）
    authUser() {
      return user.value
    },
    mapSpeedProfile() {
      return this.$store.state.mapSpeedProfile
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    // 求積表FWの初期高さ（ブラウザ高さを超えないように制限）
    kyusekiDefaultHeight() {
      if (this.isSmall500) return 540
      // PCでは最大800px、ただしブラウザ高さ-100pxを上限とする
      return Math.min(800, window.innerHeight - 100)
    },
    hasSimaLayers() {
      if (!this.map01) return false
      return this.map01.getStyle().layers.some(layer => layer.id.includes('oh-sima-'))
    },
    hasDxfLayers() {
      if (!this.map01) return false
      return this.map01.getStyle().layers.some(layer => layer.id.includes('oh-dxf-'))
    },
    isReportDialogOpenOnSmallScreen() {
      // 小画面かつレポートダイアログが開いている場合
      return this.isSmall500 && !!this.$store.state.floatingWindows['real-estate-report-window']
    },
    isElevationDialogOpenOnSmallScreen() {
      // 小画面かつ標高図ダイアログが開いている場合
      return this.isSmall500 && !!this.$store.state.floatingWindows['elevation-dialog']
    },
    isOfflineDialogOpenOnSmallScreen() {
      // 小画面かつオフラインダイアログが開いている場合
      return this.isSmall500 && !!this.$store.state.floatingWindows['offline-area-manager-window']
    },
    isRightDrawerOpenOnSmallScreen() {
      return this.isSmall500 && !!this.$store.state.showRightDrawer
    },
    isRightDrawerOpenOnLargeScreen() {
      // デスクトップでの十字マーク移動を無効化（うるさいので）
      return false
      // return !this.isSmall500 && (!!this.$store.state.showRightDrawer || !!this.$store.state.showChibanzuDrawer)
    },
    isMlitDrawerOpenOnLargeScreen() {
      return !this.isSmall500 && !!this.$store.state.floatingWindows['mlit-search-window']
    },
    isMlitDrawerOpenOnSmallScreen() {
      return this.isSmall500 && !!this.$store.state.floatingWindows['mlit-search-window']
    },
    // 教員が授業同期中かどうか
    isLessonSyncActive() {
      const activeLesson = this.$store.state.activeLesson
      return !!(activeLesson && activeLesson.syncEnabled)
    },
    // コンパスのCSSクラス（トラッキング・杭打ちモード用）
    compassDivClass() {
      const isTrackingOrKuiuchi = (!this.s_isPrint && this.isTracking) || (!this.s_isPrint && this.s_isKuiuchi)
      return isTrackingOrKuiuchi ? 'compass-div-tracking' : 'compass-div'
    },
    shouldShowSplash () {
      return !!this.isSplash
    },
    // 現在のツアータイプに応じたステップを返す
    currentTourSteps() {
      switch (this.currentTourType) {
        case 'basic':
        default:
          return this.basicTourSteps
      }
    },
    // レイヤー編ツアーのステップ定義
    basicTourSteps() {
      return getBasicTourSteps(this)
    },
    // ここで bind(this) して子へ渡す
    handlers () {
      return {
        handlePrint:        this.handlePrint.bind(this),
        goToCurrentLocation:this.goToCurrentLocation.bind(this),
        toggleWatchPosition:this.toggleWatchPosition.bind(this),
        share:              this.share.bind(this),
        help:               this.help.bind(this),
        drawListOpen:       this.drawListOpen.bind(this),
        // toggleLDraw:        this.toggleLDraw.bind(this),
        finishLine:         this.finishLine.bind(this),
        toggleDrawPoint: this.toggleDrawPoint.bind(this),
        toggleDrawText: this.toggleDrawText.bind(this),
        toggleDrawCircle: this.toggleDrawCircle.bind(this),
        toggleLDrawLine: this.toggleLDrawLine.bind(this),
        toggleLDrawPolygon: this.toggleLDrawPolygon.bind(this),
        toggleLDrawFree: this.toggleLDrawFree.bind(this),
        toggleFreehandCanvas: this.toggleFreehandCanvas.bind(this),
        closeFreehandCanvas: this.closeFreehandCanvas.bind(this),
        toggleDrawLasso: this.toggleDrawLasso.bind(this),
        toggleEditEnabled: this.toggleEditEnabled.bind(this),
        undo: this.undo.bind(this),
        redo: this.redo.bind(this),
        drawConfig: this.drawConfig.bind(this),
        drawShare: this.drawShare.bind(this),
        dialogForDlOpen: this.dialogForDlOpen.bind(this),
        deleteAllforDraw: this.deleteAllforDraw.bind(this),
        print: this.print.bind(this),
        pdfDl: this.pdfDl.bind(this),
        // === PDF Pro 機能 START ===
        // pdfDlPro: this.pdfDlPro.bind(this),
        // === PDF Pro 機能 END ===
        pngDl0: this.pngDl0.bind(this),
        openQrCodeWindow: this.openWindow.bind(this),
        handleGpsPhotosImport: this.handleGpsPhotosImport.bind(this),
        openHillshadeDialog: this.openHillshadeDialog.bind(this),
        toggleSwipeMode: this.toggleSwipeMode.bind(this),
        drawOpen: this.drawOpen.bind(this),
        onAiEnhance: this.onAiEnhance.bind(this),
      }
    },
    geoQuality () {
      const geo = this.$store.state.geo
      if (geo) {
        const qualityHtml = geo.quality === 'RTK級' ? '<span style="color: blue;">RTK</span>' : geo.quality
        return `品質= ${qualityHtml}（${geo.accuracy.toFixed(2)}m）`
      } else {
        return ''
      }
    },
    filterWidth(){ return this.isSmall500 ? 350 : 500 },
    mlyDefaultLeft() {
      const vw = (typeof window !== 'undefined' ? window.innerWidth : 1280)
      return Math.max(10, vw - this.filterWidth - 10)  // 右10px
    },
    dialogIds () {
      return Object.keys(this.$store.state.messageDialog.registry)
    },
    s_prevGeojsons: {
      get() {
        return this.$store.prevGeojsons
      },
      set(value) {
        this.$store.commit('setPrevGeojsons', value)
      }
    },
    isImage() {
      const file = Array.isArray(this.s_selectedFile)
          ? (this.s_selectedFile[0] || null)
          : this.s_selectedFile;
      if (file) {
        return !!(file.type && file.type.startsWith("image/"));
      } else {
        return isImageFile(this.s_previewUrl);
      }
    },
    isVideo() {
      const file = Array.isArray(this.s_selectedFile)
          ? (this.s_selectedFile[0] || null)
          : this.s_selectedFile;
      if (file) {
        return !!(file.type && file.type.startsWith("video/"));
      } else {
        return isVideoFile(this.s_previewUrl);
      }
    },
    isPdf() {
      const file = Array.isArray(this.s_selectedFile)
          ? (this.s_selectedFile[0] || null)
          : this.s_selectedFile;
      if (file) {
        return file.type === 'application/pdf' || isPdfFile(file.name || '');
      } else {
        return this.s_previewUrl ? isPdfFile(this.s_previewUrl) : false;
      }
    },
    s_selectedFileName() {
      const file = Array.isArray(this.s_selectedFile)
          ? (this.s_selectedFile[0] || null)
          : this.s_selectedFile;
      return file ? file.name : '';
    },
    // グローバル画像プレビュー用（popup.js等から呼び出し可能）
    globalMediaPreviewOpen: {
      get() {
        return this.$store.state.globalMediaPreview.open
      },
      set(value) {
        if (!value) {
          this.$store.commit('closeGlobalMediaPreview')
        }
      }
    },
    globalMediaPreviewSrc() {
      return this.$store.state.globalMediaPreview.src
    },
    globalMediaPreviewKind() {
      return this.$store.state.globalMediaPreview.kind
    },
    globalMediaPreviewFilename() {
      return this.$store.state.globalMediaPreview.filename
    },
    // buttons0() {
      // const btns =
      //     [
            // { key: 'print', text: '印刷', icon: 'mdi-printer', color: 'primary', disabled: this.isSmall500,  click: this.handlePrint },
            // { key: 'currentPosition', text: '現在地取得', icon: 'mdi-crosshairs-gps', click: this.goToCurrentLocation },
            // { key: 'watchPosition', text: '現在地連続取得', icon: 'mdi-map-marker-radius', click: this.toggleWatchPosition },
            // { key: 'share', text: '共有', icon: 'mdi-share-variant', color: 'primary', click: this.share },
            // { key: 'help', text: 'ヘルプ', icon: 'mdi-help', color: 'primary', click: this.help },
            // { key: 'drawList', text: 'ドローリスト', icon: 'mdi-view-list', color: 'primary', click: this.drawListOpen, style: 'margin-top: 200px;' },
    //       ]
    //   return btns
    // },
    buttons1() {
      let btns =
      [
        { key: 'point', text: '点、画像', label: '点', color: this.s_isDrawPoint ? 'green' : 'blue', click: this.toggleDrawPoint },
        { key: 'circle', text: '円', label: '円', color: this.s_isDrawCircle ? 'green' : 'blue', click: this.toggleDrawCircle },
        { key: 'line', text: '線', label: '線', color: this.s_isDrawLine ? 'green' : 'blue', click: this.toggleLDrawLine },
        { key: 'polygon', text: '多角形', label: '多角', color: this.s_isDrawPolygon ? 'green' : 'blue', click: this.toggleLDrawPolygon },
        { key: 'free', text: '自由に描く', label: '自由', color: this.s_isDrawFree ? 'green' : 'blue', click: this.toggleLDrawFree},
        { key: 'lasso', text: '投げ縄', label: '投げ縄', color: this.s_isDrawLasso ? 'green' : 'blue', click: this.toggleDrawLasso,style: 'font-size:12px;' },
        // { key: 'finish', text: '編集が終わったクリック！', label: '確定', click: this.finishDrawing, style: 'background-color: orange!important;' },
        { key: 'edit', text: '変形と移動', label: '編集', color: this.s_editEnabled ? 'green' : undefined, click: this.toggleEditEnabled },
        // { key: 'rotate', text: '回転', label: '回転',  click: this.drawRotate },
        { key: 'undo', text: '元に戻す', icon: 'mdi-undo', label: '元戻', click: this.undo },
        { key: 'redo', text: 'やり直す', icon: 'mdi-redo', label: 'やり直', click: this.redo },
        // { key: 'fix', text: '画面固定', label: '固定', color: this.s_isDrawFix ? 'green' : 'blue', click: this.toggleDrawFix },
        { key: 'config', text: '設定', label: '設定', color: 'blue', click: this.drawConfig },
        { key: 'dl', text: '各種ダウンロード', label: 'DL', style: 'background-color: navy!important;', click: this.dialogForDlOpen },
        { key: 'delete', text: '全削除', icon: 'mdi-delete', color: 'error', click: this.deleteAllforDraw },

        { key: 'close', text: '閉じる', color: 'green', icon: 'mdi-close',  click: this.drawClose }
      ]
      if (this.isSmall500) {
        // btns = btns.filter(btn => btn.key !== 'free')
        // btns = btns.filter(btn => btn.key !== 'lasso')
        btns = btns.filter(btn => btn.key !== 'dl')
        btns = btns.filter(btn => btn.key !== 'ex')
      }
      return btns
    },
    s_jdpCoordinates () {
      if (this.$store.state.jdpCoordinates) {
        if (this.$store.state.jdpCoordinates[0]) {
          return `${this.$store.state.jdpCoordinates[1].toFixed(3)}, ${this.$store.state.jdpCoordinates[0].toFixed(3)}`
        } else {
          return ''
        }
      } else {
        return ''
      }
    },
    s_isDrawAll() {
      return this.s_isDrawCircle || this.s_isDrawPoint || this.s_isDrawLine;
    },
    // 情報ボックスの表示条件（描画中 or クリック時 or 情報表示中）
    showDrawInfo() {
      const isDrawingMode = (this.s_isDrawPolygon || this.s_isDrawLine || this.s_isDrawCircle) && this.s_isDraw && !this.s_isCursorOnPanel;
      const isClickedMode = !!this.clickedFeatureInfo;
      // drawInfoVisibleがtrueの間は表示を維持
      return (isDrawingMode || isClickedMode || this.$store.state.drawInfoVisible) && !this.s_isPrint;
    },
    selectedLayers () {
      return this.$store.state.selectedLayers
    },
    cityItems() {
      const filteredCities = Object.entries(muni)
          .filter(([, value]) => value.startsWith(`${Number(this.s_chibanzuPrefCode)},`))
          .map(([, value]) => {
            const parts = value.split(',');
            return { prefCode: parts[0].padStart(5, '0'), cityCode: parts[2], cityName: parts[3] };
          });
      return filteredCities
    },
    prefItems() {
      // 都道府県を重複なく抽出
      const prefsSet = new Set();
      Object.values(muni).forEach(item => {
        const [prefCode, prefName] = item.split(',');
        prefsSet.add(`${prefCode},${prefName}`);
      });
      // 配列を作成
      const result = Array.from(prefsSet).map(item => {
        const [prefCode, prefName] = item.split(',');
        return { prefCode: prefCode.padStart(2, '0'), prefName };
      });
      return result
    },
    s_oazaYomi() {
      return this.$store.state.oazaYomi || ''
    },
    s_loadingMessage3: {
      get() {
        return `<div style="text-align: center;">${this.$store.state.loadingMessage3}</div>`
        // return this.$store.state.loadingMessage3
      },
      set(value) {
        this.$store.commit('setLoadingMessage3', value)
      }
    },
    s_transparent: {
      get() {
        return this.$store.state.transparent
      },
      set(value) {
        this.$store.commit('setTransparent', value)
        // localStorage.setItem('transparent',value)
      }
    },
    s_resolution: {
      get() {
        return this.$store.state.resolution
      },
      set(value) {
        this.$store.commit('setResolution', value)
        localStorage.setItem('resolution',value)
      }
    },
    isSmall() {
      return this.windowWidth <= 720;
    },
    // 投げ縄選択中の地物数を計算
    lassoSelectedCount() {
      try {
        const geojson = JSON.parse(this.$store.state.lassoGeojson || '{"features":[]}');
        return geojson.features.length;
      } catch (e) {
        return 0;
      }
    },
    // 投げ縄用色オプション
    lassoColorOptions() {
      return [
        { label: '赤', value: 'red', bg: '#f44336', text: 'white' },
        { label: '青', value: 'blue', bg: '#2196f3', text: 'white' },
        { label: '緑', value: 'green', bg: '#4caf50', text: 'white' },
        { label: '黒', value: 'black', bg: '#212121', text: 'white' },
        { label: 'オレンジ', value: 'orange', bg: '#ff9800', text: 'white' },
        { label: 'ピンク', value: 'hotpink', bg: '#e91e63', text: 'white' },
        { label: '黄', value: 'yellow', bg: '#ffeb3b', text: 'black' },
        { label: '透明', value: 'rgba(0,0,0,0)', bg: '#f5f5f5', text: 'grey' },
      ];
    },
    // 投げ縄FWの位置（BBOXの右または左）
    lassoFwLeft() {
      return this.$store.state.lassoFwPosition?.left ?? 10;
    },
    lassoFwTop() {
      return this.$store.state.lassoFwPosition?.top ?? 10;
    },
    // オンボーディング関連
    currentOnboardingStep() {
      if (!this.onboardingPurpose) return null
      return getStep(this.onboardingPurpose, this.onboardingStepIndex)
    },
    onboardingTotalSteps() {
      if (!this.onboardingPurpose) return 0
      return getTotalSteps(this.onboardingPurpose)
    },
    hasMoreSteps() {
      if (!this.onboardingPurpose) return false
      return hasNextStep(this.onboardingPurpose, this.onboardingStepIndex)
    },
    // 3Dモード状態判定
    is3dOff() {
      const pitch = this.$store.state.map01Pitch || 0
      return pitch === 0 || pitch === '0'
    },
    is3dNoTerrain() {
      const pitch = this.$store.state.map01Pitch || 0
      return (pitch !== 0 && pitch !== '0') && !this.isTerrainEnabled
    },
    is3dWithTerrain() {
      const pitch = this.$store.state.map01Pitch || 0
      return (pitch !== 0 && pitch !== '0') && this.isTerrainEnabled
    },
    // --- 体験プラン残量バナー ---
    // 体験プラン判定（free プラン かつ 組織メンバーでない）
    isTrialPlan() {
      const tb = this.$store.state.tokenBalance
      if (!tb || !tb.success) return false
      if (tb.is_unlimited) return false
      if (tb.is_org_member) return false
      return tb.plan_type === 'free'
    },
    // 残ポイント
    trialPointBalance() {
      const tb = this.$store.state.tokenBalance
      return (tb && typeof tb.point_balance === 'number') ? tb.point_balance : null
    },
    // バナー段階: 'low50' | 'low30' | 'low10' | null
    trialBannerStage() {
      if (!this.isTrialPlan) return null
      const pt = this.trialPointBalance
      if (pt === null) return null
      if (pt < 10) return 'low10'
      if (pt < 30) return 'low30'
      if (pt < 50) return 'low50'
      return null
    },
    // 推奨プラン（過去30日消費PT × 12 から算出）
    // 境界ぴったり問題を避けるため、各プラン容量の1.2倍以内を許容範囲とする
    //   （超過分は追加ポイント購入でカバーできるため）
    // 2000PT超はアカウント共有の疑いが強いため、事務所プラン系を推奨
    trialRecommendedPlan() {
      const usage = this.trialMonthlyUsage
      if (usage === null) return null
      const annualProjected = Math.round(usage * 12)
      const BUFFER = 1.2
      if (annualProjected <= 500 * BUFFER) {
        return { id: 'personal', name: 'Personal', points: 500, price: 9800, projected: annualProjected }
      }
      if (annualProjected <= 2000 * BUFFER) {
        return { id: 'pro', name: 'Pro', points: 2000, price: 24800, projected: annualProjected }
      }
      if (annualProjected <= 6000 * BUFFER) {
        return { id: 'office', name: '事務所', points: 6000, price: 49800, projected: annualProjected }
      }
      if (annualProjected <= 25000 * BUFFER) {
        return { id: 'office_pro', name: '事務所Pro', points: 25000, price: 118000, projected: annualProjected }
      }
      return { id: 'unlimited', name: '無制限', points: null, price: 248000, projected: annualProjected }
    },
  },
  methods: {
    // 体験プラン残量が少なくなった時に過去30日使用量を取得
    async fetchTrialMonthlyUsage() {
      if (!this.isTrialPlan) return
      if (!this.trialBannerStage) return
      if (this.trialMonthlyUsage !== null) return // 既に取得済み
      try {
        const result = await getMonthlyUsage(30)
        if (result.success) {
          this.trialMonthlyUsage = result.total_cost || 0
        }
      } catch (e) {
        console.warn('[App] fetchTrialMonthlyUsage failed:', e)
      }
    },
    onSplashFinish () {
      if (this.$store.state.userId) {
        this.$store.state.showSakuraEffect = true
      }
    },
    // 教員・生徒チェック
    async checkTeacherStatus(uid) {
      return _checkTeacherStatus(this, uid)
    },

    // 海外からのアクセスを検出（ドローツールボタンをアイコン表示にする）
    async checkOverseas() {
      return _checkOverseas(this)
    },

    // 授業モードリスナー（生徒側）
    startLessonListener(classes) {
      return _startLessonListener(this, classes)
    },

    // 教員用の授業同期（ダッシュボードを閉じても動作）
    async startTeacherLessonSync(teacherUid) {
      return _startTeacherLessonSync(this, teacherUid)
    },

    // 教員のマップ同期を開始
    startTeacherMapSync(classId) {
      return _startTeacherMapSync(this, classId)
    },

    // 教員のマップ同期を停止
    stopTeacherMapSync() {
      return _stopTeacherMapSync(this)
    },

    // 生徒の地図操作を無効化（授業同期ON時）
    disableStudentMapInteractions() {
      return _disableStudentMapInteractions(this)
    },

    // 生徒の地図操作を有効化（授業同期OFF時または授業終了時）
    enableStudentMapInteractions() {
      return _enableStudentMapInteractions(this)
    },

    // URL比較（sパラメータで判定）
    isSameUrl(url1, url2) {
      return _isSameUrl(url1, url2)
    },

    // 先生のビュー状態を適用
    applyLessonViewState(viewState) {
      return _applyLessonViewState(this, viewState)
    },

    // 先生の完全な状態を適用（viewState + selectedLayers + isTerrainEnabled）
    applyLessonFullState(lessonData) {
      return _applyLessonFullState(this, lessonData)
    },

    // ドローを地図に適用（リトライ機構付き）
    applyDrawToMap(map, clickCircleGeojsonText, retryCount) {
      return _applyDrawToMap(this, map, clickCircleGeojsonText, retryCount)
    },

    // ドローレイヤーのopacityを一括設定（フェード効果用）
    setDrawLayersOpacity(map, opacity) {
      return _setDrawLayersOpacity(map, opacity)
    },

    // 同期タイムアウトチェック（30分で自動解除）
    checkSyncTimeout(lesson) {
      return _checkSyncTimeout(lesson)
    },

    /** =========================
     * 共有地物（AI分析結果の共有）
     * → shareFeatures.ts に切り出し済み
     * ========================= */
    handleAiShareUrl(...args) { return _handleAiShareUrl(this, ...args) },
    updateAiShareUrl(...args) { return _updateAiShareUrl(this, ...args) },
    showShareFeaturesOnMap(...args) { return _showShareFeaturesOnMap(this, ...args) },
    async handleShareFeatureClick(e) { return _handleShareFeatureClick(this, e) },
    resolveShareFeatureAction(...args) { return _resolveShareFeatureAction(this, ...args) },
    fitToShareFeatures(...args) { return _fitToShareFeatures(this, ...args) },
    addShareFeature(...args) { return _addShareFeature(this, ...args) },
    removeShareFeature(...args) { return _removeShareFeature(this, ...args) },

    openAreaAnalysisModeDialog() { return _mdOpenAreaAnalysisModeDialog(this) },
    cancelAreaAnalysisModeDialog() { return _mdCancelAreaAnalysisModeDialog(this) },
    openDisasterSimulationDialog() { return _mdOpenDisasterSimulationDialog(this) },
    openElevationProfileModeDialog() { return _mdOpenElevationProfileModeDialog(this) },
    selectElevationProfileMode(mode) { return _mdSelectElevationProfileMode(this, mode) },
    confirmFreehandDraw() { return _mdConfirmFreehandDraw(this) },
    openPopulationProfileModeDialog() { return _mdOpenPopulationProfileModeDialog(this) },
    cancelPopulationProfileModeDialog() { return _mdCancelPopulationProfileModeDialog(this) },
    async selectPopulationProfileMode(mode) { return _mdSelectPopulationProfileMode(this, mode) },
    async startPopulationProfileFreehandFlow() { return _mdStartPopulationProfileFreehandFlow(this) },
    async startPopulationProfileCircleFlow() { return _mdStartPopulationProfileCircleFlow(this) },

    async startPopulationProfilePolygonFlow() { return _ifStartPopulationProfilePolygonFlow(this) },

    // 土地法規等調査 モード選択
    openFsInvestigationModeDialog() { return _ifOpenFsInvestigationModeDialog(this) },
    openMultiPointRanking(options) {
      // AI複数地点比較ランキング(シングルインスタンス)を開く
      // options.initialPoints: URL共有からの復元用座標リスト
      // options.initialPresetKey: 重みプリセットキー
      // options.initialWeights: カスタム重み配列
      if (options && typeof options === 'object') {
        this.mprdInitialPoints = options.initialPoints || null
        this.mprdInitialPresetKey = options.initialPresetKey || null
        this.mprdInitialWeights = options.initialWeights || null
      } else {
        this.mprdInitialPoints = null
        this.mprdInitialPresetKey = null
        this.mprdInitialWeights = null
      }
      this.showMultiPointRanking = true
    },
    cancelFsInvestigationModeDialog() { return _ifCancelFsInvestigationModeDialog(this) },
    async selectFsInvestigationMode(...args) { return _ifSelectFsInvestigationMode(this, ...args) },

    async startFsInvestigationFreehandFlow() { return _ifStartFsInvestigationFreehandFlow(this) },

    async startFsInvestigationCircleFlow() { return _ifStartFsInvestigationCircleFlow(this) },

    async startFsInvestigationPolygonFlow() { return _ifStartFsInvestigationPolygonFlow(this) },

    // 超串刺し抽出: ウィザードのFloatingWindow表示トグル
    openSuperCrossSectionWizard() { return _ifOpenSuperCrossSectionWizard(this) },

    // エリア分析フロー → areaAnalysisFlows.ts
    enableAreaAnalysisEvacuationLayerOnce() { return _enableAreaAnalysisEvacuationLayerOnce(this) },
    addSelectedLayerById(layerId) { return _addSelectedLayerById(this, layerId) },
    removeSelectedLayerById(layerId) { return _removeSelectedLayerById(this, layerId) },
    findLayerById(tree, targetId) { return _findLayerById(this, tree, targetId) },
    async selectAreaAnalysisMode(mode) { return _selectAreaAnalysisMode(this, mode) },
    checkAreaLimit(polygon) { return _checkAreaLimit(this, polygon) },
    async startAreaAnalysisFreehandFlow() { return _startAreaAnalysisFreehandFlow(this) },
    async startAreaAnalysisCircleFlow() { return _startAreaAnalysisCircleFlow(this) },
    async startAreaAnalysisPolygonFlow() { return _startAreaAnalysisPolygonFlow(this) },

    // 空間クエリウィンドウを開く
    openSpatialQueryModeDialog() { return _ifOpenSpatialQueryModeDialog(this) },

    beginAreaAnalysisPointSelection() { return _ifBeginAreaAnalysisPointSelection(this) },

    openAreaAnalysisPoint(...args) { return _ifOpenAreaAnalysisPoint(this, ...args) },

    // 利用規約ダイアログを表示
    showTermsDialog() { return _udShowTermsDialog(this) },
    // リセット・オンボーディング → resetAndOnboarding.ts
    checkAndStartOnboarding() { return _checkAndStartOnboarding(this) },
    resetOnboarding() { return _resetOnboarding(this) },
    async reset() { return _reset(this) },
    handlePurposeSelect(purpose) { return _handlePurposeSelect(this, purpose) },
    handleOnboardingNext() { return _handleOnboardingNext(this) },
    handleOnboardingSkip() { return _handleOnboardingSkip(this) },
    handleOnboardingComplete() { return _handleOnboardingComplete(this) },
    getMapTitle(...args) { return _udGetMapTitle(this, ...args) },
    formatPrintDate(...args) { return _udFormatPrintDate(this, ...args) },
    updatePrintTitleHeight() { return _updatePrintTitleHeight(this) },
    getSourceData(...args) { return _udGetSourceData(this, ...args) },
    // ドロー情報表示 → drawInfoDisplay.ts
    closeDrawInfo() { return _closeDrawInfo(this) },
    autoOpenDrawModeIfNeeded() { return _autoOpenDrawModeIfNeeded(this) },
    applyDrawLayerVisibility(targetMapName) { return _applyDrawLayerVisibility(this, targetMapName) },
    async copyDrawInfo() { return _copyDrawInfo(this) },
    extractDrawInfoText(element) { return _extractDrawInfoText(this, element) },
    copyWithExecCommand(text) { return _copyWithExecCommand(this, text) },
    openLoginDialog() { return _udOpenLoginDialog(this) },
    openSignUpDialog() { return _udOpenSignUpDialog(this) },
    openHillshadeDialog() { return _udOpenHillshadeDialog(this) },
    closeSignUpPromo() { return _udCloseSignUpPromo(this) },
    startSignUp() { return _udStartSignUp(this) },
    startLogin() { return _udStartLogin(this) },
    startTour(...args) { return _udStartTour(this, ...args) },
    handleUsageTourNext() { return _udHandleUsageTourNext(this) },
    handleUsageTourSkip() { return _udHandleUsageTourSkip(this) },
    handleUsageTourComplete() { return _udHandleUsageTourComplete(this) },
    /** =========================
     * ファイルドロップ/インポート
     * → fileImport.ts に切り出し済み
     * ========================= */
    migrateLocalStorage() { return _migrateLocalStorage() },
    async handleFlowSelected() { return _handleFlowSelected(this) },
    async handleToukiJump(files) { return _handleToukiJump(this, files) },
    /** =========================
     * 登記簿PDF処理・地番ハイライト
     * → toukiProcessing.ts に切り出し済み
     * ========================= */
    highlightToukiPolygon(...args) { return _highlightToukiPolygon(this, ...args) },
    clearToukiHighlight(...args) { return _clearToukiHighlight(this, ...args) },
    async handleToukiBatchExtract(...args) { return _handleToukiBatchExtract(this, ...args) },
    async handleToukiAll(...args) { return _handleToukiAll(this, ...args) },
    getMapSpeedOverrides(profile) { return _mdGetMapSpeedOverrides(this, profile) },
    applyMapSpeedProfile(profile) { return _mdApplyMapSpeedProfile(this, profile) },
    onPaintUpdate(...args) { return _umOnPaintUpdate(this, ...args) },
    // 地図フォーカス → mapFocus.ts
    maybeFocusCenterIncludePoint(a, b, opts) { return _maybeFocusCenterIncludePoint(this, a, b, opts) },
    focusCenterIncludePoint(center, target, opts) { return _focusCenterIncludePoint(this, center, target, opts) },
    installTouchSuppressor() { return _installTouchSuppressor(this) },
    openKyusekiDialog() { return _mfOpenKyusekiDialog(this) },
    // 求積表（トークンブロック付き）
    async openKyusekiDialogWithToken() {
      const r = await consumeToken('kyuseki_ocr')
      if (!r.success && r.error !== 'NETWORK_ERROR_IGNORED') return
      return _mfOpenKyusekiDialog(this)
    },
    onKyusekiDialogVisibilityChange(v) { return _mfOnKyusekiDialogVisibilityChange(this, v) },
    closeKyusekiWindow() { return _mfCloseKyusekiWindow(this) },
    openHandwriteDialog() { return _mfOpenHandwriteDialog(this) },
    onImported(payload) { return _mfOnImported(this, payload) },
    onHandwriteImported(payload) { return _mfOnHandwriteImported(this, payload) },

    // ====== ポイント名のインライン編集 ======

    cancelPointNameEdit() { return _umCancelPointNameEdit(this) },
    async updatePointNameOnServer(...args) { return _umUpdatePointNameOnServer(this, ...args) },
    _stopIntervalAndFinalize() { return _um_stopIntervalAndFinalize(this) },
    async _tickKansoku() { return _um_tickKansoku(this) },

    compassClick() { return _umCompassClick(this) },
    // ジオリファレンス → georeferencing.ts
    _applyAffine(M, pt) { return __applyAffine(this, M, pt) },
    generateWorldFile(opts) { return _generateWorldFile(this, opts) },
    onWarpConfirm(data) { return _onWarpConfirm(this, data) },
    _validCorners(c) { return __validCorners(this, c) },
    _worldFileFromAffine(arr) { return __worldFileFromAffine(this, arr) },
    onWarpWindowClose() { return _onWarpWindowClose(this) },
    clearBlueDotsOnMap() { return _clearBlueDotsOnMap(this) },
    onConfirmOk() { return _udOnConfirmOk(this) },
    onConfirmCancel() { return _udOnConfirmCancel(this) },
    onConfirmMiddle() { return _udOnConfirmMiddle(this) },
    mapillaryFilterOpen() { return _umMapillaryFilterOpen(this) },
    openZoomInfo() { return _umOpenZoomInfo(this) },
    togglePegman() {
      // トークン/ログインチェック
      const userId = this.$store.state.userId
      if (!userId) {
        this.$store.state.tokenLoginRequired = true
        return
      }
      const tb = this.$store.state.tokenBalance
      if (tb && !tb.is_unlimited && tb.point_balance <= 0) {
        this.$store.state.tokenLimitExceeded = {
          featureKey: 'street_view',
          tokenCost: 1,
          pointBalance: tb.point_balance,
        }
        return
      }
      const flg = !this.$store.state.streetViewFlg
      this.$store.state.streetViewFlg = flg
      localStorage.setItem('streetView', flg)
      const map = this.$store.state.map01
      if (!map) return
      // カバレッジレイヤーが未追加なら追加（表示状態に合わせて作成）
      if (!map.getSource('street-view-coverage')) {
        map.addSource('street-view-coverage', {
          type: 'raster',
          tiles: ['https://mts1.googleapis.com/vt?hl=ja&lyrs=svv|cb_client:apiv3&style=40,18&x={x}&y={y}&z={z}'],
          tileSize: 256,
          maxzoom: 21,
        })
        map.addLayer({
          id: 'street-view-coverage-layer',
          type: 'raster',
          source: 'street-view-coverage',
          layout: { visibility: flg ? 'visible' : 'none' },
          paint: { 'raster-opacity': 0.7 },
        })
      } else if (map.getLayer('street-view-coverage-layer')) {
        map.setLayoutProperty('street-view-coverage-layer', 'visibility', flg ? 'visible' : 'none')
      }
      // 常に最上位に
      if (flg && map.getLayer('street-view-coverage-layer')) {
        map.moveLayer('street-view-coverage-layer')
      }
      if (!flg) {
        this.$store.dispatch('hideFloatingWindow', 'street-view-window')
      }
    },
    fullscreenForIframe() { return _udFullscreenForIframe(this) },
    waitForMap(...args) { return _udWaitForMap(this, ...args) },
    // 地図回転・ピッチ・地形 → mapOrientationControl.ts
    targets(mapName, syncBoth) { return _targets(this, mapName, syncBoth) },
    hillshadeStop() { return _hillshadeStop(this) },
    pressRotateRight(mapName, opts) { return _pressRotateRight(this, mapName, opts) },
    pressRotateLeft(mapName, opts) { return _pressRotateLeft(this, mapName, opts) },
    pressPitchUp(mapName, opts) { return _pressPitchUp(this, mapName, opts) },
    pressPitchDown(mapName, opts) { return _pressPitchDown(this, mapName, opts) },
    releaseOrientationButtons() { return _releaseOrientationButtons(this) },
    terrainReset(mapName) { return _terrainReset(this, mapName) },
    drawToolSet(...args) { return _drawToolSet(this, ...args) },
    mapillaryClose() { return _umMapillaryClose() },
    addressSearchClose() { return _umAddressSearchClose(this) },
    clearAllAddressMarkers() { return _umClearAllAddressMarkers(this) },
    zoomInfoClose() { return _umZoomInfoClose() },

    // 価格推移表FW関連
    openPriceHistoryWindow(...args) { return _umOpenPriceHistoryWindow(this, ...args) },

    closePriceHistoryWindow(...args) { return _umClosePriceHistoryWindow(this, ...args) },

    onPriceHistoryPositionChange(...args) { return _umOnPriceHistoryPositionChange(this, ...args) },

    downloadPriceHistoryCsv(...args) { return _umDownloadPriceHistoryCsv(this, ...args) },

    onTimeSliderClose() { return _umOnTimeSliderClose() },
    // 今昔マップレイヤーがあればタイムスライダーを自動で開く
    openTimeSliderIfKonjyakuExists() { return _umOpenTimeSliderIfKonjyakuExists(this) },
    drawListOpen(...args) { return _drawListOpen(this, ...args) },
    drawOpen(...args) { return _drawOpen(this, ...args) },
    popupDialogClose() { return _umPopupDialogClose(this) },
    isDisabledChange() { return _umIsDisabledChange(this) },
    drawGeojsonAddPicture(...args) { return _drawGeojsonAddPicture(this, ...args) },
    triggerCamera() { return _umTriggerCamera() },
    onCameraPicked(...args) { return _umOnCameraPicked(this, ...args) },
    // 写真アップロード・削除 → mediaUploadHandlers.ts
    async savePicture() { return _savePicture(this) },
    async deletePicture() { return _deletePicture(this) },
    onFileSelected() { return _onFileSelected(this) },
    async test() { return _umTest(this) },
    openPaintEditorWindow() { return _umOpenPaintEditorWindow(this) },
    onWidthChangedForMapillary() { return _umOnWidthChangedForMapillary() },
    onWidthChanged(...args) { return _umOnWidthChanged(this, ...args) },
    openWindow() { return _umOpenWindow(this) },
    /** =========================
     * オフラインキャッシュ
     * → offlineCache.ts に切り出し済み
     * ========================= */
    async cachesCrear() { return _cachesCrear() },
    async downloadTiles() { return _downloadTiles(this) },
    bboxPolygon() { return _bboxPolygon(this) },
    setMaxZIndex(...args) { return _umSetMaxZIndex(this, ...args) },
    // ─── 連続拡大 ───
    startScaleUp() { return _umStartScaleUp(this) },
    stopScaleUp() { return _umStopScaleUp(this) },
    // ─── 連続縮小 ───
    startScaleDown() { return _dmStartScaleDown(this) },
    stopScaleDown() { return _dmStopScaleDown(this) },
    // ─── スライダーや入力ボックス連動用 ───
    onScaleInput(...args) { return _dmOnScaleInput(this, ...args) },
    // ---- 編集モード 確定/元に戻す（サーバー同期付き） ----
    onEditConfirm() { return _dmOnEditConfirm(this) },
    onEditRevert() { return _dmOnEditRevert(this) },
    // 投げ縄変形・編集モード → lassoTransformControl.ts
    async persistAndExitEditMode(restorePrev) { return _persistAndExitEditMode(this, restorePrev) },
    startAnglePlus() { return _startAnglePlus(this) },
    stopAnglePlus() { return _stopAnglePlus(this) },
    startAngleMinus() { return _startAngleMinus(this) },
    stopAngleMinus() { return _stopAngleMinus(this) },
    onRotateInput(angle) { return _onRotateInput(this, angle) },
    resetLassoTransform() { return _resetLassoTransform(this) },
    closeLassoControl() { return _closeLassoControl(this) },
    exitLassoMode() { return _exitLassoMode(this) },
    showLassoCloseConfirmDialog() { return _showLassoCloseConfirmDialog(this) },
    confirmLassoClose() { return _confirmLassoClose(this) },
    cancelLassoMode() { return _cancelLassoMode(this) },
    async restoreLassoChanges() { return _restoreLassoChanges(this) },
    // ─── 複製モードを終了 ───
    exitDuplicateMode() { return _dmExitDuplicateMode(this) },
    // ─── 編集内容を元に戻す（編集モードは継続） ───
    revertEditChanges() { return _dmRevertEditChanges(this) },
    /** =========================
     * ラッソ操作
     * → lassoOperations.ts に切り出し済み
     * ========================= */
    startLassoBulkMove() { return _startLassoBulkMove(this) },
    getCheckColor(colorValue) { return _getCheckColor(this, colorValue) },
    applyLassoBulkColor(colorName) { return _applyLassoBulkColor(this, colorName) },
    applyLassoCustomColor() { return _applyLassoCustomColor(this) },
    async confirmLassoBulkDelete() { return _confirmLassoBulkDelete(this) },
    executeLassoBulkDelete() { return _executeLassoBulkDelete(this) },
    async confirmLassoDeleteOthers() { return _confirmLassoDeleteOthers(this) },
    executeLassoDeleteOthers() { return _executeLassoDeleteOthers(this) },
    colorNameToRgba(colorName, opacity = 0.5) { return _colorNameToRgba(colorName, opacity) },
    // Keyboard shortcuts for drawing
    /** キーボードショートカット → keyboardShortcuts.ts */
    onKeydown(e) { return _onKeydown(this, e) },

    /** =========================
     * アップロード処理
     * → uploadHandlers.ts に切り出し済み
     * ========================= */
    uploadChibanzu() { return _uploadChibanzu(this) },
    async uploadMyroomKml() { return _uploadMyroomKml(this) },
    uploadDrawKml() { return _uploadDrawKml(this) },
    async uploadDrawCsv() { return _uploadDrawCsv(this) },
    async uploadMyLayer() { return _uploadMyLayer(this) },
    uploadDraw(...args) { return _uploadDraw(this, ...args) },
    uploadDrawGeojson(...args) { return _uploadDrawGeojson(this, ...args) },
    /** =========================
     * タイリング・複製・マーカー・データ作成
     * → tileAndDuplicate.ts に切り出し済み
     * ========================= */
    startTiling() { return _startTiling(this) },
    openTileUploadDialog(payload) { return _openTileUploadDialog(this, payload) },
    updateMapMarkers() { return _updateMapMarkers(this) },
    onMapClick(e) { return _onMapClick(this, e) },
    test0() { return _test0(this) },
    executeDuplicate(lng, lat) { return _executeDuplicate(this, lng, lat) },
    calculateCentroid(coordinates) { return _calculateCentroid(coordinates) },
    createStampMarkerForDuplicate(newId, coords, props, map) { return _createStampMarkerForDuplicate(this, newId, coords, props, map) },
    createElevationFromFeature(feature) { return _createElevationFromFeature(this, feature) },
    elevationCreate() { return _elevationCreate(this) },
    pointCsvCreate() { return _pointCsvCreate(this) },
    pointSimaCreate() { return _pointSimaCreate(this) },
    /** =========================
     * ドロー保存・エクスポート・履歴
     * → drawSaveHistory.ts に切り出し済み
     * ========================= */
    dialogForDlOpen() { return _dialogForDlOpen(this) },
    dialogForSaveDXFOpen() { return _dialogForSaveDXFOpen(this) },
    saveGpx() { return _saveGpx(this) },
    async saveSima() { return _saveSima(this) },
    saveGeojson() { return _saveGeojson(this) },
    saveKml() { return _saveKml(this) },
    saveDxf() { return _saveDxf(this) },
    execVisibleDxfExport() { return _execVisibleDxfExport(this) },
    saveHistory() { return _saveHistory(this) },
    async undo() { return _undo() },
    async redo() { return _redo() },
    finishLine() { return _finishLine(this) },
    endDraw(...args) { return _endDraw(this, ...args) },
    // floating-buttons ドラッグ機能
    // フローティングボタン → floatingButtonsControl.ts
    startFloatingButtonsDrag(e) { return _startFloatingButtonsDrag(this, e) },
    onFloatingButtonsDrag(e) { return _onFloatingButtonsDrag(this, e) },
    endFloatingButtonsDrag() { return _endFloatingButtonsDrag(this) },
    resetFloatingButtonsPos() { return _resetFloatingButtonsPos(this) },
    // スクリーンキャプチャ → screenCapture.ts
    openDialog() { return _openDialog(this) },
    async captureAndPreview() { return _captureAndPreview(this) },
    async deleteAllforDraw() { return _dmDeleteAllforDraw(this) },
    /** =========================
     * 印刷・PDF・PNGエクスポート
     * → printExport.ts に切り出し済み
     * ========================= */
    pdfDl() { return _pdfDl(this) },
    executePdfDl() { return _executePdfDl(this) },
    openPdfPrintOptionsDialog() { return _openPdfPrintOptionsDialog(this) },
    confirmPdfDl() { return _confirmPdfDl(this) },
    pngDl0() { return _pngDl0(this) },
    print() { return _print(this) },
    s_printMap01() { return _s_printMap01(this) },
    s_printMap02() { return _s_printMap02(this) },
    s_printMap03() { return _s_printMap03(this) },
    startPrintSync() { return _startPrintSync(this) },
    stopPrintSync() { return _stopPrintSync(this) },
    applyPrintMap03Layout() { return _applyPrintMap03Layout(this) },
    setPrintZoomOffset(offset) { return _setPrintZoomOffset(this, offset) },
    handlePrint(...args) { return _handlePrint(this, ...args) },
    getScreenConfiguration() { return _getScreenConfiguration(this) },
    adjustPositionForScreenChange(...args) { return _adjustPositionForScreenChange(...args) },
    updateScaleDisplay(...args) { return _updateScaleDisplay(this, ...args) },
    initScaleDraggable() { return _initScaleDraggable(this) },
    // 4画面モード同期用（iframeからの利用時）
    setup4ScreenSync(...args) { return _setup4ScreenSync(this, ...args) },
    directionChange() { return _dmDirectionChange(this) },
    toggleLDraw(...args) { return _toggleLDraw(this, ...args) },
    drawFix(...args) { return _drawFix(this, ...args) },
    toggleDrawFix(...args) { return _toggleDrawFixMethod(this, ...args) },
    toggleEditEnabled() { return _dmToggleEditEnabled(this) },

    toggleDrawLasso() { return _dmToggleDrawLasso(this) },
    toggleLDrawFree() { return _dmToggleLDrawFree(this) },
    // 強化版フリーハンドキャンバスモード
    toggleFreehandCanvas() { return _fcToggleFreehandCanvas(this) },
    onFreehandConfirm(...args) { return _fcOnFreehandConfirm(this, ...args) },
    onFreehandClose() { return _fcOnFreehandClose() },
    // GeoJSONからスタンプマーカーを復元
    restoreStampsFromGeojson(...args) { return _fcRestoreStampsFromGeojson(this, ...args) },
    // スケッチモードを強制終了（他のツールに切り替える時に使用）
    closeFreehandCanvas() { return _fcCloseFreehandCanvas(this) },
    toggleLDrawPolygon() { return _dmToggleLDrawPolygon(this) },
    toggleLDrawLine() { return _dmToggleLDrawLine(this) },
    toggleDrawCircle() { return _dmToggleDrawCircle(this) },
    toggleDrawPoint() { return _dmToggleDrawPoint(this) },
    toggleDrawText() { return _dmToggleDrawText(this) },
    save() { return _foSave(this) },
    ...mapMutations(['setChibanzuDrawer', 'setRightDrawer', 'setPointInfoDrawer', 'saveSelectedPointFeature']),
    ...mapActions(['saveSelectedPointFeatureToFirestore']),
    close() { return _foClose(this) },
    setDrawerVisible(...args) { return _foSetDrawerVisible(this, ...args) },
    setRightVisible(...args) { return _foSetRightVisible(this, ...args) },
    savePointDescription(...args) { return _foSavePointDescription(this, ...args) },
    openFeatureDrawer(...args) { return _foOpenFeatureDrawer(this, ...args) },
    handleDescriptionSave(...args) { return _foHandleDescriptionSave(this, ...args) },
    simaClose() { return _foSimaClose(this) },
    myRoom() { return _foMyRoom(this) },
    updateSnackbar() { return _foUpdateSnackbar(this) },
    onResize() { return _foOnResize(this) },
    simaDelete() { return _foSimaDelete(this) },
    fileClose() { return _foFileClose(this) },
    dxfDl() { return _foDxfDl(this) },
    dxfPartDl() { return _foDxfPartDl(this) },
    dxfDelete() { return _foDxfDelete(this) },
    pngDownload() { return _foPngDownloadFn(this) },
    dxfLoad() { return _foDxfLoad(this) },
    shpLoad() { return _foShpLoad(this) },
    imagePngLoad() { return _foImagePngLoad(this) },
    pdfLoad() { return _foPdfLoad(this) },
    pngLoad0() { return _foPngLoad0(this) },
    jpgLoad0() { return _foJpgLoad0(this) },
    geoTiffLoad0() { return _foGeoTiffLoad0(this) },
    geoTifLoad1file() { return _foGeoTifLoad1file(this) },
    simaOpacityInput() { return _foSimaOpacityInput(this) },
    loadSima() { return _foLoadSima(this) },
    simaPartDl() { return _foSimaPartDl(this) },
    simaDl() { return _foSimaDl(this) },
    simaAddDraw(...args) { return _simaAddDraw(this, ...args) },
    draw(...args) { return _drawToggle(this, ...args) },
    drawConfig(...args) { return _drawConfig(this, ...args) },
    drawShare(...args) { return _drawShare(this, ...args) },
    help() { return _mmHelp() },
    share() { return _mmShare(this) },
    zoomIn() { return _mmZoomIn(this) },
    zoomOut() { return _mmZoomOut(this) },
    /** =========================
     * 測位関連（位置観測の開始・収集・停止・サマリー・保存）
     * ========================= */
    // サーバから現ジョブの点を取得して CSV ダウンロード（ファイル名は JOB名_件数.csv）
    async downloadCsv2() { return _mmDownloadCsv2(this) },
    // SIMA 出力（A01点列のみ。ラインは規格外のため非対応）
    async exportCsv2Sima() { return _mmExportCsv2Sima(this) },
    /** =========================
     * 杭打関連（測位点登録・赤丸表示・ジョブ管理・結線表示）
     * ========================= */

    /** 単点/結線モードの切替（観測中は不可）＋即時更新 */
    setLineMode(...args) { return _mmSetLineMode(this, ...args) },

    // ジョブ管理（Picker/作成/削除/選択/一覧）
    /** ジョブピッカーを開き、サーバ一覧を最新化 */
    async openJobPicker() { return _mmOpenJobPicker(this) },
    // 測位（トークンブロック付き）
    async openJobPickerWithToken() {
      const r = await consumeToken('positioning')
      if (!r.success && r.error !== 'NETWORK_ERROR_IGNORED') return
      return _mmOpenJobPicker(this)
    },

    // 測位点（赤丸）: 設置/クリック/レイヤ管理
    /** マップ中央のゾーンから座標系ラベルを推定し store に反映 */
    zahyoGet() { return _mmZahyoGet(this) },

    startTorokuHere() { return _mmStartTorokuHere(this) },

    /** ジョブ終了処理（クリーンアップ含む） */
    onJobEndClick(...args) { return _mmOnJobEndClick(this, ...args) },

    /** =========================
     * 追跡関連（現在地追跡・距離線・ログ出力）
     * → gpsTracking.ts に切り出し済み
     * ========================= */
    startWatchPosition(...args) { return _startWatchPosition(this, ...args) },
    stopWatchPosition(...args) { return _stopWatchPosition(this, ...args) },
    saveGeoMetrics(...args) { return _saveGeoMetrics(this, ...args) },
    getGeoQualityLabel(...args) { return _getGeoQualityLabel(...args) },
    drawGpsLine(...args) { return _drawGpsLine(this, ...args) },
    attachGpsLineClick(...args) { return _attachGpsLineClick(this, ...args) },
    detachGpsLineClick(...args) { return _detachGpsLineClick(this, ...args) },
    clearGpsLine(...args) { return _clearGpsLine(this, ...args) },
    async watchPosition(...args) { return _watchPositionHandler(this, ...args) },
    async toggleWatchPosition(...args) { return _toggleWatchPosition(this, ...args) },
    requestClearLog(...args) { return _requestClearLog(this, ...args) },
    doClearLog(...args) { return _doClearLog(this, ...args) },
    toggleTrackLog(...args) { return _toggleTrackLog(this, ...args) },
    startTrackLog(...args) { return _startTrackLog(this, ...args) },
    stopTrackLog(...args) { return _stopTrackLog(this, ...args) },
    clearTrackLog(...args) { return _clearTrackLog(this, ...args) },
    maybeLogPoint(...args) { return _maybeLogPoint(this, ...args) },
    exportTrackCsv(...args) { return _exportTrackCsv(this, ...args) },
    exportTrackSima(...args) { return _exportTrackSima(this, ...args) },
    $_jstLocal(...args) { return $_jstLocal(...args) },
    $_jstStamp(...args) { return $_jstStamp(...args) },
    $_toShiftJisBytes(...args) { return $_toShiftJisBytes(...args) },
    $_downloadBlob(...args) { return $_downloadBlob(...args) },
    $_downloadText(...args) { return $_downloadText(...args) },
    updateLocationAndCoordinates(...args) { return _updateLocationAndCoordinates(this, ...args) },
    goToCurrentLocation(...args) { return _goToCurrentLocation(this, ...args) },
    createCurrentLocationPopupContent(...args) { return _createCurrentLocationPopupContent(this, ...args) },
    setupCurrentLocationPopupButtons(...args) { return _setupCurrentLocationPopupButtons(this, ...args) },
    async copyCurrentLocationInfo(...args) { return _copyCurrentLocationInfo(this, ...args) },


    btnPosition() { return _tcBtnPosition(this) },
    terrainBtnClos() { return _tcTerrainBtnClos() },
    // 3Dオフ：テレインOFF、pitch/bearing を 0 にリセット、コントローラー非表示
    terrain3dOff() { return _tcTerrain3dOff(this) },
    terrainBtnExpand() { return _tcTerrainBtnExpand() },
    // 3Dメニューから選択：テレインあり/なしを指定して3D表示
    terrainBtnExpandWithTerrain(...args) { return _tcTerrainBtnExpandWithTerrain(this, ...args) },
    // 標高強調スライダー変更
    terrainLevelInput() { return _tcTerrainLevelInput(this) },
    // 3Dメニューハンドラ（コントローラー内のドロップダウン用）
    handle3dOff(...args) { return _tcHandle3dOff(this, ...args) },
    handle3dNoTerrain(...args) { return _tcHandle3dNoTerrain(this, ...args) },
    handle3dWithTerrain(...args) { return _tcHandle3dWithTerrain(this, ...args) },
    // 3Dメニューハンドラ（FAB用）
    handle3dOffFab() { return _tcHandle3dOffFab(this) },
    handle3dNoTerrainFab() { return _tcHandle3dNoTerrainFab(this) },
    handle3dWithTerrainFab() { return _tcHandle3dWithTerrainFab(this) },
    // 3Dジオラマを開く
    openDiorama3D(...args) { return _tcOpenDiorama3D(this, ...args) },
    // マイルームボタンクリック（ログインチェック付き）
    onMyroomClick(...args) { return _tcOnMyroomClick(this, ...args) },
    btnClickMyroom(...args) { return _tcBtnClickMyroom(this, ...args) },
    async btnClickMenu(...args) { return _msBtnClickMenu(this, ...args) },
    btnClickLayer(...args) { return _msBtnClickLayer(this, ...args) },
    printLayerBtn(mapName) {
      const windowId = 'layer-window-' + mapName
      const currentVisible = this.$store.state.floatingWindows[windowId]

      if (currentVisible) {
        this.$store.dispatch('hideFloatingWindow', windowId)
      } else {
        // handlePrintでdisplay:noneが設定されているので解除
        const layerDialogEl = this.$store.state.dialogs?.layerDialog?.[mapName]
        if (layerDialogEl && layerDialogEl.style) {
          layerDialogEl.style.display = ''
        }
        this.$store.dispatch('showFloatingWindow', windowId)
      }
    },
    // 非表示化（極小化）
    // ボタントグルに組み込み
    btnClickSplit() { return _msBtnClickSplit(this) },
    // マップを表示すべきかどうか
    shouldShowMap(...args) { return _msShouldShowMap(this, ...args) },
    // 画面モードを設定（1画面、2画面、4画面）
    setScreenMode (...args) { return _setScreenMode(this, ...args) },
    // 4画面プリセットを適用
    apply4ScreenPreset (...args) { return _apply4ScreenPreset(this, ...args) },
    // 4画面モードの切り替え
    toggle4ScreenMode () { return _toggle4ScreenMode(this) },
    // 4画面モード: ドラッグ開始
    onMapDragStart(...args) { return _msOnMapDragStart(this, ...args) },
    // 4画面モード: ドラッグオーバー（ドロップ先に入った時）
    onMapDragEnter(...args) { return _msOnMapDragEnter(this, ...args) },
    // 4画面モード: ドラッグリーブ
    onMapDragLeave(...args) { return _msOnMapDragLeave(this, ...args) },
    // 4画面モード: ドロップ（位置入れ替え）
    onMapDrop(...args) { return _msOnMapDrop(this, ...args) },
    // 4画面モード: ドラッグ終了
    onMapDragEnd() { return _msOnMapDragEnd(this) },
    // 4画面モード: マップ位置の入れ替え
    swapMapPositions(...args) { return _msSwapMapPositions(this, ...args) },
    // map03, map04の初期化
    initializeMap03And04(...args) { return _msInitializeMap03And04(this, ...args) },
    // map03, map04にデフォルトレイヤーをセット（空の時のみ）、または既存レイヤーを復帰
    ensureMap03And04Layers() { return _msEnsureMap03And04Layers(this) },
    // 既存のselectedLayersをマップに適用する（4画面復帰時用）
    applyExistingLayersToMap(...args) { return _msApplyExistingLayersToMap(this, ...args) },
    // 指定されたマップにOSMベクターレイヤーをセット（空の時のみ）
    ensureMapLayers(...args) { return _msEnsureMapLayers(this, ...args) },
    // ネイティブ4画面同期のセットアップ
    setupNative4ScreenSync() { return _msSetupNative4ScreenSync(this) },
    // ポインター同期のセットアップ（2画面 / 4画面対応）
    setupPointerSync () { return _setupPointerSync(this) },
    // 4画面モード用のポインター同期を追加セットアップ
    setup4ScreenPointerSync() { return _msSetup4ScreenPointerSync(this) },
    ensureMap02Layers() { return _msEnsureMap02Layers(this) },
    // 2画面切替時: map02の位置・ズームをmap01に合わせる
    // ※ s_map2Flg=true設定前にmap01の位置を取得して渡すこと
    //   (連動モードのsyncハンドラがresize時にmap01を上書きするため)
    syncMap02ToMap01(...args) { return _msSyncMap02ToMap01(this, ...args) },
    // 4画面モード用：一番上のレイヤー名を取得
    getTopLayerLabel(...args) { return _msGetTopLayerLabel(this, ...args) },
    // スワイプ比較モード
    getMapStyle(...args) { return _msGetMapStyle(this, ...args) },
    toggleSwipeMode () { return _toggleSwipeMode(this) },
    // mapSync — 地図同期・スワイプ制御
    startMapSync() { return _startMapSync(this) },
    stopMapSync() { return _stopMapSync(this) },
    toggleMapSyncMode() { return _toggleMapSyncMode(this) },
    syncAllMapsToMap01() { return _syncAllMapsToMap01(this) },
    saveAllMapStates() { return _saveAllMapStates(this) },
    enableSwipePan() { return _enableSwipePan(this) },
    disableSwipePan() { return _disableSwipePan(this) },
    enableSwipeNudge() { return _enableSwipeNudge(this) },
    disableSwipeNudge() { return _disableSwipeNudge(this) },
    enableSwipeManualPan() { return _enableSwipeManualPan(this) },
    disableSwipeManualPan() { return _disableSwipeManualPan(this) },
    forceEnableMapInteractions(...args) { return _forceEnableMapInteractions(this, ...args) },
    setMapInteractions(...args) { return _setMapInteractions(this, ...args) },
    enableSwipeFollow() { return _enableSwipeFollow(this) },
    disableSwipeFollow() { return _disableSwipeFollow(this) },
    addOsmToMap02() { return _addOsmToMap02(this) },
    // AI画像鮮明化 — FloatingWindowを開く
    onAiEnhance() { return _mmOnAiEnhance(this) },
    // ESCキーでスワイプモードと2画面モードの両方を終了（アニメーション付き）
    exitSwipeAndSplit() { return _mmExitSwipeAndSplit(this) },
    startSwipeDrag(...args) { return _mmStartSwipeDrag(this, ...args) },
    /** =========================
     * URL状態管理・パーマリンク
     * → permalinkState.ts に切り出し済み
     * ========================= */
    updatePermalink() { return _updatePermalink(this) },
    restoreGyoseiHistoryFromUrl(map) { return _restoreGyoseiHistoryFromUrl(this, map) },
    async restoreFromParamString(paramString) { return _restoreFromParamString(this, paramString) },
    async restoreUserSettings(opts) { return _restoreUserSettingsPermalink(this, opts) },
    restoreSwipeState(pos) { return _restoreSwipeState(this, pos) },
    cancelShortUrlTimer() { return _cancelShortUrlTimer(this) },
    scheduleShortUrl() { return _scheduleShortUrl(this) },
    createShortUrl() { return _createShortUrl(this) },
    async parseUrlParams() { return _parseUrlParams(this) },
    async init() { return initApp(this) },
  },
  async created() {
    await onCreated(this)
  },
  beforeUnmount() {
    onBeforeUnmount(this)
  },
  mounted() {
    onMounted(this)
    // バナー高さを実測
    this.$nextTick(() => {
      if (this.$refs.paidBanner) {
        this.bannerHeight = this.$refs.paidBanner.offsetHeight
      }
    })
    // 起動時にすでにログイン済みならトークン残高を取得（体験プランバナー用）
    if (this.$store.state.userId) {
      getTokenBalance().catch(() => {})
    }
  },
  watch: {
    // ログイン/ログアウト検知してトークン残高を取得
    userId(newUid, oldUid) {
      if (newUid && newUid !== oldUid) {
        getTokenBalance().catch(() => {})
      } else if (!newUid) {
        // ログアウト時は残高とバナー状態をクリア
        this.$store.state.tokenBalance = null
        this.trialMonthlyUsage = null
      }
    },
    // 体験プラン残量が閾値を下回ったら月間使用量を取得
    trialBannerStage(stage, oldStage) {
      if (stage && !oldStage) {
        this.fetchTrialMonthlyUsage()
      }
      // バナー内容が変わったら高さ再計測
      this.$nextTick(() => {
        if (this.$refs.paidBanner) {
          this.bannerHeight = this.$refs.paidBanner.offsetHeight
        }
      })
    },
    // スマホ時は4画面を強制的に1画面にする（2画面は許可）
    isSmall500(isSmall) {
      if (isSmall && this.$store.state.is4ScreenMode) {
        this.setScreenMode(1)
      }
      // バナーテキスト量が変わるので高さ再計測
      this.$nextTick(() => {
        if (this.$refs.paidBanner) {
          this.bannerHeight = this.$refs.paidBanner.offsetHeight
        }
      })
    },
    // PC状態で表示トグル → bodyクラスで制御
    '$store.state.showPcMode'(val) {
      if (val) {
        document.body.classList.add('show-pc-mode')
      } else {
        document.body.classList.remove('show-pc-mode')
      }
    },
    // ボトムシートモードが変更されたら地図をリサイズ
    bottomSheetActive(active) {
      this.$nextTick(() => {
        // 地図のリサイズを通知
        if (this.$store.state.map01) {
          this.$store.state.map01.resize()
        }
      })
    },
    // 投げ縄コントロールウィンドウが閉じられたらTransform Boxも非表示
    '$store.state.floatingWindows["lasso-control-window"]'(visible) {
      if (!visible) {
        hideTransformBox()
      }
    },
    // shareFeaturesが変更されたら自動的に地図上に表示
    shareFeatures: {
      handler() {
        if (this.mapReady) {
          this.showShareFeaturesOnMap()
        }
      },
      deep: true
    },
    mapReady(value) {
      if (value) {
        this.$nextTick(() => { this.hsRef = this.$refs.hs })
        // URL共有パラメータを処理
        this.handleAiShareUrl()
        // 授業モードの保留中状態を適用
        const lessonMode = this.$store.state.lessonMode
        // 新形式: pendingLessonData（完全状態）
        if (lessonMode?.active && lessonMode?.pendingLessonData) {

          this.applyLessonFullState(lessonMode.pendingLessonData)
          this.$store.state.lessonMode.pendingLessonData = null
        }
        // 旧形式: pendingViewState（後方互換性）
        else if (lessonMode?.active && lessonMode?.pendingViewState) {

          this.applyLessonViewState(lessonMode.pendingViewState)
          this.$store.state.lessonMode.pendingViewState = null
        }
      }
    },
    authUser(newUser) {
      // ログアウト時（null）に新規登録促進ダイアログを再表示
      if (!newUser) {
        this.showSignUpPromoDialog = true
      }
    },
    // 教員の授業同期状態を監視
    '$store.state.activeLesson': {
      handler(newVal, oldVal) {

        if (!newVal && oldVal) {
          // 授業終了 → 同期を停止
          this.stopTeacherMapSync()
          return
        }
        if (newVal && newVal.classId) {
          const prevSyncEnabled = oldVal?.syncEnabled
          const currentSyncEnabled = newVal.syncEnabled
          if (currentSyncEnabled && !prevSyncEnabled) {
            // 同期がONになった → 同期を開始

            this.startTeacherMapSync(newVal.classId)
          } else if (!currentSyncEnabled && prevSyncEnabled) {
            // 同期がOFFになった → 同期を停止

            this.stopTeacherMapSync()
          } else if (currentSyncEnabled && !this.teacherMapSyncHandler) {
            // 同期ONで、まだハンドラがない場合（初回開始）
            this.startTeacherMapSync(newVal.classId)
          }
        }
      },
      deep: true
    },
    // 生徒側の授業同期状態を監視（地図操作の有効/無効を制御）
    '$store.state.lessonMode': {
      handler(newVal, oldVal) {
        if (!newVal) return
        const prevSync = oldVal?.syncEnabled
        const currentSync = newVal.syncEnabled
        const isActive = newVal.active

        if (isActive && currentSync && !prevSync) {
          // 同期ONになった → 生徒の操作を無効化
          this.disableStudentMapInteractions()
        } else if (isActive && !currentSync && prevSync) {
          // 同期OFFになった → 生徒の操作を有効化
          this.enableStudentMapInteractions()
        } else if (!isActive && oldVal?.active) {
          // 授業終了 → 生徒の操作を有効化
          this.enableStudentMapInteractions()
        } else if (isActive && currentSync && !this.studentInteractionsDisabled) {
          // 授業開始時に既に同期ONなら無効化
          this.disableStudentMapInteractions()
        }
      },
      deep: true
    },
    // スナップショット/URL復元時の再同期（教員側）
    '$store.state.snapshotRestoredAt'(newVal) {
      if (!newVal) return
      const activeLesson = this.$store.state.activeLesson
      if (!activeLesson || !activeLesson.syncEnabled) return
      // スナップショット復元後、複数回送信でレイヤー読み込み完了を待つ

      if (this.teacherMapSyncHandler) {
        // 500ms後に1回目
        setTimeout(() => {

          this.teacherMapSyncHandler()
        }, 500)
        // 2秒後に2回目
        setTimeout(() => {

          this.teacherMapSyncHandler()
        }, 2000)
      }
    },
    // ログインダイアログトリガー（他コンポーネントからログインダイアログを開くため）
    '$store.state.loginDialogTrigger'(newVal) {
      if (newVal > 0) {
        if (this.$store.state.initialLoginMode === 'signup') {
          this.openSignUpDialog()
        } else {
          this.openLoginDialog()
        }
      }
    },
    printTitleText() {
      this.updatePrintTitleHeight()
    },
    '$store.state.printTitleTexts': {
      deep: true,
      handler() {
        this.updatePrintTitleHeight()
      }
    },
    textPx() {
      this.updatePrintTitleHeight()
    },
    showKyusekiDialog(v) {
      // 求積表インポートダイアログの開閉をstoreに反映（地図操作ブロック用）
      this.$store.commit('setIsKyusekiDialogOpen', v)
    },
    '$store.state.fileDropFlow.action'(newAction) {
      if (newAction) {
        this.handleFlowSelected();
      }
    },
    mapSpeedProfile(value) {
      this.applyMapSpeedProfile(value)
    },
    isContextMenu(value) {
      if (value) {
        this.detachForContextMenu = attachMapRightClickMenu(this.contextMenuObject);
      } else{
        const isFn = typeof this.detachForContextMenu === 'function'
        if (isFn) {
          this.detachForContextMenu()
          this.detachForContextMenu = null
        }
      }
    },
    s_isDraw(val) {
      // ドローリストは右アイコン群のボタンからのみ開く
      // const isGroupDraw = (this.$store.state.geojsonName || '').startsWith('グループ共有_')
      // if (val && !this.isSmall500 && !isGroupDraw) {
      //   this.$store.commit('setDrawListDrawer', true)
      // }
    },
    // 複製モード時のカーソル変更
    '$store.state.isDrawDuplicate'(isDuplicate) {
      const map = this.$store.state.map01
      if (!map) return
      const canvas = map.getCanvas()
      if (!canvas) return

      if (isDuplicate) {
        // 複製モード時は copy カーソル（!importantを使うためクラスで対応）
        canvas.classList.add('duplicate-mode')
      } else {
        // 通常モードに戻す
        canvas.classList.remove('duplicate-mode')
      }
    },
    // 変更時にローカル保存＆（必要なら）副作用フック
    lineMode(newVal) {
      try { localStorage.setItem('oh3_line_mode', newVal); } catch (e) {}
    },
    async isJobMenu(v) {
      if (v) {
        if (this.jobPickerBusy || this.jobPickerOpen) return;
        this.jobPickerBusy = true;
        try {
          this.jobPickerOpen = true;
          await this.$nextTick();
          this.$refs.jobPicker?.focus?.(); // 任意
        } finally {
          // 連打対策：描画完了後に即解除
          setTimeout(() => { this.jobPickerBusy = false; }, 0);
        }
      } else {
        this.jobPickerOpen = false; // メニュー閉じたらPickerも閉じる
      }
    },
    // 描画モード時にカーソルスタイルを変更
    's_isDrawPolygon'(isDrawing) {
      const canvas = this.$store.state.map01?.getCanvas();
      if (!canvas) return;
      if (isDrawing && this.$store.state.isDraw) {
        canvas.classList.add('is-drawing-polygon');
      } else {
        canvas.classList.remove('is-drawing-polygon');
      }
    },
    's_isDrawLine'(isDrawing) {
      const canvas = this.$store.state.map01?.getCanvas();
      if (!canvas) return;
      if (isDrawing && this.$store.state.isDraw) {
        canvas.classList.add('is-drawing-line');
      } else {
        canvas.classList.remove('is-drawing-line');
      }
    },
    's_isDrawPoint'(isDrawing) {
      const canvas = this.$store.state.map01?.getCanvas();
      if (!canvas) return;
      if (isDrawing && this.$store.state.isDraw) {
        canvas.classList.add('is-drawing-point');
      } else {
        canvas.classList.remove('is-drawing-point');
      }
    },
    's_isDrawCircle'(isDrawing) {
      const canvas = this.$store.state.map01?.getCanvas();
      if (!canvas) return;
      if (isDrawing && this.$store.state.isDraw) {
        canvas.classList.add('is-drawing-circle');
      } else {
        canvas.classList.remove('is-drawing-circle');
      }
    },
    's_isDrawText'(isDrawing) {
      const canvas = this.$store.state.map01?.getCanvas();
      if (!canvas) return;
      if (isDrawing && this.$store.state.isDraw) {
        canvas.classList.add('is-drawing-text');
      } else {
        canvas.classList.remove('is-drawing-text');
      }
    },

    tempLineCoordsGuide: {
      handler: function () {
        const length = dedupeCoords(this.tempLineCoordsGuide).length
        this.confirmBtnColor = 'info'
        if (this.s_isDrawPolygon) {
          if (length >= this.drawTool.minPolygonPoints) {
            this.confirmBtnColor = 'success'
          }
        } else if (this.s_isDrawLine) {
          if (length >= this.drawTool.minLinePoints) {
            this.confirmBtnColor = 'success'
          }
        }
      },
      deep: true
    },
    showDrawConfrim(value) {
      if (value && this.isSmall500) {
        this.drawFix(true)
      } else {
        this.drawFix(false)
      }
      // 描画終了時にユーザーが移動した位置をリセット
      if (!value) {
        this.floatingButtonsUserPos = null
      }
    },
    async s_is360Pic(value) {
      const map01 = this.$store.state.map01
      if (map01.getZoom() < 14) return
      if (value && map01.getLayer('oh-mapillary-images-highlight')) {
        this.$store.commit('setTargetSeq', '')
        map01.setFilter('oh-mapillary-images-highlight', ['==', ['get', 'sequence_id'], this.$store.state.targetSeq]);
        const src = map01.getSource('mly-current-point');
        if (src && src.setData) {
          src.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: [] },
              properties: {}
            }]
          });
        }
        //
        await setFllter360(map01)
      } else {
        this.$store.commit('setFilter360', null)
        this.$store.commit('setTargetSeq', null)
        map01.setFilter('oh-mapillary-images', this.$store.state.filter360)
        map01.setPaintProperty('oh-mapillary-images', 'circle-color', '#35AF6D');
        await setFllter360(map01)
      }
    },
    isDraw(value) {
      // 🔍 デバッグログ: isDraw watcher発火
      const debugSource = this.map01?.getSource('click-circle-source')
      const debugGeojson = debugSource ? this.getSourceData(debugSource) : null

      this.toggleLDraw()
      if (value) {
        // isDraw時はピッチと回転を0にする
        if (this.map01 && !this.$store.state.suppressDrawPitchReset) {
          this.map01.setPitch(0)
          this.map01.setBearing(0)
        }
        // スタンプマーカーを復元（遅延を持たせてソースデータの読み込みを待つ）
        setTimeout(() => {
          this.restoreStampsFromGeojson()
        }, 500)
        // if (this.map01.getLayers('click-circle-layer-ex')) {
        //   this.map01.removeLayer('click-circle-layer-ex')
        // }
      } else {
        // if (!this.map01.getLayers('click-circle-layer-ex')) {
        //   this.map01.addLayer(clickCircleLayerEx)
        // }
        const source = this.map01.getSource('click-circle-source');
        const geojson = this.getSourceData(source);
        if (geojson && geojson.features) {
          geojson.features.forEach(f => {
            delete f.properties.lassoSelected
          })
          this.s_isLassoSelected = false
          this.map01.getSource('click-circle-source').setData(geojson)
          this.$store.commit('setClickCircleGeojsonText', JSON.stringify(geojson))
          this.$store.commit('toggleUpdatePermalinkFire')
        }
      }
    },
    s_dialogForPicture() {
      this.dialogForPictureZindex = getNextZIndex()
    },
    drawFeature() {
      const borderRadius = this.drawFeature.properties.borderRadius ?? '10px'
      const containerSize = this.drawFeature.properties.containerSize ?? 100
      this.thumbnailType = {borderRadius, containerSize}
      this.containerColor = this.drawFeature.properties.containerColor ?? 'white'
    },
    s_isPrint (value) {
      const app = document.getElementById('app');
      if (!value) {
        const map00Div = document.getElementById('map00');
        map00Div.style.marginTop = '0px'
        app.style.overflow = 'hidden'
        // バナー高さを再計測
        this.$nextTick(() => {
          if (this.$refs.paidBanner) {
            this.bannerHeight = this.$refs.paidBanner.offsetHeight
          }
        })
      } else {
        app.style.overflow = 'auto'
      }
      markaersRemove()
      markerAddAndRemove(true)
    },
    clickCircleGeojsonText (value) {

      if (!this.$store.state.isApplyingHistory) {
        const shouldSeed =
          !this.$store.state.isUsingServerGeojson &&
          this.$store.state.prevGeojsons?.length === 0 &&
          this.$store.state.historyCursor < 0 &&
          typeof value === 'string' &&
          value.trim() !== ''
        if (shouldSeed) {
          seedInitialSnapshot(value)
        } else {
          pushSnapshotDebounced(value);
        }
      }

      try {
        if (JSON.parse(value).features.filter(f => f.properties.id !== 'config').length > 1) {
        } else {
        }
      }catch (e) {

      }
      this.updatePermalink()
    },
    gcpList: {
      deep: true,
      handler() {
        this.updateMapMarkers();
      }
    },
    s_editEnabled (value) {
      const map01 = this.$store.state.map01
      if (value) {
        let geojsonText = this.$store.state.clickCircleGeojsonText
        if (!geojsonText) {
          geojsonText = this.$store.state.clickCircleGeojsonTextMyroom
          this.$store.commit('setClickCircleGeojsonText', this.$store.state.clickCircleGeojsonTextMyroom)
        }
        if (geojsonText) {
          const geojson = JSON.parse(geojsonText)
          const vertexPointsGeojson = getAllVertexPoints(map01, geojson)
          vertexSource.obj.data = vertexPointsGeojson
          setAllMidpoints(map01, geojson)
        } else {
          // ここで watcher を抜ける
          // this.s_editEnabled = false
          return
        }
      } else {
        // 編集モード終了時：確実に全ハンドラーをクリア
        getAllVertexPoints(map01)
        setAllMidpoints(map01)

        // 念のため直接クリアも実施（関数が失敗した場合の保険）
        try {
          const vertexSrc = map01.getSource('vertex-source')
          if (vertexSrc) vertexSrc.setData({ type: 'FeatureCollection', features: [] })

          const midpointSrc = map01.getSource('midpoint-source')
          if (midpointSrc) midpointSrc.setData({ type: 'FeatureCollection', features: [] })

          const dragHandlesSrc = map01.getSource('drag-handles-source')
          if (dragHandlesSrc) dragHandlesSrc.setData({ type: 'FeatureCollection', features: [] })

        } catch (e) {
          console.warn('ハンドラークリアでエラー:', e)
        }
      }
      // ----------------------------------------------------------
      const vm = this
      updateDragHandles(vm.s_editEnabled)
    },
    // 配列で監視（いずれかが変化したら発動）
    s_isDrawAll() {
      // computedの結果を監視する形に
      if (this.s_isDrawAll) {
        // しばらく中止
        // this.startDraw();
      } else {
        this.endDraw();
      }
    },
    // permalink更新トリガー統合: 5つの個別watcherを1つに集約
    // updatePermalinkFire, isCrossSection3D, isDiorama3D, elevationGeojson, isWindow
    permalinkTriggerKey() {
      try { this.updatePermalink() } catch (e) {}
    },
    s_loading(val) {
      this.updateSnackbar();
    },
    s_loading2(val) {
      this.updateSnackbar();
    },
    s_loading3(val) {
      this.updateSnackbar();
    },
    // shareFeatures再表示トリガー（Dialog-layerのaddLayers後に発火）
    '$store.state.refreshShareFeaturesFire'() {
      if (this.shareFeatures.length > 0) {
        this.showShareFeaturesOnMap()
      }
    },
    swipeMode () {
      this.updatePermalink()
    },
    swipePosition () {
      if (this.swipeMode) {
        this.updatePermalink()
      }
    },
    pitch: {
      handler: function () {
        // this.updatePermalink()
        // this.$store.state.map01.setTerrain({ 'source': 'gsidem-terrain-rgb', 'exaggeration': this.s_terrainLevel })
      },
      deep: true
    },
  }
}
</script>
<style scoped>
/* 免責表示フッター */
.disclaimer-footer {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background: transparent;
  color: rgba(0, 0, 0, 0.5);
  text-shadow: 0 0 8px #fff, 0 0 8px #fff;
  font-size: 11px;
  padding: 4px 16px;
  z-index: 1000;
  cursor: pointer;
  white-space: nowrap;
}
.disclaimer-footer:hover {
  color: rgba(0, 0, 0, 0.7);
}
.footer-links {
  margin-left: 12px;
}
.footer-links a {
  color: rgba(0, 0, 0, 0.45);
  text-decoration: none;
  margin-left: 8px;
  font-size: 10px;
}
.footer-links a:hover {
  color: rgba(0, 0, 0, 0.7);
  text-decoration: underline;
}

/* 免責表示フェードインアニメーション */
.disclaimer-fade-enter-active {
  transition: opacity 1.5s ease;
}
.disclaimer-fade-enter-from {
  opacity: 0;
}

#map00 {
  background-color: rgb(240, 242, 245);
  height: 100%;
  position: relative;
}
#map01 {
  /* border: #000 1px solid; */
  height: 100%;
}
#map01.oh-map-motion {
  position: relative;
}
#map01.oh-map-motion,
#map02.oh-map-motion {
  transition: filter 160ms ease;
  overflow: hidden;
}
#map01.oh-map-motion::after,
#map02.oh-map-motion::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 70%);
  transition: opacity 160ms ease;
}
#map01.oh-map-motion--active,
#map02.oh-map-motion--active {
  filter: saturate(1.05) brightness(1.02);
}
#map01.oh-map-motion--active::after,
#map02.oh-map-motion--active::after {
  opacity: 1;
}
/* モノクロフィルター — canvas要素だけに適用（FW等のUIに影響させない） */
#map01.oh-sketch-mode :deep(.maplibregl-canvas),
#map02.oh-sketch-mode :deep(.maplibregl-canvas),
#map03.oh-sketch-mode :deep(.maplibregl-canvas),
#map04.oh-sketch-mode :deep(.maplibregl-canvas) {
  filter: grayscale(1);
  transition: filter 300ms ease;
}
#map01 :deep(.v-overlay) {
  background-color: transparent !important;
}

/*地球状態になったとき白くなる現象を下記で回避*/
#map01 :deep(.v-locale--is-ltr) { background-color: transparent !important; }


#map02 {
  /* border: #000 1px solid; */
  position:absolute;
}

#map03, #map04 {
  position: absolute;
}

/* 4画面モード時のmap01 */
.is-4screen #map01 {
  position: absolute;
}

/* 4画面モード時はattributionアイコン（iボタン）を非表示 */
.is-4screen .maplibregl-ctrl-attrib-button {
  display: none !important;
}
/* スマホ（500px以下）時 & PC状態でない場合はattributionアイコン（iボタン）を非表示 */
@media (max-width: 500px) {
  body:not(.show-pc-mode) .maplibregl-ctrl-attrib-button {
    display: none !important;
  }
  body:not(.show-pc-mode) .maplibregl-ctrl-attrib {
    font-size: 10px;
  }
}

/* 4画面モード用ドラッグハンドル */
.map-drag-handle {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: var(--oh3-radius-base);
  padding: var(--oh3-spacing-1);
  cursor: grab;
  z-index: 10;
  transition: all 0.2s ease;
  opacity: 0.6;
}
.map-drag-handle:hover {
  background: rgba(0, 0, 0, 0.7);
  opacity: 1;
  transform: scale(1.1);
}
.map-drag-handle:active,
.map-drag-handle.dragging {
  cursor: grabbing;
  opacity: 0.8;
  transform: scale(0.95);
}
/* ドロップ先ハイライト */
#map01.drag-over,
#map02.drag-over,
#map03.drag-over,
#map04.drag-over {
  outline: 3px dashed var(--main-color, #1976d2);
  outline-offset: -3px;
}

/* スワイプ比較モード */
.swipe-slider-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 100;
}

.swipe-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40px;
  transform: translateX(-50%);
  cursor: ew-resize;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.swipe-handle::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  background: linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0.8));
  box-shadow: 0 0 10px rgba(0,0,0,0.5);
  z-index: 1;
}

.swipe-handle-knob {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  border-radius: var(--oh3-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--oh3-shadow-strong);
  flex-shrink: 0;
  transition: transform 0.15s ease;
  z-index: 2;
}

.swipe-handle-knob:hover {
  transform: scale(1.1);
}

.swipe-close-btn {
  position: absolute !important;
  top: 10px;
  left: 50%;
  transform: translate(-50%, 0) !important;
  transition: none !important;
  pointer-events: auto;
  box-shadow: var(--oh3-shadow-strong);
}

/* グローバルの v-btn ホバー変形を打ち消す */
.swipe-close-btn.v-btn,
.swipe-close-btn.v-btn:hover,
.swipe-close-btn.v-btn:active {
  transform: translate(-50%, 0) !important;
  transition: none !important;
  box-shadow: var(--oh3-shadow-strong);
}

.map02-layer-btn {
  position: absolute !important;
  top: 10px;
  right: 10px;
  z-index: 101;
  pointer-events: auto;
}

.map02-layer-btn .v-btn {
  box-shadow: var(--oh3-shadow-strong);
}

/* 他マップのカーソル位置表示（十字線） */
.pointer {
  position: absolute;
  pointer-events: none;
  z-index: 10;
  display: none;
  transform: translate(-50%, -50%);
}
.pointer::before,
.pointer::after {
  content: '';
  position: absolute;
  background-color: rgba(30, 144, 255, 0.9);
}
/* 縦線 */
.pointer::before {
  width: 2px;
  height: 20px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
/* 横線 */
.pointer::after {
  width: 20px;
  height: 2px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
#iframe-div{
  position:absolute;
  top:10px;
  left:10px;
  z-index:3;
}
.center-target{
  position: absolute;
  background-image:url('@/assets/images/target.gif');
  background-repeat:  no-repeat;
  width:24px;
  height:24pX;
  pointer-events: none;
  top: calc(50% - 12px);
  left: calc(50% - 12px);
  z-index: 1;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.center-target-adjusted{
  /* 小画面でレポートダイアログ表示中：上40%の中心（20%の位置）に調整 */
  top: calc(20% - 12px) !important;
}
.center-target-adjusted-50{
  /* 小画面でオフラインダイアログ表示中：上50%の中心（25%の位置）に調整 */
  top: calc(25% - 12px) !important;
}
.center-target-bottom-sheet{
  /* ボトムシートモード時：map01（上半分）の中央に調整 */
  top: calc(50% - 12px) !important;
}
.center-target-drawer-right{
  /* デスクトップで右ドロワー（400px）表示中：地図の可視中心に調整 */
  left: calc(50% - 200px - 12px) !important;
  transition: left 0.3s;
}
.center-target-mlit-drawer{
  /* デスクトップでGQLチャットドロワー（500px）表示中：地図の可視中心に調整 */
  left: calc(50% - 250px - 12px) !important;
  transition: left 0.3s;
}
.center-target-mlit-drawer-sm{
  /* 小画面でGQLチャット下ドロワー（60vh）表示中：上部40%の中心（20%の位置）に調整 */
  top: calc(20% - 12px) !important;
  transition: top 0.3s;
}
#left-top-div {
  position:absolute;
  top:10px;
  left:10px;
  z-index:1005; /* FreehandCanvas(1000)より上に */
}
#left-top-div-row2 {
  position:absolute;
  top:68px;
  left:10px;
  z-index:1005;
}
/* 教員同期中のハートビートアニメーション */
.teacher-sync-pulse {
  animation: teacher-pulse 1.5s ease-in-out infinite;
}
@keyframes teacher-pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(229, 57, 53, 0.7);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 0 8px rgba(229, 57, 53, 0);
  }
}
#right-top-div {
  position:absolute;
  top:10px;
  right:10px;
  z-index:1005; /* FreehandCanvas(1000)より上に */
}
.zoom-div {
  position: absolute;
  left: 10px;
  bottom: 10px;
  font-size: large;
  z-index: 2;
  text-shadow:
      -1px -1px 0 #ffffff,
      1px -1px 0 #ffffff,
      -1px  1px 0 #ffffff,
      1px  1px 0 #ffffff;
  pointer-events: auto;
  user-select: text;
  cursor: default;
}
.zoom-div .oaza-yomi {
  font-size: 0.85em;
  opacity: 0.8;
}
/* スマホ時は地図操作を優先 */
.zoom-div.zoom-div-passthrough {
  pointer-events: none;
  user-select: none;
}

/* ストリートビューペグマンボタン */
.pegman-btn {
  position: absolute;
  left: 10px;
  bottom: 120px;
  z-index: 3;
  opacity: 0.9;
  box-shadow: var(--oh3-shadow-sm);
  background: var(--oh3-color-surface, #fff) !important;
}

.pegman-btn:hover {
  opacity: 1;
}

.pegman-btn--active {
  background: var(--oh3-color-primary-light, #e3f2fd) !important;
  box-shadow: 0 0 0 2px #FBBC04, var(--oh3-shadow-sm);
}

.pegman-icon {
  width: 22px;
  height: 28px;
}

/* 画面中心情報ボタン */
.zoom-info-btn {
  position: absolute;
  left: 10px;
  bottom: 70px;
  z-index: 3;
  opacity: 0.9;
  box-shadow: var(--oh3-shadow-sm);
}

.zoom-info-btn:hover {
  opacity: 1;
}

.current-position {
  position: absolute;
  top: 0px;
  left: -50px;
}
.watch-position {
  position: absolute;
  top: 60px;
  left: 0px;
}
.zoom-in {
  position: absolute;
  top: 120px;
  left: 0;
}
.zoom-out {
  position: absolute;
  top: 180px;
  left: 0;
}
.share {
  position: absolute;
  /*top: 240px;*/
  top: 120px;
  left: -50px;
}
.share-x {
  position: absolute;
  /*top: 240px;*/
  top: 300px;
  left: 0;
}
.draw {
  position: absolute;
  /*top: 300px;*/
  top: 180px;
  left: 0;
}
.draw-circle {
  position: absolute;
  top: 240px;
  left: 0;
}
.draw-point {
  position: absolute;
  top: 300px;
  left: 0;
}
.printer {
  position: absolute;
  top: 240px;
  left: 0;
}
/*3Dのボタン-------------------------------------------------------------*/
.terrain-btn-expand-div {
  position:absolute;
  bottom: 24px;
  right: 10px;
  z-index:1010;
  display: block;
}

.floating-action-btn {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  min-height: 40px !important;
  background-color: rgb(var(--v-theme-primary, 25, 118, 210)) !important;
  box-shadow: var(--oh3-shadow-strong);
}

.floating-action-btn .v-icon {
  color: white !important;
  font-size: 22px;
}
.terrain-btn {
  background-color: var(--oh3-color-primary);
}
.terrain-btn-div{
  position:absolute;
  /*top:60%;*/
  bottom: 20px;
  right:100px;
  z-index:1010;
  display:none;
  height:180px;
  width:180px;
  margin-left:-90px;
  margin-top:20px;
  background:rgba(0,0,0,0.1);
  /*border-radius:180px 0 0 180px;*/
  border-radius:180px;
  -moz-user-select:none;
  -webkit-user-select:none;
  -ms-user-select:none;
  cursor:move;
}
/*@media screen and (max-width:480px) {*/
/*  .terrain-btn-div {*/
/*    top:calc(50% - 73px);*/
/*  }*/
/*}*/
.terrain-btn-container{
  position:relative;
  height:100%;
}
.terrain-btn-close {
  position: absolute;
  top:25px;
  left:25px;
  font-size: x-large;
  color: var(--oh3-color-primary);
  cursor: pointer;
}
.terrain-btn-3d-label {
  position: absolute;
  top: 25px;
  right: 30px;
  font-size: large;
  font-weight: bold;
  color: var(--oh3-color-primary);
  cursor: pointer;
}
.terrain-btn-3d-label:hover {
  color: var(--oh3-color-link);
}
.terrain-menu {
  min-width: 200px;
  position: relative;
}
.terrain-menu-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  cursor: pointer;
  opacity: 0.6;
}
.terrain-menu-close:hover {
  opacity: 1;
}
/* 3Dドロップダウンメニュー */
.terrain-3d-menu-wrapper {
  position: relative;
}
.terrain-3d-dropdown {
  position: absolute;
  bottom: 100%;
  top: auto;
  right: 0;
  margin-bottom: 8px;
  background: #fff;
  border-radius: var(--oh3-radius-lg);
  box-shadow: var(--oh3-shadow-strong);
  min-width: 200px;
  padding: 8px 0;
  z-index: 9999;
}
.terrain-3d-dropdown-fab {
  bottom: 100%;
  top: auto;
  right: 0;
  margin-bottom: 8px;
}
.terrain-3d-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: var(--oh3-font-size-base);
  transition: background 0.15s;
}
.terrain-3d-item:hover {
  background: rgba(0,0,0,0.04);
}
.terrain-3d-item.active {
  color: var(--oh3-color-primary);
  font-weight: 600;
}
.terrain-slider-container {
  padding: 8px 16px 12px;
}
.terrain-slider-label {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-sub);
  margin-bottom: 4px;
}
.terrain-btn-up{
  position:absolute;
  top:10px;
  left:50%;
  padding:0;
  width: 50px;
  min-width: 50px;
  height:50px!important;
  margin-left:-25px;
  color: white;
  border-radius: var(--oh3-radius-lg);
}
.terrain-btn-down{
  position:absolute;
  bottom:4px;
  left:50%;
  padding:0;
  width: 50px;
  min-width: 50px;
  height:50px!important;
  margin-left:-25px;
  color: white;
  border-radius: var(--oh3-radius-lg);
}
.terrain-btn-left{
  position:absolute;
  top:50%;
  left:10px;
  padding:0;
  width: 50px;
  min-width: 50px;
  height:50px!important;
  margin-top:-25px;
  color: white;
  border-radius: var(--oh3-radius-lg);
}
.terrain-btn-right{
  position:absolute;
  top:50%;
  right:10px;
  padding:0;
  width: 50px;
  min-width: 50px;
  height:50px!important;
  margin-top:-25px;
  color: white;
  border-radius: var(--oh3-radius-lg);
}
.terrain-btn-center{
  position:absolute;
  top:50%;
  right:calc(50% - 25px);
  padding:0;
  width:50px;
  height:50px;
  margin-top:-25px;
  color: white;
  border-radius:50px;
}
.terrain-reset{
  position:absolute;
  top:80px;
  left:50%;
  padding:0;
  width:40px;
  height:20px;
  margin-left:-20px;
  text-align:center;
  font-size: larger;
  color: white;
  /*-webkit-text-stroke: 1px #FFF;*/
  /*text-stroke: 1px #FFF;*/
}
.terrain-btn-div .ui-spinner{
  font-size:10px;
  width:30px;
  background:rgba(0,0,0,0);
  border:none;
}
.loadingImg {
  width:80px;
  display:none;
  position: absolute;
  top:calc(50% - 40px);
  left:calc(50% - 40px);
  z-index:1;
}
@keyframes pulse {
  0%, 100% { opacity: 1;   }
  50%      { opacity: 0.1; }
}
.pulse {
  position: absolute;
  bottom: 70px;
  z-index: 3;
  animation: pulse 3s ease-in-out infinite;
}
/* 横一列固定・折り返さず、はみ出したら横スクロール */
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  position: relative;
  z-index: 2;
  flex-wrap: nowrap; /* 折り返さない */
  overflow: hidden;
  -webkit-overflow-scrolling: touch; /* iOSで慣性スクロール */
}
/* 子要素が縮まないようにする（等幅で並べる） */
.toolbar-row :is(.v-switch, .v-text-field) {
  flex: 0 0 auto;
}
.mapillary-qry-result {
  width: 150px;
  height: 40px;
  overflow: hidden;
}

#floating-buttons {
  position: fixed;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  padding: 8px 16px;
  border-radius: 24px;
  box-shadow: var(--oh3-shadow-popup);
  z-index: 10;
}

.draw-confirm-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.draw-confirm-content .drag-handle {
  cursor: grab;
  opacity: 0.5;
  transition: opacity 0.2s;
  margin-right: -4px;
}

.draw-confirm-content .drag-handle:hover {
  opacity: 1;
}

.draw-confirm-content .drag-handle:active {
  cursor: grabbing;
}

/* 編集モード用バー */
#edit-mode-bar {
  position: fixed;
  top: 52px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  padding: 8px 16px;
  border-radius: 24px;
  box-shadow: var(--oh3-shadow-popup);
  z-index: 10;
}

.edit-mode-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.edit-mode-text {
  font-size: var(--oh3-font-size-base);
  font-weight: 500;
  color: #1976d2;
}

/* 複製モード用バー */
#duplicate-mode-bar {
  position: fixed;
  top: 52px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  padding: 8px 16px;
  border-radius: 24px;
  box-shadow: var(--oh3-shadow-popup);
  z-index: 10;
}

.duplicate-mode-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.duplicate-mode-text {
  font-size: var(--oh3-font-size-base);
  font-weight: 500;
  color: #9c27b0;
}

.duplicate-mode-hint {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-secondary);
  padding: 2px 8px;
  background: #f5f5f5;
  border-radius: var(--oh3-radius-xl);
}

.my-speed-dial {
  position: absolute;
  top: 100px;
  left: 100px;
  z-index: 99999999;
}

/* v-btn の内容領域をボタン全体に広げる */
.btn-img-full :deep(.v-btn__content) {
  width: 100%;
  height: 100%;
}

/* 画像をボタンいっぱいにフィット */
.btn-img {
  position: absolute;
  width: 70%;
  display: block;
  object-fit: contain;  /* 画像を切らずに収める（切って良いなら cover） */
}

/* v-dialog の中で左右いっぱいに広がる 3 列。点名が残り幅をすべて受け持つ */
.oh3-grid-3col {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px 220px; /* 左が可変、右2つは固定幅 */
  align-items: center;
  gap: 8px;
}
.oh3-grid-4col {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px 150px 150px; /* 左が可変、右3つは固定幅 */
  align-items: center;
  gap: 8px;
}
/* 点名は可変幅でグリッド領域ぴったりに */
.oh3-col-flex {
  width: 100%;
}
/* 右2つのセレクトは固定幅。Vuetifyの入力は内部で100%になるので、ラッパーに幅を指定 */
.oh3-col-fixed {
  width: 150px;
}

/* 狭い画面では 1 列にフォールバック */
@media (max-width: 640px) {
  .oh3-grid-3col {
    grid-template-columns: 1fr;
  }
  .oh3-grid-4col {
    grid-template-columns: 1fr;
  }
  .oh3-col-fixed {
    width: 100%;
  }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

/* デスクトップ：4列（必要なら固定幅を調整） */
.oh3-grid-4col{
  display: grid;
  grid-template-columns: 1fr 180px 150px 160px; /* ←好みで調整 */
  gap: 8px;
  align-items: start;
}

/* 小画面：3段（a=100%, b=100%, c+d=50%ずつ横並び） */
@media (max-width: 1000px){
  .oh3-grid-4col{
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "a a"
      "b b"
      "c d";
    gap: 8px;
  }
  .oh3-grid-4col > .g-a{ grid-area: a; }
  .oh3-grid-4col > .g-b{ grid-area: b; }
  .oh3-grid-4col > .g-c{ grid-area: c; }
  .oh3-grid-4col > .g-d{ grid-area: d; }

  /* 中身は全幅で流し込む */
  .oh3-col-flex,
  .oh3-col-fixed{
    width: 100%;
  }
}
/* ハートビート（鼓動） */
.oh-chip-heartbeat {
  /* ほんの少し大きめに（任意） */
  transform: translateZ(0);             /* ジャギ回避 */
  animation: oh-heartbeat 1.0s ease-in-out infinite;
  will-change: transform, box-shadow;
}

/* 鼓動キーフレーム：ドクン→少し戻る→休む */
@keyframes oh-heartbeat {
  0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(0,0,0,0); }
  10%  { transform: scale(1.06); box-shadow: 0 0 0 0.25rem color-mix(in srgb, currentColor 30%, transparent); }
  20%  { transform: scale(1.12); box-shadow: 0 0 0 0.35rem color-mix(in srgb, currentColor 22%, transparent); }
  40%  { transform: scale(1.03); box-shadow: 0 0 0 0.15rem color-mix(in srgb, currentColor 18%, transparent); }
  60%  { transform: scale(1.00); box-shadow: 0 0 0 0 rgba(0,0,0,0); }
  100% { transform: scale(1.00); box-shadow: 0 0 0 0 rgba(0,0,0,0); }
}

/* 端末の「簡易アニメーション」設定に配慮（OS設定でアニメ抑制時は停止） */
@media (prefers-reduced-motion: reduce) {
  .oh-chip-heartbeat { animation: none; }
}

/* App.vue <style scoped> などに */
.oh-panel{
  border:1px solid var(--v-theme-outline);
  border-radius: var(--oh3-radius-lg);
  padding:12px 14px;
  background:color-mix(in oklab, var(--v-theme-surface) 92%, transparent);
  /* ↑ 明るさをほんの少しだけ上げる。対応しない環境でも単色なので破綻しない */
}
.oh-panel__title{
  font-size:12px;
  letter-spacing:.06em;
  text-transform:uppercase;
  color:rgba(0,0,0,.62); /* ダーク時はVuetifyがよしなに反転 */
  margin-bottom:10px;
  border-left:3px solid var(--v-theme-outline);
  padding-left:8px;
}

/* 余白を抑えてフラットに */
.v-list-item{
  border-radius: var(--oh3-radius-lg);
}
.v-list-item:hover{
  background: color-mix(in oklab, var(--v-theme-surface) 88%, transparent);
}

.editable-label {
  cursor: pointer;
  border-bottom: 1px dotted currentColor; /* クリックできることを示す */
}
.editable-label:hover { opacity: .9; }

/* ===== Compass (OH3) ===== */
.compass-wrap { cursor: pointer; display:inline-block; }
.compass-icon { display:block; border-radius:999px; }

/* ダークUI前提の落ち着いた背景（透け感） */
.c-bg {
  fill: rgba(18, 18, 20, 0.82);
  stroke: rgba(255, 255, 255, 0.06);
  stroke-width: 1.2;
  vector-effect: non-scaling-stroke;
}

/* リング：テーマ色 × グラデ。親の color を優先（currentColor） */
.c-ring {
  fill: none;
  stroke-width: 2.4;
  vector-effect: non-scaling-stroke;
}
.compass-wrap { color: var(--oh-primary, #ff9800); } /* OH3既定:オレンジ */

/* 目盛り */
.c-ticks line {
  stroke: rgba(255,255,255,0.6);
  vector-effect: non-scaling-stroke;
}

/* 針 */
.c-needle-n { fill: #e53935; /* 上品な赤 */ }
.c-needle-s { fill: rgba(255,255,255,0.55); }
.c-cap {
  fill: rgba(0,0,0,0.35);
  stroke: rgba(255,255,255,0.7);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

/* 方位文字：Nだけテーマ色でアクセント */
.c-labels text { fill: rgba(255,255,255,0.85); }
.c-labels .c-N { fill: currentColor; }

/* 小さめ端末でのくっきり維持 */
@media (max-width: 480px) {
  .compass-icon { width: 48px; height: 48px; }
}

/* ====== 高さ伝搬の骨格（flex） ====== */
.fw-fit{
  width:100%; height:100%;
  display:flex; flex-direction:column;
  min-width:0; min-height:0;
}
.fw-card{
  width:100%; height:100%;
  display:flex; flex-direction:column;
}
.fw-body{
  flex:1 1 auto;
  display:flex; flex-direction:column;
  overflow:hidden;           /* 中でスクロールを持つ */
  min-height:0;              /* ★ 子 overflow を有効化 */
  padding:12px;
}

/* 中間ラッパ：上に固定ブロック＋下にスクロール箱を置くための器 */
.section-grow{
  display:flex; flex-direction:column;
  flex:1 1 0%;               /* ★ 0% 基準で残り高さを受け取る */
  min-height:0;              /* ★ 必須 */
}

/* 内側のスクロール領域（ここで確実にスクロールを発生させる） */
.list-pane{
  flex:1 1 0%;               /* ★ 0% 基準で下側を伸縮 */
  min-height:0;              /* ★ 必須 */
  overflow:auto;             /* ← スクロール発生点 */
}

/* インライン編集：見た目＆幅 70% */
.editable-label{ cursor:pointer; border-bottom:1px dotted currentColor; }
.editable-label:hover{ opacity:.9; }

.name-edit-wrap{
  display:flex;
  align-items:center;
  gap:6px;
  width:100%;
  min-width:0;
}

.name-input-70{
  flex:0 1 70%;
  max-width:70%;
  min-width:140px;           /* お好みで調整 */
}
/* Vuetify 内側ラッパへ幅伝搬（v2） */
.name-input-70 :deep(.v-input),
.name-input-70 :deep(.v-input__control),
.name-input-70 :deep(.v-field){ width:100%; }
/* タイトル直下の操作行を右端寄せに */
/* scoped でも効くようにシンプルに */
.title-actions{
  display:flex;
  justify-content:flex-end;  /* 右端 */
  padding:4px 16px 0;        /* タイトルとの間隔 */
}
/* モバイル余白（任意） */
@media (max-width: 500px){
  .fw-body{ padding:8px; }
}

/* Option D: パルス（淡い心拍風）。実行中やビジー中は付かない想定 */
.pulse-anim {
  animation: pulseGlow 1.4s ease-in-out infinite;
}
@keyframes pulseGlow {
  0% { box-shadow: 0 0 0 0 rgba(33,150,243,0.45); }
  70% { box-shadow: 0 0 0 12px rgba(33,150,243,0); }
  100% { box-shadow: 0 0 0 0 rgba(33,150,243,0); }
}

/* ========================================
   Component-Specific Styles (moved from global)
   ======================================== */

/* Android用ネイティブselect */
.native-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.native-select-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.7);
}

.native-select {
  padding: 10px 12px;
  font-size: 1rem;
  border: 1px solid rgba(var(--v-theme-outline-variant-rgb, 0, 0, 0), 0.38);
  border-radius: var(--oh3-radius-lg);
  background-color: var(--v-theme-surface, #fff);
  color: var(--v-theme-on-surface, inherit);
}

.native-select:disabled {
  opacity: 0.6;
}

label {
  cursor: pointer;
}

/* v-btn overrides - component-specific, not global */
:deep(v-btn), :deep(.button) {
  background-color: var(--main-color)!important;
  transition: all 0.2s ease !important;
}

:deep(v-btn):hover, :deep(.button):hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-hover) !important;
}

:deep(v-btn):active, :deep(.button):active {
  transform: translateY(0);
  box-shadow: var(--shadow-sm) !important;
}

font {
  pointer-events: none;
}

.carousel {
  position: relative;
  width: 80%;
  /*max-width: 600px;*/
  width: 200px;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: var(--oh3-shadow-popup);
  margin-bottom: 10px;
}
.carousel-images {
  display: flex;
  transition: transform 0.5s ease-in-out;
}
.carousel-images img {
  display: block;
  width: 200px; /* Ensures consistent width */
  height: auto; /* Maintains aspect ratio */
  flex-shrink: 0;
  object-fit: contain; /* Prevents stretching */
}
.carousel-buttons {
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
}
.carousel-button {
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: var(--oh3-radius-lg);
}
.carousel-button:hover {
  background-color: rgba(0, 0, 0, 0.7);
}
.carousel-buttons.hidden {
  display: none;
}
.carousel-button.disabled {
  background-color: rgba(0, 0, 0, 0.2);
  cursor: not-allowed;
}

.custom-switch {
  height: 34px; /* 全体の高さを調整 */
}
:deep(.custom-switch .v-switch__control) {
  height: 18px; /* スイッチ部分の高さ */
  width: 32px;  /* スイッチ部分の幅 */
}
:deep(.custom-switch .v-switch__thumb) {
  height: 18px; /* スイッチの丸部分の高さ */
  width: 18px;  /* スイッチの丸部分の幅 */
}

.color-container {
  margin-top: 15px;
  display: flex; /* 横並びにする */
  gap: 10px; /* ボックス間の余白 */
  height: 25px;
}
.color-container .box {
  width: 100px;
  height: 25px;
  text-align: center;
  line-height: 100px;
  font-weight: bold;
  color: white;
  border-radius: var(--oh3-radius-lg); /* 角を少し丸く */
  cursor: pointer; /* ポインターアイコン */
  transition: transform 0.1s, box-shadow 0.1s; /* スムーズなアニメーション */
}
.color-container .circle {
  width: 25px;
  height: 25px;
  border-radius: 25px; /* 角を少し丸く */
  cursor: pointer; /* ポインターアイコン */
  transition: transform 0.1s, box-shadow 0.1s; /* スムーズなアニメーション */
}
.box:hover {
  filter: brightness(1.2); /* 明るさを少しアップ */
}
.box:active {
  transform: translateY(2px); /* クリック時に少し下がる */
  box-shadow: var(--oh3-shadow-soft); /* 影をつける */
}
.color-container2 {
  margin-top: 5px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between; /* 均等に配置 */
  align-items: center; /* 垂直方向で中央揃え */
}
.color-container2 .circle {
  margin-right: 5px;
  margin-left: 5px;
  width: 25px; /* 真円の幅 */
  height: 25px; /* 真円の高さ */
  border-radius: 100%; /* 真円にする */
  cursor: pointer; /* ポインターに変更 */
  transition: transform 0.1s, box-shadow 0.1s; /* スムーズなアニメーション */
}
.circle:hover {
  filter: brightness(1.2); /* 明るさを少しアップ */
}
.circle:active {
  transform: translateY(2px); /* クリック時に少し下がる */
  box-shadow: var(--oh3-shadow-medium); /* 影をつける */
}
.box1 { background-color: red; }
.box2 { background-color: black; }
.box3 { background-color: blue; }
.box4 { background-color: green; }
.box5 { background-color: orange; }
.box6 {
  border: 1px solid #a0c4ff;
  background-color: rgba(0,0,0,0);
}

:deep(.swich .v-input__control) {
  height: 20px!important;
}

.select-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: auto;
  margin: 20px auto;
}

.select-label {
  font-size: var(--oh3-font-size-base);
  font-weight: bold;
  margin-bottom: var(--oh3-spacing-1);
}

.custom-select {
  appearance: none; /* ブラウザのデフォルトスタイルを無効化 */
  background: linear-gradient(to right, #f0f4ff, #e0eaff);
  border: 1px solid #a0c4ff;
  border-radius: var(--oh3-radius-lg);
  padding: 10px 15px;
  font-size: var(--oh3-font-size-base);
  cursor: pointer;
  box-shadow: var(--oh3-shadow-subtle);
  outline: none;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.custom-select:hover {
  border-color: #4d94ff;
}

.custom-select:focus {
  border-color: #1a73e8;
  box-shadow: 0 0 5px rgba(26, 115, 232, 0.5);
}

.custom-select option {
  padding: 10px;
}

select {
  position: relative;
  z-index: 1000; /* 他の要素の上に表示 */
  direction: ltr; /* 左から右に展開 */
}

.mapillary-attribution-container {
  display: none!important;
}

.mapillary-container,
.mapillary-container2 {
  position: relative;
  overflow: hidden;
  z-index: 10; /* 他の要素に埋もれてないか確認 */
}

.mapillary-viewer {
  background-color: white;
}


/*!* input[type=number] のデフォルト矢印（上下ボタン）非表示（Chrome, Edge, Safari） *!*/
/*.oh-cool-input-number::-webkit-inner-spin-button,*/
/*.oh-cool-input-number::-webkit-outer-spin-button {*/
/*  -webkit-appearance: none;*/
/*  margin: 0;*/
/*}*/
/*!* Firefox用 *!*/
/*.oh-cool-input-number[type=number] {*/
/*  -moz-appearance: textfield;*/
/*}*/

/* 半透明ドット */
.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: var(--oh3-radius-full);
  background: rgba(100,100,100,0.2);
  vertical-align: middle;
  cursor: pointer;
  pointer-events: auto;
  /* アニメや影も追加可 */
}

.print-title {
  position: absolute;
  top: 47px;
  width: 100%;
  text-align: center;
  z-index: 2;
  font-size: var(--oh3-font-size-3xl);
  /* 背景は何も指定しない＝透明 */
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.print-title-bg {
  display: inline-block;
  background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(250,250,250,0.9) 100%);
  padding: 6px 20px;
  border-radius: var(--oh3-radius-2xl);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.12);
  letter-spacing: 0.08em;
  font-weight: 500;
  line-height: 1.4;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  pointer-events: auto;
}
@media (max-width: 500px) {
  .print-title{
    top: 60px;
  }
}
.print-date {
  position: absolute;
  /* topはインラインスタイルで動的に設定 */
  width: 100%;
  text-align: center;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  pointer-events: none;
}
.print-date-bg {
  display: inline-block;
  background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(250,250,250,0.9) 100%);
  padding: 6px 20px;
  border-radius: var(--oh3-radius-2xl);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.12);
  letter-spacing: 0.08em;
  font-weight: 500;
  line-height: 1.4;
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.print-tile-note {
  font-size: var(--oh3-font-size-sm);
  color: var(--oh3-color-text-secondary);
  margin-top: var(--oh3-spacing-1);
}
@media (max-width: 500px) {
  /* モバイル時のtopもインラインスタイルで動的に計算 (60 + printTitleHeight) */
}
.print-buttons {
  position: absolute;
  left:5px;
  top: 10px;
  z-index: 1;
}

/* 投げ縄コントロールパネル（FloatingWindow内） */
.lasso-control-content {
  padding: var(--oh3-spacing-4);
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 投げ縄モード説明画面 */
.lasso-mode-guide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
}

.lasso-guide-icon {
  padding: var(--oh3-spacing-4);
  background: rgb(var(--v-theme-primary));
  border-radius: var(--oh3-radius-full);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.lasso-guide-icon .v-icon {
  color: #ffffff !important;
}

.lasso-guide-text {
  text-align: center;
}

.lasso-guide-text p {
  font-size: var(--oh3-font-size-base);
  color: var(--oh3-color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.lasso-selected-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: var(--oh3-radius-lg);
  margin-bottom: var(--oh3-spacing-4);
  font-size: var(--oh3-font-size-base);
  font-weight: 500;
  color: var(--oh3-color-text-primary);
}

.lasso-control-row {
  margin-bottom: var(--oh3-spacing-4);
}

.lasso-control-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--oh3-color-text-secondary);
  margin-bottom: var(--oh3-spacing-2);
  text-align: center;
}

.lasso-control-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lasso-control-buttons-centered {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}

.lasso-control-btn {
  flex-shrink: 0;
}

.lasso-control-input {
  width: 80px;
  padding: 8px 10px;
  border: 2px solid #e0e0e0;
  border-radius: var(--oh3-radius-md);
  font-size: var(--oh3-font-size-base);
  text-align: center;
  outline: none;
  transition: all 0.2s;
}

.lasso-control-input:focus {
  border-color: #1976d2;
  background: #f8f9fa;
}

.lasso-control-unit {
  font-size: var(--oh3-font-size-base);
  color: var(--oh3-color-text-secondary);
  font-weight: 600;
  flex-shrink: 0;
  min-width: 24px;
}

.lasso-control-actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding-top: var(--oh3-spacing-4);
  border-top: 2px solid #e0e0e0;
}

.lasso-control-actions .v-btn {
  flex: 1;
}

/* 投げ縄 主要アクションボタン */
.lasso-primary-actions {
  margin-bottom: var(--oh3-spacing-3);
}

/* 投げ縄 Transform Box説明 */
.lasso-transform-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--oh3-color-surface-highlight, #e3f2fd);
  border-radius: var(--oh3-radius-md);
  font-size: 11px;
  color: var(--oh3-color-text-secondary, #666);
  margin-bottom: var(--oh3-spacing-2);
}

/* 投げ縄 詳細設定トグル */
.lasso-advanced-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: var(--oh3-radius-md);
  cursor: pointer;
  font-size: 13px;
  color: var(--oh3-color-text-secondary);
  margin-bottom: var(--oh3-spacing-2);
  transition: background 0.2s;
}

.lasso-advanced-toggle:hover {
  background: #eeeeee;
}

/* 投げ縄 詳細設定コントロール */
.lasso-advanced-controls {
  padding: var(--oh3-spacing-3);
  background: #fafafa;
  border-radius: var(--oh3-radius-lg);
  border: 1px solid #e0e0e0;
}

/* 投げ縄 スタイリッシュカラーセレクター（インライン版） */
.lasso-stylish-color-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: var(--oh3-radius-xl);
  padding: var(--oh3-spacing-3);
  margin-bottom: var(--oh3-spacing-3);
}

.lasso-stylish-color-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.lasso-stylish-color-item {
  position: relative;
  cursor: pointer;
  padding: 3px;
  border-radius: var(--oh3-radius-full);
  transition: all 0.2s ease;
}

.lasso-stylish-color-item:hover {
  transform: scale(1.15);
}

.lasso-stylish-color-item.is-selected {
  padding: 2px;
  border: 2px solid var(--oh3-color-link);
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.3);
}

.lasso-stylish-color-circle {
  width: 26px;
  height: 26px;
  border-radius: var(--oh3-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px var(--oh3-color-overlay-15), inset 0 1px 2px rgba(255,255,255,0.3);
  transition: all 0.2s ease;
}

.lasso-stylish-color-item.is-transparent .lasso-stylish-color-circle {
  background: linear-gradient(45deg, #ccc 25%, transparent 25%),
              linear-gradient(-45deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
  border: 1px dashed #999;
}

/* 投げ縄 透過度スライダー */
.lasso-stylish-opacity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0,0,0,0.08);
}

.lasso-opacity-icon {
  color: var(--oh3-color-overlay-50);
}

.lasso-stylish-slider {
  flex: 1;
}

.lasso-stylish-opacity-value {
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  color: var(--oh3-color-link);
  min-width: 36px;
  text-align: right;
}

/* 投げ縄 カスタムカラーピッカー */
.lasso-hidden-input {
  opacity: 0;
  position: absolute;
  pointer-events: none;
}

.lasso-stylish-picker-circle {
  background: conic-gradient(
    from 0deg,
    #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000
  );
}

.lasso-stylish-picker .lasso-stylish-color-circle {
  position: relative;
}

.lasso-stylish-picker .lasso-stylish-color-circle::after {
  content: '';
  position: absolute;
  inset: 4px;
  background: white;
  border-radius: var(--oh3-radius-full);
}

.lasso-stylish-picker .lasso-stylish-color-circle .v-icon {
  position: relative;
  z-index: 1;
}

/* 投げ縄 削除・元に戻す・確定ボタン */
.lasso-action-buttons {
  display: flex;
  gap: 6px;
  margin-top: var(--oh3-spacing-2);
}

.lasso-action-btn {
  flex: 1;
  min-width: 0;
  padding: 0 8px !important;
}

.lasso-action-btn :deep(.v-btn__content) {
  justify-content: center;
}

.lasso-delete-others-btn {
  font-size: 11px !important;
}

/* ドローヘルプウィンドウのスタイル */
.draw-help-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

.help-section {
  margin-bottom: var(--oh3-spacing-5);
}

.shortcut-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shortcut-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: var(--oh3-radius-md);
  transition: background-color 0.2s;
}

.shortcut-item:hover {
  background: #e9ecef;
}

.shortcut-item kbd {
  display: inline-block;
  min-width: 80px;
  padding: 4px 8px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: var(--oh3-radius-base);
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.08);
  margin-right: var(--oh3-spacing-3);
}

.shortcut-item span {
  font-size: var(--oh3-font-size-base);
  color: #495057;
  flex: 1;
}

.map-radio {
  position: absolute;
  z-index: 2;
  left: calc(50% - 75px);
}
:deep(.map-radio div) {
  margin: 0px!important;
}

/* 印刷モードのヘッダーエリア（チップ群） */
.print-header-area {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding: 8px 0 4px;
  gap: 4px;
}
.print-layer-btns {
  display: flex;
  gap: 6px;
}
@media print {
  .print-layer-btns {
    display: none !important;
  }
}
.print-chips-group {
  pointer-events: auto;
}
:deep(.print-chips-group .v-chip-group__content) {
  justify-content: center;
  gap: 4px;
}
/* 印刷モード時のタイトル位置（上に移動） */
.print-mode .print-title {
  top: 5px;
}

@keyframes draw-indicator-pulse {
  0% {
    box-shadow: 0 0 8px #fff, 0 0 0 0 rgba(255, 20, 147, 0.7);
  }
  50% {
    box-shadow: 0 0 12px #fff, 0 0 0 6px rgba(255, 20, 147, 0);
  }
  100% {
    box-shadow: 0 0 8px #fff, 0 0 0 0 rgba(255, 20, 147, 0);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.draw-indicator {
  position: fixed;
  width: 12px;
  height: 12px;
  background: deeppink;
  border-radius: var(--oh3-radius-full);
  pointer-events: none;
  z-index: 9999;
  transform: translate(-50%, -20%);
  transition: none; /* 遅延ゼロ */
  box-shadow: 0 0 8px #fff;
  animation: draw-indicator-pulse 1.5s ease-in-out infinite;
}

/* 円半径ドラッグ時のツールチップ */
.circle-radius-tooltip {
  position: fixed;
  display: none;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 6px 12px;
  border-radius: var(--oh3-radius-md);
  font-size: var(--oh3-font-size-base);
  font-weight: 600;
  pointer-events: none;
  z-index: 10000;
  white-space: nowrap;
  box-shadow: var(--oh3-shadow-strong);
  transform: translate(-50%, -100%);
  margin-top: -12px;
}

.circle-radius-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.85);
}

.draw-realtime-info {
  position: fixed;
  bottom: 80px;
  right: 20px;
  color: #fff;
  padding: 16px 20px;
  padding-top: 28px;
  border-radius: var(--oh3-radius-lg);
  pointer-events: auto;
  z-index: 9998;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  min-width: 200px;
  box-shadow: var(--oh3-shadow-modal);
  backdrop-filter: blur(10px);
  animation: fade-in-up 0.3s ease-out;
}

.draw-info-copy-btn {
  position: absolute;
  top: 8px;
  right: 32px;
  display: inline-block;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--oh3-font-size-base);
  font-weight: 700;
  line-height: 1;
  padding: var(--oh3-spacing-1);
  user-select: none;
  transition: opacity 0.2s;
  z-index: 1;
  -webkit-tap-highlight-color: transparent;
}

.draw-info-copy-btn:hover {
  opacity: 0.7;
}

.draw-info-copy-btn:active {
  opacity: 0.5;
}

.draw-info-close-x {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-block;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--oh3-font-size-xl);
  font-weight: 700;
  line-height: 1;
  padding: var(--oh3-spacing-1);
  user-select: none;
  transition: opacity 0.2s;
  z-index: 1;
}

.draw-info-close-x:hover {
  opacity: 0.7;
}

.draw-info-close-x:active {
  opacity: 0.5;
}

.draw-realtime-info .info-title {
  font-size: var(--oh3-font-size-sm);
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: var(--oh3-spacing-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.draw-realtime-info .info-value {
  font-size: var(--oh3-font-size-2xl);
  font-weight: 700;
  color: #fff;
  margin-bottom: var(--oh3-spacing-1);
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.draw-realtime-info .info-secondary {
  font-size: var(--oh3-font-size-base);
  color: #fff;
  margin-top: var(--oh3-spacing-2);
  padding-top: var(--oh3-spacing-2);
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.draw-realtime-info .info-item {
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
}

.draw-realtime-info .info-label {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.draw-realtime-info .info-number {
  color: #fff;
  font-weight: 600;
  font-size: 13px;
}

.draw-realtime-info #draw-realtime-info {
  max-height: 300px;
  overflow-y: auto;
}

.draw-realtime-info .info-segments {
  margin-top: var(--oh3-spacing-2);
  padding-top: var(--oh3-spacing-2);
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.draw-realtime-info .info-segments .info-label {
  margin-bottom: var(--oh3-spacing-1);
  font-size: var(--oh3-font-size-sm);
  color: rgba(255, 255, 255, 0.8);
}

.draw-realtime-info .info-segment-item {
  font-size: var(--oh3-font-size-sm);
  color: rgba(255, 255, 255, 0.9);
  padding: 2px 0;
  display: flex;
  justify-content: space-between;
}

.drawing-finish-div {
  position: absolute;
  top: 200px;
  left: 200px;
  z-index: 3;
}

.tooltip-wrapper[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: -1.8em;
  left: 50%;
  transform: translateX(-50%);
  /*background: var(--main-color);*/
  background: black;
  color: white;
  font-size: var(--oh3-font-size-xs);
  /*padding: 2px 6px;*/
  border-radius: var(--oh3-radius-lg);
  white-space: nowrap;
  z-index: 9999;
  padding: 2px 10px 2px 10px;
}

.keyword-item {
  font-size: var(--oh3-font-size-lg);
  padding: var(--oh3-spacing-1);
  cursor: pointer;
  border-bottom: 1px solid #ccc;
  transition: background-color 0.2s;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.keyword-item:hover {
  background-color: #e0f0ff;
}

.image-marker {
  position: absolute;
  background: red;
  color: white;
  border-radius: var(--oh3-radius-full);
  width: 20px;
  height: 20px;
  text-align: center;
  line-height: 20px;
  font-size: var(--oh3-font-size-sm);
  font-weight: bold;
  cursor: move;
  z-index: 999;
}

#warp-canvas {
  position: absolute;
  top: 0;
  left: 0;
  /*background: rgba(255, 0, 0, 0.3); !* ← 目立つ赤で確認用 *!*/
  z-index: 2; /* マーカーより上に出したいとき調整 */
  pointer-events: none; /* マーカークリックなどを通す */
}

.compass-div {
  position: absolute;
  top: 10px;
  right: 30px;
  width: 40px;
  height: 40px;
  pointer-events: auto;
  cursor: pointer;
  z-index: 1;
}
.compass-div-tracking {
  position: absolute;
  top: 60px;
  left: calc(50% - 20px);
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 3;
}
.scale-ratio {
  position: absolute;
  bottom: 35px;
  left: 6px;
  /*background: white;*/
  padding: 4px 8px;
  font-size: var(--oh3-font-size-base);
  /*border: 1px solid #ccc;*/
  /*border-radius: var(--oh3-radius-base);*/
  pointer-events: none;
  z-index: 1;
  text-shadow:
      -1px -1px 0 #ffffff,
      1px -1px 0 #ffffff,
      -1px  1px 0 #ffffff,
      1px  1px 0 #ffffff;
}

.print-print {
  position: absolute!important;
  top: 60px!important;
  left: 0!important;
}
.print-config {
  position: absolute!important;
  top: 120px!important;
  left: 0!important;
}
.print-png {
  position: absolute!important;
  top: 180px!important;
  left: 0px!important;
}

:deep(.v-main) {
  background-color: black;
}


.rotate-btn {
  display: flex;
  height: 60px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--oh3-color-text-primary);
  font-size: var(--oh3-font-size-3xl);
}

.rotate-btn:hover {
  color: #1976d2; /* ホバー時に青く */
}

/* スマホでスムースにスクロールできるように */
/* 外側: 影を出す。スクロールはしない */
.scrollable-menu {
  overflow: visible !important;
  touch-action: auto !important;              /* ← 親の touch-action:none を打ち消す */
  overscroll-behavior: contain;               /* スクロール連鎖を切る（Android向け） */
}

/* 内側（v-list / v-card 等）をスクロール領域に */
.scrollable-menu > * {
  max-height: 300px;                          /* menu-props の maxHeight と揃える */
  overflow-y: auto !important;
  /* iOS の慣性。Androidでも無害 */
  -webkit-overflow-scrolling: touch;
}

/* 念のため、メニュー内部の全要素でタッチを奪わないように */
.scrollable-menu, .scrollable-menu * {
  touch-action: auto !important;
}

.my-popup{
  overflow: hidden;
}

:deep(.my-snackbar .v-snackbar__wrapper) {
  background-color: color-mix(in srgb, var(--main-color) 70%, black)!important;
  margin-top: 60px;
}

/* ===== v-select風スタイル ===== */
.vlike{
  display:inline-flex;
  flex-direction:column;
  gap:4px;
  font-size:14px;
  min-width:220px;
  width: 100%;
  position:relative;
  margin-bottom: 25px;
}
.vlike__text{
  color:var(--v-theme-on-surface, #1f1f1f);
  opacity:.7;
  padding-left:4px;
}
.vlike__control{
  -webkit-appearance:none;
  appearance:none;
  width:100%;
  height:55px;
  line-height:55px;
  padding:0 40px 0 12px;
  border-radius: var(--oh3-radius-lg);border:1px solid rgba(0,0,0,.12);
  background:var(--v-theme-surface,#fff);
  color:var(--v-theme-on-surface,#1f1f1f);
  outline:none;
  box-shadow:none;
  transition:border-color .15s,box-shadow .15s,background .15s;
}
.vlike__control:hover{
  border-color:rgba(0,0,0,.22)
}
.vlike__control:focus{
  border-color:var(--v-theme-primary,#1976d2);
  box-shadow:0 0 0 2px color-mix(in srgb, var(--v-theme-primary,#1976d2) 25%, transparent);
}
/* プレースホルダー時の薄色表示 */
.vlike__control[data-empty="true"]{
  color:rgba(0,0,0,.38)
}
/* 無効時 */
.vlike__control:disabled{
  background:var(--v-theme-surface-variant,#f6f6f6);
  color:rgba(0,0,0,.38);
  border-color:rgba(0,0,0,.12);
  cursor:not-allowed
}
/* エラー枠 */
:deep(.is-error .vlike__control){
  border-color:var(--v-theme-error,#d32f2f);
  box-shadow:0 0 0 2px color-mix(in srgb, var(--v-theme-error,#d32f2f) 20%, transparent);
}
/* 矢印（純CSS） */
.vlike::after{
  content:"";position:absolute;right:12px;top:28px;transform:translateY(-50%);
  border-left:6px solid transparent;border-right:6px solid transparent;border-top:7px solid currentColor;
  opacity:.54;pointer-events:none
}
/* ヘルパー / メッセージ */
.vlike__hint{margin-top:4px;font-size:12px;line-height:1.2;color:rgba(0,0,0,.6)}
.vlike__message{margin-top:4px;font-size:12px;line-height:1.2}
.vlike__message--error{color:var(--v-theme-error,#d32f2f)}
/* 密度を下げたい場合 */
:deep(.vlike--dense .vlike__control){height:32px;line-height:32px}

/* ===== 追加・上書き（フローティングラベル） ===== */
.vlike--floating {
  position: relative;
  padding-top: 6px;
}

/* ラベルをボーダーに被せる */
.vlike--floating .vlike__text{
  position: absolute;
  top: 8px;             /* ボーダーの上に少し出す */
  left: 12px;
  font-size: var(--oh3-font-size-sm);
  line-height: 1;
  padding: 0 6px;
  border-radius: var(--oh3-radius-lg);
  background: white; /* 背景でボーダーを"マスク" */
  color: rgba(0,0,0,.6);
  pointer-events: none;  /* クリックを邪魔しない */
  text-align: left;
  z-index: 10000;
}

/* フォーカス時はプライマリ色で強調 */
.vlike--floating:focus-within .vlike__text{
  color: var(--v-theme-primary, #1976d2);
}

/* エラー時はエラー色 */
:deep(.is-error.vlike--floating .vlike__text){
  color: var(--v-theme-error, #d32f2f);
}

/* コントロールは少し下げて余白を作る */
.vlike--floating .vlike__control{
  margin-top: 6px;
}

/* ↓矢印位置の微調整（必要なら） */
.vlike--floating::after{
  top: 28px; /* コントロール中央に来るように調整 */
}

.media-picker-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;       /* 一行に収める */
  padding-bottom: var(--oh3-spacing-5);
}

/* ネイティブ input は画面に出さない（「ファイルを選択」を見せない） */
.sr-only {
  position: absolute;
  left: -9999px;
}

.camera-btn {
  margin-top: 10px;
  margin-right: 10px;
}

/* v-file-input は余白を食い過ぎないように */
.library-input,
.w-full {
  flex: 1 1 auto;          /* 残り幅にフィット */
  min-width: 0;            /* 折り返し防止 */
}

:deep(.exclude-overlay .v-input__prepend) {
  display: none;
}

.dialog-close {
  position: absolute;
  right:5px;
  font-size: 40px;
  cursor: pointer;
}
.dialog-close:hover {
  color: red;
}

.oh-left-bottom-tools {
  position: absolute;
  left: 10px;
  bottom: 110px;
  z-index: 3;
  display: flex;
  gap: 8px;
  font-size: var(--oh3-font-size-xl);
}

.blink {
  animation: oh-blink 1s infinite;
}

@keyframes oh-blink {
  50% { filter: brightness(1.25); }
}

/* About link at bottom center */
.about-link-container {
  position: fixed;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3000;
  pointer-events: none;
}

.about-link {
  display: inline-block;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  padding: 4px 8px;
  pointer-events: auto;
  transition: all 0.2s ease;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.6);
  font-weight: 500;
}

.about-link:hover {
  color: rgba(255, 255, 255, 1);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9), 0 0 12px rgba(0, 0, 0, 0.7);
  text-decoration: underline;
}

@media (prefers-color-scheme: dark) {
  .about-link {
    color: rgba(255, 255, 255, 0.75);
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9), 0 0 10px rgba(0, 0, 0, 0.8);
  }

  .about-link:hover {
    color: rgba(255, 255, 255, 0.95);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 1), 0 0 14px rgba(0, 0, 0, 0.9);
  }
}

/* 新規登録促進ダイアログ */
.signup-promo-dialog {
  overflow: hidden;
}

.signup-promo-content {
  padding: 8px 0;
}

.promo-hero {
  text-align: center;
  padding: 20px 0;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%);
  border-radius: var(--oh3-radius-xl);
  margin-bottom: var(--oh3-spacing-6);
}

.features-card {
  border-radius: var(--oh3-radius-xl);
}

.feature-item {
  display: flex;
  align-items: flex-start;
  padding: 4px 0;
}

.signup-cta-btn {
  font-size: var(--oh3-font-size-md);
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3) !important;
  transition: all 0.3s ease;
}

.signup-cta-btn:hover {
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.4) !important;
  transform: translateY(-2px);
}

@media (max-width: 600px) {
  .promo-hero {
    padding: 16px 0;
  }

  .signup-cta-btn {
    width: 100%;
  }
}

</style>







<style>
/* ========================================
   Global Styles Only
   ======================================== */

/* OH3 Tour Card Styles */
.oh3-tour-card {
  z-index: 10000 !important;
  border-radius: var(--oh3-radius-xl) !important;
  overflow: hidden;
}
.oh3-tour-card .v-card-title {
  font-size: 1rem;
}
.oh3-tour-card .v-card-text {
  font-size: 0.95rem;
  line-height: 1.6;
}
/* vue3-tour overlay */
.v-tour__target--highlighted {
  box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.5) !important;
  border-radius: var(--oh3-radius-lg);
}
.v-step {
  z-index: 10000 !important;
}

/* Legend styles for ext components */
/* Used in ext-zeni.vue, ext-kyakusu.vue, and layers.js */
.legend-scale {
  margin-top: var(--oh3-spacing-5);
}
.legend-scale ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.legend-scale ul li {
  margin: 0 0 5px 0;
  padding: 0;
  display: flex;
  align-items: center;
}
.legend-scale ul li span {
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-right: 5px;
  border: 1px solid #999;
}

/* MapLibre library overrides */
.maplibregl-ctrl{
  display: none;
}
.maplibregl-canvas.force-pointer {
  cursor: pointer !important;
}
.maplibregl-canvas.force-wait {
  cursor: wait !important;
}
.maplibregl-canvas.duplicate-mode {
  cursor: copy !important;
}
.maplibregl-canvas.circle-edge-resize {
  cursor: ew-resize !important;
}
.maplibregl-canvas {
  cursor: default !important;
}

/* 描画モード時のカーソル */
.maplibregl-canvas.is-drawing-polygon,
.maplibregl-canvas.is-drawing-line {
  cursor: crosshair !important;
}
.maplibregl-canvas.is-drawing-point {
  cursor: cell !important;
}
.maplibregl-canvas.is-drawing-circle {
  cursor: crosshair !important;
}
.maplibregl-canvas.is-drawing-text {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Ctext x='4' y='18' font-family='Arial' font-size='18' font-weight='bold' fill='%231976d2'%3EA%3C/text%3E%3Cline x1='18' y1='4' x2='18' y2='20' stroke='%231976d2' stroke-width='2'/%3E%3C/svg%3E") 12 12, text !important;
}
/* 流域計算クリックモード時のカーソル */
.maplibregl-canvas.is-watershed-click-mode {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ccircle cx='16' cy='16' r='4' fill='none' stroke='%23e74c3c' stroke-width='2'/%3E%3Cline x1='16' y1='0' x2='16' y2='11' stroke='%23e74c3c' stroke-width='2'/%3E%3Cline x1='16' y1='21' x2='16' y2='32' stroke='%23e74c3c' stroke-width='2'/%3E%3Cline x1='0' y1='16' x2='11' y2='16' stroke='%23e74c3c' stroke-width='2'/%3E%3Cline x1='21' y1='16' x2='32' y2='16' stroke='%23e74c3c' stroke-width='2'/%3E%3C/svg%3E") 16 16, crosshair !important;
}
/* AI防災リスク診断フリーハンドモード時のカーソル */
.maplibregl-canvas.is-area-analysis-freehand {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'%3E%3Cpath d='M5 20c5 2 10 2 16-1' stroke='%23ff6b6b' stroke-width='2' stroke-linecap='round' fill='none'/%3E%3Cpath d='M4 9c4-3 8-4 12-2 3 2 6 1 8-1' stroke='%23ff6b6b' stroke-width='2' stroke-linecap='round' fill='none'/%3E%3Ccircle cx='7' cy='22' r='2' fill='%23ff6b6b'/%3E%3Ccircle cx='21' cy='18' r='2' fill='%23ff6b6b'/%3E%3C/svg%3E") 14 14, crosshair !important;
}
/* AI防災リスク診断サークルモード時のカーソル */
.maplibregl-canvas.is-area-analysis-circle {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='%239C27B0' stroke-width='2' stroke-dasharray='4 2'/%3E%3Ccircle cx='12' cy='12' r='2' fill='%239C27B0'/%3E%3C/svg%3E") 12 12, crosshair !important;
}
.maplibregl-canvas.is-area-analysis-circle-radius {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='%239C27B0' stroke-width='2'/%3E%3Cline x1='12' y1='12' x2='22' y2='12' stroke='%239C27B0' stroke-width='2'/%3E%3Ccircle cx='12' cy='12' r='2' fill='%239C27B0'/%3E%3C/svg%3E") 12 12, crosshair !important;
}
/* AI防災リスク診断ポリゴンモード時のカーソル */
.maplibregl-canvas.is-area-analysis-polygon {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpolygon points='12,2 22,20 2,20' fill='none' stroke='%239C27B0' stroke-width='2'/%3E%3Ccircle cx='12' cy='2' r='2' fill='%239C27B0'/%3E%3C/svg%3E") 12 12, crosshair !important;
}

/* 塗り絵マップ: 選択色カーソル */
.maplibregl-canvas.is-coloring-mode {
  cursor: var(--oh3-coloring-cursor) !important;
}

/* ドローツールメニューのスタイル - グローバル定義（App.vueのメニューにも適用するため） */
.draw-tool-menu {
  min-width: 200px;
  background: rgb(var(--v-theme-primary)) !important;
  filter: brightness(0.85);
  border-radius: var(--oh3-radius-lg) !important;
  box-shadow: var(--oh3-shadow-modal) !important;
  padding: 6px !important;
  animation: menuSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* メニュー表示アニメーション */
@keyframes menuSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.draw-tool-menu .v-list-item {
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: var(--oh3-radius-md);
  margin: 3px 0;
  background: rgba(0, 0, 0, 0.1);
  animation: menuItemSlideIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}

/* メニュー項目のスタッガーアニメーション */
@keyframes menuItemSlideIn {
  0% {
    opacity: 0;
    transform: translateX(-10px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.draw-tool-menu .v-list-item:nth-child(1) { animation-delay: 0.02s; }
.draw-tool-menu .v-list-item:nth-child(2) { animation-delay: 0.04s; }
.draw-tool-menu .v-list-item:nth-child(3) { animation-delay: 0.06s; }
.draw-tool-menu .v-list-item:nth-child(4) { animation-delay: 0.08s; }

.draw-tool-menu .v-list-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(5px) scale(1.03);
  box-shadow: var(--oh3-shadow-medium);
}

.draw-tool-menu .v-list-item:active {
  transform: translateX(3px) scale(0.98);
  background: rgba(255, 255, 255, 0.1);
}

.draw-tool-menu .v-list-item-title {
  font-size: var(--oh3-font-size-base);
  font-weight: 700;
  color: #ffffff !important;
  letter-spacing: 0.3px;
}

/* アイコンを完全な白色で統一 */
.draw-tool-menu .v-icon {
  color: #ffffff !important;
  opacity: 1;
}

/* ホバー時にさらに強調 */
.draw-tool-menu .v-list-item:hover .v-icon {
  filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.5));
}

/* Vuetify v-snackbar overrides */
/* content-classで指定されるため、グローバルに定義する必要がある */
.snack-max {
  max-height: calc(100vh - 80px);
  align-items: stretch;
}
.snack-max .snack-scroll {
  max-height: 100%;
  overflow: auto;
}

/* CSS Variables - Legacy compatibility layer
   これらの変数は後方互換性のために残されています。
   新しいコードでは --oh3-* プレフィックスの変数を使用してください。
*/
:root {
  /* メインカラー（デザインシステムを参照） */
  --main-color: var(--oh3-color-primary);

  /* Shadow System（デザインシステムを参照） */
  --shadow-sm: var(--oh3-shadow-sm);
  --shadow-md: var(--oh3-shadow-md);
  --shadow-lg: var(--oh3-shadow-lg);
  --shadow-hover: var(--oh3-shadow-md);

  /* Spacing System（デザインシステムを参照） */
  --spacing-xs: var(--oh3-spacing-1);
  --spacing-sm: var(--oh3-spacing-2);
  --spacing-md: var(--oh3-spacing-3);
  --spacing-lg: var(--oh3-spacing-4);
  --spacing-xl: var(--oh3-spacing-6);

  /* Border Radius（デザインシステムを参照） */
  --radius-sm: var(--oh3-radius-base);
  --radius-md: var(--oh3-radius-lg);
  --radius-lg: var(--oh3-radius-xl);
}

/* ========================================
   Shared Button Utilities
   ======================================== */
.tiny-btn {
  font-size: var(--oh3-font-size-xs) !important;
  padding: 2px 6px !important;
  min-width: 24px !important;
  /*height: 24px !important;*/
  line-height: 24px !important;
  margin-bottom: 10px !important;
}

.tiny-btn2 {
  font-size: var(--oh3-font-size-xs) !important;
}

.sub-btn {
  width: 40px !important;
  height: 40px !important;
}

.tiny-icon-btn {
  width: 30px!important;
  height:30px!important;
  padding: 0px!important;
  margin:  0px!important;
}

/* ========================================
   MapLibre Popup Styles
   ======================================== */
.popup-html-div {
  width: 100%;
  max-height: 500px;
  overflow: auto;
}

.popup-html-div hr {
  width: 100% !important;
  margin: 10px 0;
}

.popup-html-div > div {
  width: 100% !important;
  max-width: 100% !important;
}

/* 幕末ポップアップは固定幅を維持 */
.popup-html-div > div.kinseipoint {
  width: 300px !important;
}

.pyramid-btn {
  margin-top: 5px;
  height: 20px;
  line-height: 20px;
  width: 100%;
  background-color: var(--main-color);
  color: white;
  border-radius: var(--oh3-radius-lg);
  cursor: pointer;
  transition: transform 0.1s ease, opacity 0.1s ease, filter 0.1s ease;
}
.pyramid-btn:hover {
  opacity: 0.85;
  filter: brightness(1.15);
}
.pyramid-btn:active {
  transform: scale(0.95);
  opacity: 0.8;
}

.popup-table {
  margin-bottom: 10px;
  width: 100%;
}

.popup-table, .popup-table th, .popup-table td {
  border: 1px solid darkgray;
  border-collapse: collapse;
  font-size: large;
  padding: 5px;
}

.popup-table th {
  text-align: center;
  font-weight: normal;
}

.popup-table td:nth-of-type(3) {
  text-align: right;
}

.popup-btn {
  cursor: pointer;
  background-color: rgb(50,101,186);
  color: white;
  padding: 3px 10px 3px 10px;
  border-radius: var(--oh3-radius-lg);
  transition: transform 0.1s ease, opacity 0.1s ease;
}
.popup-btn:active {
  transform: scale(0.95);
  opacity: 0.8;
}
.popup-btn-traffic {
  background-color: var(--oh3-color-primary);
  display: block;
  width: fit-content;
  margin: 8px auto 4px;
}

/* 小字・市町村ポップアップの由来ボタン */
.koaza-origin-btn,
.city-origin-btn,
.mura-name {
  transition: transform 0.1s ease, opacity 0.1s ease !important;
}
.koaza-origin-btn:active,
.city-origin-btn:active,
.mura-name:active {
  transform: scale(0.95) !important;
  opacity: 0.8 !important;
}

.break-hr {
  border: solid 2px rgb(50,101,186);
}

.popup-body {
  padding: 10px;
}

.layer-label-div {
  color: white;
  background-color: var(--main-color);
  padding: 5px;
  margin-bottom: 10px;
  /* width: 300px; */
  height: 30px;
  border-top-left-radius: var(--oh3-radius-base);
  border-top-right-radius: var(--oh3-radius-base);
}

.layer-label-div-red {
  color: white;
  background-color: red;
  padding: 5px;
  margin-bottom: 10px;
  height: 30px;
  border-top-left-radius: var(--oh3-radius-base);
  border-top-right-radius: var(--oh3-radius-base);
}

.layer-label-div-brown {
  color: white;
  background-color: #795548;
  padding: 5px;
  margin-bottom: 10px;
  height: 30px;
  border-top-left-radius: var(--oh3-radius-base);
  border-top-right-radius: var(--oh3-radius-base);
}

/* ========================================
   Popup Chip Navigation (複数地物切り替えUI)
   ======================================== */
.popup-chip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 4px;
  margin-bottom: var(--oh3-spacing-2);
  border-bottom: 1px solid #e0e0e0;
}

.popup-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  color: var(--main-color);
  background-color: #f5f5f5;
  border: 1px solid var(--main-color);
  border-radius: var(--oh3-radius-2xl);
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.popup-chip:hover {
  background-color: #e3f2fd;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(50, 101, 186, 0.2);
}

.popup-chip:active {
  transform: translateY(1px) scale(0.98);
  box-shadow: none;
  background-color: #bbdefb;
}

.popup-chip.active {
  color: white;
  background-color: var(--main-color);
  border-color: var(--main-color);
  box-shadow: 0 2px 6px rgba(50, 101, 186, 0.3);
}

.popup-chip.active:hover {
  background-color: #2a5aa0;
}

.popup-chip.active:active {
  background-color: #1e4080;
  transform: translateY(1px) scale(0.98);
}

.popup-section {
  /* セクションのスタイル（必要に応じて調整） */
}

/* ========================================
   Color Picker Styles (for popups and draw UI)
   ======================================== */
.circle-list {
  display: flex;
  gap: 8px; /* 丸同士の間隔 */
  margin: 0px;
  overflow: hidden;
}

.circle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--oh3-radius-full);
  cursor: pointer;
  transition: box-shadow 0.12s, transform 0.07s;
  box-shadow: var(--oh3-shadow-subtle);
  outline: none;
  color: white;
  font-size: var(--oh3-font-size-base);
  font-weight: bold;
}

.circle.red   { background: red; }
.circle.blue  { background: blue; }
.circle.green { background: green; }
.circle.orange { background: orange; }
.circle.black { background: black; }
.circle.hotpink { background: hotpink; }
.circle.yellow { background: yellow; }
.circle.transparent { background: rgba(0,0,0,0.1); }

/* ハイライト: クリック時・Tab選択時・ホバー時 */
.circle:active,
.circle:focus-visible {
  box-shadow: 0 0 0 3px rgba(255,255,255,0.8), 0 1px 2px rgba(0,0,0,0.15);
  transform: scale(0.90) translateY(1px);
}

.circle:hover {
  box-shadow: 0 0 0 6px rgba(200,200,200,0.18), 0 2px 6px rgba(0,0,0,0.13);
}

/* チェックアイコンのスタイル */
.color-check-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: var(--oh3-font-size-md);
  font-weight: bold;
  text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
  pointer-events: none;
}

.color-check-icon.white-check {
  color: white;
}

.color-check-icon.gray-check {
  color: var(--oh3-color-text-secondary);
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.8);
}

/* ========================================
   Shared Form Input Utilities
   ======================================== */
.oh-cool-input {
  padding: 0.6em 1em;
  border: 1px solid #2979ff;
  border-radius: var(--oh3-radius-lg);
  outline: none;
  font-size: 1.1em;
  background: #f4f8ff;
  color: #18365a;
  transition: border 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: 0 2px 8px 0 rgba(41, 121, 255, 0.08);
}

.oh-cool-input:focus {
  /*border: 2px solid #1565c0;*/
  background: #e3f2fd;
  /*box-shadow: 0 4px 16px 0 rgba(41, 121, 255, 0.15);*/
}

.oh-cool-input::placeholder {
  color: #90caf9;
  letter-spacing: 0.05em;
}

.oh-cool-input-number {
  /*padding: 0.4em 0.8em;*/
  padding: 0em 0.2em 0 0.6em;
  border: 1px solid #2979ff;
  border-radius: var(--oh3-radius-lg);
  outline: none;
  font-size: 1em;
  background: #f4f8ff;
  color: #18365a;
  transition: border 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: 0 1px 4px rgba(41,121,255,0.08);
  width: 5em;
  /* 数値入力矢印を右寄せで少し小さく */
  /* ChromeやSafari用 */
  -webkit-appearance: none;
  appearance: textfield;
}

/*.oh-cool-input-number:focus {*/
/*  border: 2px solid #1565c0;*/
/*  background: #e3f2fd;*/
/*  box-shadow: 0 2px 8px rgba(41,121,255,0.13);*/
/*}*/

.oh-cool-input-number::placeholder {
  color: #90caf9;
  letter-spacing: 0.04em;
}

.oh-cool-select {
  padding: 0 8px;
  border: 1px solid #2979ff;
  border-radius: var(--oh3-radius-lg);
  outline: none;
  font-size: 1em;
  background: #f4f8ff;
  color: #18365a;
  transition: border 0.2s, box-shadow 0.2s, background 0.2s;
  box-shadow: 0 1px 4px rgba(41,121,255,0.08);
  width: 8em; /* 適宜調整 */
  appearance: none;
  /* 下矢印を消したい場合は独自アイコンを使ってもOK */
}

.oh-cool-select:focus {
  /*border: 2px solid #1565c0;*/
  background: #e3f2fd;
  /*box-shadow: 0 2px 8px rgba(41,121,255,0.13);*/
}

/* ========================================
   Other Shared Utilities
   ======================================== */
.style01 {
  font-weight: 500;
}

/* ========================================
   Global Range Slider Styles
   ======================================== */
input[type="range"] {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

/* Chrome, Safari, Edge */
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: var(--oh3-radius-full);
  background: var(--main-color);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--oh3-shadow-medium);
}

input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 0 0 8px rgba(50, 101, 186, 0.15);
}

input[type="range"]::-webkit-slider-thumb:active {
  transform: scale(1.1);
  box-shadow: 0 0 0 12px rgba(50, 101, 186, 0.2);
}

/* Firefox */
input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: var(--oh3-radius-full);
  background: var(--main-color);
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  box-shadow: var(--oh3-shadow-medium);
}

input[type="range"]::-moz-range-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 0 0 8px rgba(50, 101, 186, 0.15);
}

input[type="range"]::-moz-range-track {
  background: #ddd;
  height: 6px;
  border-radius: 3px;
}

/* Base HTML styles */
html, body {
  overscroll-behavior: none;
  overflow: hidden;
  touch-action: none;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  width: 100%;
  position: fixed;
  margin: 0;
  padding: 0;
}

#app {
  height: 100%; /* 親要素が100%の高さを持つ */
  width: 100%;
  overflow: hidden;
  position: relative;
}

/* MapLibre popup styles（FloatingWindowと統一） */
.maplibregl-popup-content {
  padding: 0;
  box-shadow: var(--oh3-shadow-soft);
  border-radius: var(--oh3-radius-base);
  border: 1px solid whitesmoke;
}

.maplibregl-popup-close-button {
  font-size: 28px;
  background: transparent !important;
  color: white;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s;
  top: -4px;
  right: 2px;
}
.maplibregl-popup-close-button:hover {
  background: transparent !important;
  color: var(--oh3-color-overlay-white-70);
}
/* 白背景ポップアップではバツボタンを黒に（マーカー・チップ選択） */
.maplibregl-popup-content:has(.marker-popup) > .maplibregl-popup-close-button,
.maplibregl-popup-content:has(.popup-chip-container) > .maplibregl-popup-close-button {
  color: var(--oh3-color-text-primary);
}
.maplibregl-popup-content:has(.marker-popup) > .maplibregl-popup-close-button:hover,
.maplibregl-popup-content:has(.popup-chip-container) > .maplibregl-popup-close-button:hover {
  color: var(--oh3-color-text-tertiary);
}

/* コロプレスマップのポップアップ（白背景なのでバツボタンを黒に） */
.oh-choropleth-popup .maplibregl-popup-close-button {
  color: var(--oh3-color-text-primary);
  font-size: 22px;
  top: 2px;
  right: 4px;
}
.oh-choropleth-popup .maplibregl-popup-close-button:hover {
  color: var(--oh3-color-text-tertiary);
}

/* CKANオープンデータポップアップ（白背景なのでバツボタンを黒に） */
.ckan-data-popup .maplibregl-popup-close-button {
  color: var(--oh3-color-text-primary);
  font-size: 22px;
  top: 2px;
  right: 4px;
}
.ckan-data-popup .maplibregl-popup-close-button:hover {
  color: var(--oh3-color-text-tertiary);
}

/* 航空機トラッキングポップアップ（白背景なのでバツボタンを黒に） */
.oh-aircraft-popup .maplibregl-popup-close-button {
  color: var(--oh3-color-text-primary);
  font-size: 22px;
  top: 2px;
  right: 4px;
}
.oh-aircraft-popup .maplibregl-popup-close-button:hover {
  color: var(--oh3-color-text-tertiary);
}

/* KMZ ポップアップ（白背景なのでバツボタンを黒に） */
.oh-kmz-popup .maplibregl-popup-close-button {
  color: var(--oh3-color-text-primary);
  font-size: 22px;
  top: 2px;
  right: 4px;
}
.oh-kmz-popup .maplibregl-popup-close-button:hover {
  color: var(--oh3-color-text-tertiary);
}

.maplibregl-popup {
  z-index: 3;
}

/* ポップアップ共通スタイル（popup.jsのインラインスタイルをクラス化） */
.popup-base {
  font-weight: normal;
  color: var(--oh3-color-text-primary);
  line-height: 25px;
  padding: 10px;
}

.popup-base-lg {
  font-weight: normal;
  color: var(--oh3-color-text-primary);
  line-height: 25px;
  font-size: var(--oh3-font-size-xl);
  padding: 10px;
}

.popup-base-relaxed {
  font-weight: normal;
  color: var(--oh3-color-text-primary);
  line-height: 1.6;
  padding: 10px;
}

.popup-base-compact {
  font-weight: normal;
  color: var(--oh3-color-text-primary);
  line-height: 22px;
  padding: 10px;
}

.popup-min-w280 {
  min-width: 280px;
}

.popup-min-w300 {
  min-width: 300px;
}

.popup-external-links {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 8px 10px 4px;
  border-top: 1px solid var(--oh3-color-border, #e0e0e0);
  margin-top: 8px;
}

.popup-link-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border: none;
  cursor: pointer;
  border-radius: var(--oh3-radius-2xl);
  background: var(--oh3-color-gray-100, #f5f5f5);
  color: var(--oh3-color-text-secondary, #555);
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
}

.popup-link-chip:hover {
  background: var(--oh3-color-primary, #1976d2);
  color: white;
}

.popup-link-chip:active {
  transform: scale(0.93);
  background: var(--oh3-color-primary-dark, #1565c0);
  color: white;
}

.popup-link-icon {
  font-size: var(--oh3-font-size-base);
  line-height: 1;
}

.maplibregl-popup-content hr {
  margin-top: 10px;
  margin-bottom: 10px;
}

.maplibregl-popup-tip {
  /* 吹き出し部分にも薄く影をつける */
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.15));
}

/* Vuetify global adjustments */
.v-overlay-container .v-select__content {
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
  max-height: 300px; /* 必要に応じて調整 */
  touch-action: pan-y !important; /* 縦方向のタッチスクロールを有効化 */
}

.v-navigation-drawer{
  /* iOS での安定化: 不要な backdrop-filter は避ける */
  backdrop-filter: none !important;
}
.v-navigation-drawer__content{
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.v-overlay:not(.exclude-overlay)  {
  z-index: 2147483647 !important;
}

/* Print media query */
@media print {
  /* 印刷時にtransform scaleを解除（実サイズで印刷） */
  #map00 {
    transform: none !important;
    transform-origin: initial !important;
  }
  .print-buttons,
  .print-header-area,
  .draggable-div {
    display: none !important;
  }
  .maplibregl-ctrl-attrib {
    background: transparent !important;
    box-shadow: none !important;
    color: #888 !important;      /* 文字も薄く */
    border: none !important;
    opacity: 0.5 !important;     /* うるささ軽減 */
  }
  .maplibregl-ctrl-attrib-button{
    display: none !important;
  }
  .terrain-btn-span {
    display: none;
  }
  #terrain-btn-div-map01 {
    display: none;
  }
  .sub-btns {
    display: none;
  }
  #print-div {
    display: none!important;
  }
  .features-rotate-div {
    display: none;
  }
  .map-radio div {
    display: none!important;
  }
}

/* Mobile responsive adjustments for third-party libraries */
@media (max-width: 450px) {
  .maplibregl-popup {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    z-index: 9999;
  }
  .maplibregl-popup-tip {
    display: none !important; /* 吹き出し非表示でモーダル感UP */
  }
  .maplibregl-popup-content {
    width: 400px !important;         /* ★固定幅 */
    max-width: 90vw !important;      /* 必要なら調整 */
    min-width: 300px !important;     /* 最小幅も確保 */
    overflow-y: auto;

    /* ★ 位置調整 */
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  }
  textarea,
  input {
    font-size: var(--oh3-font-size-md) !important;
  }
  .sub-btn {
    width: 38px !important;
    height: 38px !important;
  }
}

@media (max-width: 500px) {
  .v-overlay {
    z-index: 2147483647 !important;
  }
  .zahyo-wrap {
    flex-direction: column-reverse !important;
  }
}

/* Accessibility: reduced motion */
@media (prefers-reduced-motion: reduce) {
  .point-info-drawer .v-navigation-drawer{ transition: none !important; }
  /* 無限アニメーションを無効化してパフォーマンス向上 */
  .pulse,
  .oh-toggle-btn,
  .pulse-anim,
  .blink {
    animation: none !important;
  }
}

/* iOS/モバイルでのバッテリー節約のため、無限アニメーションを条件付きで削減 */
@supports (-webkit-touch-callout: none) {
  /* iOS Safari detection */
  .pulse {
    animation-duration: 4s; /* 3s -> 4s に延長 */
  }
  .oh-toggle-btn {
    animation-duration: 1.5s; /* 1.0s -> 1.5s に延長 */
  }
  .pulse-anim {
    animation-duration: 2s; /* 1.4s -> 2s に延長 */
  }
  .blink {
    animation-iteration-count: 3; /* infinite -> 3回のみ */
  }
}

/* 求積表インポートダイアログが開いている時の地図操作ブロック用オーバーレイ */
.map-overlay-blocker {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 100;
  pointer-events: auto;
  cursor: not-allowed;
}

/* ========================================
   UI Component Hover Effects
   ======================================== */

/* Button hover effects - 1px lift + enhanced shadow */
.v-btn {
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.v-btn:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: var(--oh3-shadow-medium) !important;
}

.v-btn:active:not(:disabled) {
  transform: translateY(0) !important;
  box-shadow: var(--oh3-shadow-soft) !important;
}

/* Card hover effects - 2px lift + elevation change */
.v-card:not(.draw-popup-card):not(.drawer) {
  transition: transform 0.3s ease, box-shadow 0.3s ease !important;
}

.v-card:not(.draw-popup-card):not(.drawer):hover {
  transform: translateY(-2px) !important;
  box-shadow: var(--oh3-shadow-popup) !important;
}

/* v-dialog内のv-cardはホバーエフェクトを無効化 */
.v-overlay--active .v-dialog .v-card,
.v-dialog > .v-overlay__content > .v-card,
.v-dialog .v-card {
  transition: none !important;
  transform: none !important;
}

.v-overlay--active .v-dialog .v-card:hover,
.v-dialog > .v-overlay__content > .v-card:hover,
.v-dialog .v-card:hover {
  transform: none !important;
  box-shadow: 0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12) !important;
}

/* Text field focus glow effect */
.v-text-field input:focus,
.v-textarea textarea:focus {
  /* outline: 2px solid rgba(33, 150, 243, 0.4); */
  outline-offset: -2px;
  transition: outline 0.3s ease !important;
}

.v-input--is-focused .v-field {
  /* outline: 2px solid rgba(33, 150, 243, 0.4); */
  outline-offset: -2px;
}

/* Add padding to text fields to prevent layout shift */
.v-text-field,
.v-textarea {
  padding-top: 2px;
}

/* Chip hover effects - 5% scale animation */
.v-chip {
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.v-chip:hover {
  transform: scale(1.05) !important;
  box-shadow: var(--oh3-shadow-soft) !important;
}

/* Dialog and Menu enhanced shadows and border radius */
.v-dialog > .v-overlay__content > .v-card,
.v-dialog > .v-overlay__content > .v-sheet {
  box-shadow: var(--oh3-shadow-modal) !important;
  border-radius: var(--oh3-radius-xl) !important;
}

.v-menu > .v-overlay__content {
  box-shadow: var(--oh3-shadow-modal) !important;
  border-radius: var(--oh3-radius-lg) !important;
}

/* SpeedDialのメニューは影なし（teleportされるのでtransform-originで特定） */
.v-overlay__content[style*="transform-origin: right center"] {
  box-shadow: none !important;
  border-radius: 0 !important;
  background: transparent !important;
}

.v-list {
  border-radius: var(--oh3-radius-lg) !important;
}

/* ========================================
   Typography & Scrollbar Enhancements
   ======================================== */

/* Anti-aliased fonts for better rendering */
* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Unified font family and line height */
body, .v-application {
  font-family: 'Roboto', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
  line-height: 1.6 !important;
}
.oh3-token-cost {
  display: none;
}

/* Custom scrollbar styling - 8px width, rounded corners, hover effects */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: var(--oh3-radius-base);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--oh3-radius-base);
  transition: background 0.2s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.35);
}

::-webkit-scrollbar-thumb:active {
  background: rgba(0, 0, 0, 0.5);
}

/* Firefox scrollbar styling */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05);
}

/* Scale control positioning */
.maplibregl-ctrl-bottom-left {
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.maplibregl-ctrl-bottom-left .maplibregl-ctrl-scale {
  pointer-events: auto;
}

.maplibregl-ctrl-scale.scale-bottom-center {
  position: absolute !important;
  left: 50% !important;
  bottom: 10px !important;
  transform: translateX(-50%) !important;
  z-index: 1 !important;
}

/* 4画面モード レイヤー名ラベル */
.map-layer-label {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: var(--oh3-font-size-xs, 11px);
  padding: 2px 12px;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
  pointer-events: none;
  backdrop-filter: blur(4px);
  letter-spacing: 0.02em;
}

/* Dragged scale control */
.maplibregl-ctrl-scale.scale-dragged {
  position: absolute !important;
  z-index: 1 !important;
}

/* 印刷モード用スケール：地図左下に固定 */
.scale-for-print-mode {
  position: absolute !important;
  left: 10px !important;
  bottom: 10px !important;
  z-index: 1000 !important;
  pointer-events: none !important;
  transform: none !important;
  margin: 0 !important;
  right: auto !important;
  top: auto !important;
}

.scale-for-print-mode .maplibregl-ctrl-scale {
  border-width: 1px !important;
  cursor: default !important;
}

/* Thin border for scale control */
.maplibregl-ctrl-scale {
  border-width: 1px !important;
}

/* リセット中オーバーレイ */
.reset-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.reset-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: var(--oh3-radius-full);
  animation: reset-spin 0.8s linear infinite;
}

@keyframes reset-spin {
  to { transform: rotate(360deg); }
}

.reset-text {
  color: #fff;
  font-size: var(--oh3-font-size-md);
  font-weight: 500;
}


/* 有償化開始バナー（v-main内、map00の前に通常フローで配置） */
.paid-launch-banner {
  padding: 4px 16px;
  background: linear-gradient(135deg, #1565C0 0%, #0D47A1 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--oh3-font-size-sm);
  font-weight: 500;
  z-index: 3;
}
/* 体験プラン残量警告（段階的に赤寄りへ） */
.paid-launch-banner--low50 {
  background: linear-gradient(135deg, #EF6C00 0%, #E65100 100%);
}
.paid-launch-banner--low30 {
  background: linear-gradient(135deg, #E53935 0%, #C62828 100%);
}
.paid-launch-banner--low10 {
  background: linear-gradient(135deg, #C62828 0%, #8B0000 100%);
  animation: trialBannerPulse 2s ease-in-out infinite;
}
@keyframes trialBannerPulse {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.15); }
}
.trial-warn-text strong {
  font-weight: 700;
  padding: 0 2px;
}
.paid-launch-banner-btn {
  color: #1565C0 !important;
  font-weight: 700;
  flex-shrink: 0;
}
.paid-launch-banner--low50 .paid-launch-banner-btn {
  color: #E65100 !important;
}
.paid-launch-banner--low30 .paid-launch-banner-btn,
.paid-launch-banner--low10 .paid-launch-banner-btn {
  color: #C62828 !important;
}
.banner-policy-link {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 11px;
  margin-left: 12px;
  flex-shrink: 0;
}
.banner-policy-link:hover {
  color: white;
  text-decoration: underline;
}
@media (max-width: 600px) {
  .paid-launch-banner {
    font-size: 11px;
    padding: 3px 10px;
  }
}

/* 授業モードバナー - 中央フローティング配置（左右ボタン群を隠さない） */
.lesson-mode-banner {
  position: fixed;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  height: 32px;
  padding: 0 16px;
  background: linear-gradient(135deg, #1565C0 0%, #0D47A1 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1006; /* 右ボタン群(1005)より少し上、でもモーダル等より下 */
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--oh3-radius-2xl);
  box-shadow: var(--oh3-shadow-strong);
  max-width: calc(100% - 200px); /* 左右100pxずつ空ける */
  white-space: nowrap;
}
.lesson-mode-banner.sync-locked {
  background: linear-gradient(135deg, #C62828 0%, #B71C1C 100%);
  animation: pulse-banner 2s infinite;
}
@keyframes pulse-banner {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
}

/* ツアー用ハイライトアニメーション */
.tour-highlight {
  animation: tour-pulse 0.8s ease-in-out infinite;
  border-radius: var(--oh3-radius-base);
  background-color: #ff6b00 !important;
  color: white !important;
}

.tour-highlight .v-icon {
  color: white !important;
}

@keyframes tour-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 107, 0, 0.8);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(255, 107, 0, 0);
    transform: scale(1.1);
  }
}

/* マップ連動/個別モード切り替えトグル */
.map-sync-toggle-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  pointer-events: auto;
}

.map-sync-toggle-btn {
  box-shadow: var(--oh3-shadow-strong) !important;
  opacity: 0.85;
  transition: all 0.2s ease !important;
}

.map-sync-toggle-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

@media print {
  .map-sync-toggle-container {
    display: none !important;
  }
}

.spin-fast {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ポップアップルートボタンのクリック感 */
.popup-route-btn {
  transition: all 0.15s ease;
}
.popup-route-btn:hover:not(:disabled) {
  background: #1565C0 !important;
  transform: translateY(-1px);
  box-shadow: var(--oh3-shadow-medium);
}
.popup-route-btn:active:not(:disabled) {
  background: #0D47A1 !important;
  transform: translateY(1px);
  box-shadow: none;
}

.popup-route-clear-btn {
  transition: all 0.15s ease;
}
.popup-route-clear-btn:hover:not(:disabled) {
  background: #e0e0e0 !important;
  transform: translateY(-1px);
  box-shadow: var(--oh3-shadow-soft);
}
.popup-route-clear-btn:active:not(:disabled) {
  background: #bdbdbd !important;
  transform: translateY(1px);
  box-shadow: none;
}

/* スタンプマーカー（App.vueからの復元用） */
.stamp-marker {
  cursor: pointer;
}
.stamp-marker svg {
  display: block;
  pointer-events: none;
}
.stamp-number-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: var(--oh3-font-size-lg);
  font-weight: 700;
  border: 3px solid;
  border-radius: var(--oh3-radius-full);
  background: white;
}

/* 3D復元時オーバーレイ */
.restoring-3d-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #1a1a2e;
  display: flex;
  align-items: center;
  justify-content: center;
}
.restoring-3d-content {
  text-align: center;
}
.restoring-3d-text {
  color: white;
  font-size: var(--oh3-font-size-md);
  margin-top: 16px;
  letter-spacing: 1px;
}
.fade-3d-overlay-leave-active {
  transition: opacity 0.4s ease;
}
.fade-3d-overlay-leave-to {
  opacity: 0;
}

</style>
